(() => {
  const boot = (root) => {
  const config = JSON.parse(root.querySelector("[data-ibg-config]").textContent);
  if (!root || root.dataset.ready === 'true') return;
  root.dataset.ready = 'true';
  const core = window.ImpoundBuyerCore;
  if (!core) { root.dataset.ready = ''; root.querySelector('[data-ibg-count]').textContent = 'Guide unavailable: please check the guide assets.'; return; }

  const FALLBACK_IMAGE = config.fallbackImage;
  const DEFAULT_SNAPSHOT_URL = config.snapshotAsset;
  const HOLO_SNAPSHOT_URL = config.holoAsset;
  const IMAGE_MANIFEST_URL = config.imageManifestAsset;
  const SNAPSHOT_URL = config.snapshotUrl || DEFAULT_SNAPSHOT_URL;
  const AUTO_REFRESH = config.autoRefresh;
  const REFRESH_MS = Math.max(1, Number(config.refreshHours || 168)) * 60 * 60 * 1000;
  const CACHE_KEY = 'impoundBuyerGuideSnapshot:v24-odin';
  const icon = (name) => `<svg class="ibg-icon" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true" focusable="false"><use href="#ibg-icon-${config.sectionId}-${name}"></use></svg>`;
  const normalize = (value) => String(value || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[char]));
  const handleize = (value) => normalize(value).replace(/\s+/g, '-');
  const plainText = (value) => String(value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  const number = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
  let imageManifestLoaded = false;
  let imageManifest = { source_to_asset: {}, gallery_by_title: {} };
  const shopifyAssetUrl = (fileName) => {
    if (!fileName) return '';
    try {
      const url = new URL(IMAGE_MANIFEST_URL, window.location.href);
      url.search = '';
      url.hash = '';
      url.pathname = url.pathname.replace(/[^/]*$/, encodeURIComponent(String(fileName)).replace(/%2F/g, '/'));
      return url.href;
    } catch (error) {
      return '';
    }
  };
  const resolveGuideImageUrl = (sourceUrl) => {
    const value = String(sourceUrl || '');
    if (!value) return '';
    if (value.startsWith('asset://')) return shopifyAssetUrl(value.slice(8));
    const localFile = imageManifest.source_to_asset?.[value];
    return localFile ? shopifyAssetUrl(localFile) : value;
  };
  const localGalleryFor = (ship) => {
    const keys = [ship.wikiTitle, ship.name, ship.className, ship.slug].flatMap((value) => {
      const key = normalize(value);
      if (!key) return [];
      const variants = [
        key,
        key.replace(/\s+(?:w|with)\s+c8x$/, ''),
        key.replace(/\s+(?:edition|carbon|talus|solstice|vindicator)$/, ''),
        key.replace(/^(?:aegis|anvil|argo|crusader|drake|esperia|gatac|greycat|misc|mirai|origin|rsi|tumbril)\s+/, '')
      ];
      if (/^pisces c8r/.test(key)) variants.push(key.replace(/^pisces c8r(?: rescue)?/, 'c8r pisces'));
      if (/^c8r pisces/.test(key)) variants.push('c8r pisces');
      return variants;
    }).filter(Boolean);
    for (const key of keys) {
      const files = imageManifest.gallery_by_title?.[key];
      if (Array.isArray(files) && files.length) return files.map(shopifyAssetUrl).filter(Boolean);
    }
    return [];
  };
  const wikiImageCache = new Map();
  const optimizedImageUrl = (url, width = 800) => {
    const value = String(url || '');
    if (!value || value === FALLBACK_IMAGE || !value.includes('media.starcitizen.tools') || value.includes('/thumb/')) return value;
    try {
      const parsed = new URL(value);
      const fileName = parsed.pathname.split('/').pop();
      if (!fileName || !/\.(jpe?g|png|webp)$/i.test(fileName)) return value;
      return `${parsed.origin}/thumb${parsed.pathname}/${width}px-${fileName}`;
    } catch (error) {
      return value;
    }
  };
  const absoluteRsiUrl = (url) => {
    const value = String(url || '');
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    return `https://media.robertsspaceindustries.com/${value.replace(/^\/+/, '')}`;
  };
  const vehicleOverrides = {
    genesis: {
      displayName: 'Genesis Starliner',
      cargo: 300,
      wikiTitles: ['Genesis Starliner', 'Genesis']
    },
    'genesis starliner': {
      displayName: 'Genesis Starliner',
      cargo: 300,
      wikiTitles: ['Genesis Starliner', 'Genesis']
    }
  };

  const overrideFor = (vehicle) => {
    const keys = [vehicle.name, vehicle.shipmatrix_name, vehicle.game_name]
      .map(normalize)
      .filter(Boolean);
    return keys.map((key) => vehicleOverrides[key]).find(Boolean) || null;
  };

  const isGroundVehicle = (ship) => {
    const rawSize = normalize(ship.size?.en_EN || ship.size);
    const rawType = normalize(ship.type?.en_EN || ship.type);
    return (Boolean(ship.is_vehicle || ship.is_gravlev) || rawSize === 'vehicle' || rawType === 'ground') && !ship.is_spaceship;
  };
  const sizeName = (ship) => {
    if (isGroundVehicle(ship)) return 'Ground vehicle';
    const matrixSize = normalize(ship.size?.en_EN);
    if (matrixSize === 'snub') return 'Snub';
    if (matrixSize === 'small') return 'Small';
    if (matrixSize === 'medium') return 'Medium';
    if (matrixSize === 'large') return 'Large';
    if (matrixSize === 'capital') return 'Capital';
    if (matrixSize === 'vehicle') return 'Ground vehicle';
    return ({ 1: 'Snub', 2: 'Small', 3: 'Medium', 4: 'Large', 5: 'Large', 6: 'Capital' }[Number(ship.size_class)] || 'Unknown');
  };
  const fmt = (value, suffix = '') => value || value === 0 ? `${value}${suffix}` : 'TBD';
  const fmtPositive = (value, suffix = '') => Number(value) > 0 ? `${value}${suffix}` : 'TBD';

  const globoMenuLinks = {"2949":"/collections/best-in-show-2949","2950":"/collections/best-in-show-2950","2951":"/collections/best-in-show-2951","2952":"/collections/best-in-show-2952","2953":"/collections/best-in-show-2953","2954":"/collections/best-in-show-2954","2955":"/collections/best-in-show-2955","ships and vehicles":"/collections/ships-and-vehicles","aegis":"/collections/aegis-dynamics","aegis dynamics":"/collections/aegis-dynamics","avenger stalker":"/collections/buy-avenger-stalker","buy avenger stalker":"/collections/buy-avenger-stalker","avenger titan":"/collections/buy-avenger-titan","buy avenger titan":"/collections/buy-avenger-titan","avenger titan renegade":"/collections/buy-avenger-titan-renegade","buy avenger titan renegade":"/collections/buy-avenger-titan-renegade","avenger warlock":"/collections/buy-avenger-warlock","buy avenger warlock":"/collections/buy-avenger-warlock","eclipse":"/collections/buy-eclipse","buy eclipse":"/collections/buy-eclipse","gladius":"/collections/buy-gladius","buy gladius":"/collections/buy-gladius","gladius valiant":"/collections/buy-gladius-valiant","buy gladius valiant":"/collections/buy-gladius-valiant","hammerhead":"/collections/buy-hammerhead","buy hammerhead":"/collections/buy-hammerhead","idris":"/collections/buy-idris","buy idris":"/collections/buy-idris","javelin":"/collections/javelin","nautilus":"/collections/nautilus","reclaimer":"/collections/buy-reclaimer","buy reclaimer":"/collections/buy-reclaimer","redeemer":"/collections/buy-redeemer","buy redeemer":"/collections/buy-redeemer","retaliator":"/collections/buy-retaliator","buy retaliator":"/collections/buy-retaliator","retaliator bomber":"/collections/retaliator-bomber","sabre":"/collections/buy-sabre","buy sabre":"/collections/buy-sabre","sabre comet":"/collections/buy-sabre-comet","buy sabre comet":"/collections/buy-sabre-comet","sabre firebird":"/collections/sabre-firebird","sabre peregrine":"/collections/sabre-peregrine","sabre raven":"/collections/buy-sabre-raven","buy sabre raven":"/collections/buy-sabre-raven","tiburon":"/collections/tiburon","vanguard harbinger":"/collections/buy-vanguard-harbinger","buy vanguard harbinger":"/collections/buy-vanguard-harbinger","vanguard hoplite":"/collections/buy-vanguard-hoplite","buy vanguard hoplite":"/collections/buy-vanguard-hoplite","vanguard sentinel":"/collections/buy-vanguard-sentinel","buy vanguard sentinel":"/collections/buy-vanguard-sentinel","vanguard warden":"/collections/buy-vanguard-warden","buy vanguard warden":"/collections/buy-vanguard-warden","vulcan":"/collections/buy-vulcan","buy vulcan":"/collections/buy-vulcan","anvil":"/collections/anvil-aerospace","anvil aerospace":"/collections/anvil-aerospace","asgard":"/collections/asgard","arrow":"/collections/arrow","ballista":"/collections/buy-ballista","buy ballista":"/collections/buy-ballista","carrack":"/collections/buy-carrack","buy carrack":"/collections/buy-carrack","carrack expedition":"/collections/buy-carrack-expedition","buy carrack expedition":"/collections/buy-carrack-expedition","centurion":"/collections/centurion","crucible":"/collections/buy-crucible","buy crucible":"/collections/buy-crucible","f7a hornet":"/collections/buy-f7a-hornet","buy f7a hornet":"/collections/buy-f7a-hornet","f7c hornet mk i":"/collections/buy-f7c-hornet","buy f7c hornet":"/collections/buy-f7c-hornet","f7c hornet":"/collections/buy-f7c-hornet","f7c hornet mk ii":"/collections/f7c-hornet-mk-ii","f7c hornet wildfire mk i":"/collections/buy-f7c-hornet-wildfire","buy f7c hornet wildfire":"/collections/buy-f7c-hornet-wildfire","f7c hornet wildfire":"/collections/buy-f7c-hornet-wildfire","f7c m sh heartseeker mk i":"/collections/buy-f7c-m-super-hornet-heartseeker","buy f7c m super hornet heartseeker":"/collections/buy-f7c-m-super-hornet-heartseeker","f7c m super hornet heartseeker":"/collections/buy-f7c-m-super-hornet-heartseeker","f7c m super hornet mk i":"/collections/buy-f7c-m-super-hornet","buy f7c m super hornet":"/collections/buy-f7c-m-super-hornet","f7c m super hornet":"/collections/buy-f7c-m-super-hornet","f7c m super hornet mk ii":"/collections/f7c-m-super-hornet-mk-ii","f7c r hornet tracker mk i":"/collections/buy-f7c-r-hornet-tracker","buy f7c r hornet tracker":"/collections/buy-f7c-r-hornet-tracker","f7c r hornet tracker":"/collections/buy-f7c-r-hornet-tracker","f7c r hornet tracker mk ii":"/collections/f7c-r-hornet-tracket-mk-ii","f7c r hornet tracket mk ii":"/collections/f7c-r-hornet-tracket-mk-ii","f7c s hornet ghost mk i":"/collections/buy-f7c-s-hornet-ghost","buy f7c s hornet ghost":"/collections/buy-f7c-s-hornet-ghost","f7c s hornet ghost":"/collections/buy-f7c-s-hornet-ghost","f7c s hornet ghost mk ii":"/collections/f7c-s-hornet-ghost-mk-ii","f8c lightning":"/collections/buy-f8c-lightning","buy f8c lightning":"/collections/buy-f8c-lightning","gladiator":"/collections/buy-gladiator","buy gladiator":"/collections/buy-gladiator","hawk":"/collections/buy-hawk","buy hawk":"/collections/buy-hawk","hurricane":"/collections/buy-hurricane","buy hurricane":"/collections/buy-hurricane","legionnaire":"/collections/legionnaire","liberator":"/collections/buy-liberator","buy liberator":"/collections/buy-liberator","paladin":"/collections/paladin","pisces":"/collections/pisces","pisces c8r":"/collections/pisces-c8r","pisces c8x expedition":"/collections/pisces-c8x-expedition","spartan":"/collections/buy-spartan","buy spartan":"/collections/buy-spartan","terrapin":"/collections/buy-terrapin","buy terrapin":"/collections/buy-terrapin","terrapin medic":"/collections/terrapin-medic","valkyrie":"/collections/buy-valkyrie","buy valkyrie":"/collections/buy-valkyrie","valkyrie liberator":"/collections/buy-valkyrie-liberator","buy valkyrie liberator":"/collections/buy-valkyrie-liberator","aopoa":"/collections/aopoa","khartu al":"/collections/buy-khartu-al","buy khartu al":"/collections/buy-khartu-al","nox":"/collections/buy-nox","buy nox":"/collections/buy-nox","nox kue":"/collections/buy-nox-kue","buy nox kue":"/collections/buy-nox-kue","san tok yai":"/collections/buy-santokyai","buy santokyai":"/collections/buy-santokyai","santokyai":"/collections/buy-santokyai","argo":"/collections/argo-astronautics","argo astronautics":"/collections/argo-astronautics","atls":"/collections/atls","atls geo":"/collections/atls-geo","csv sm":"/collections/csv-sm","mole":"/collections/buy-mole","buy mole":"/collections/buy-mole","moth":"/collections/moth","mpuv cargo":"/collections/buy-mpuv-cargo","buy mpuv cargo":"/collections/buy-mpuv-cargo","mpuv personnel":"/collections/buy-mpuv-personnel","buy mpuv personnel":"/collections/buy-mpuv-personnel","mpuv tractor":"/collections/mpuv-tractor","raft":"/collections/buy-raft","buy raft":"/collections/buy-raft","srv":"/collections/buy-srv","buy srv":"/collections/buy-srv","banu":"/collections/banu","defender":"/collections/buy-defender","buy defender":"/collections/buy-defender","merchantman":"/collections/buy-merchantman","buy merchantman":"/collections/buy-merchantman","banu souli":"/collections/banu","cnou":"/collections/consolidated-outland","consolidated outland":"/collections/consolidated-outland","pioneer":"/collections/buy-pioneer","buy pioneer":"/collections/buy-pioneer","hoverquad":"/collections/hoverquad","mustang alpha":"/collections/buy-mustang-alpha","buy mustang alpha":"/collections/buy-mustang-alpha","mustang beta":"/collections/buy-mustang-beta","buy mustang beta":"/collections/buy-mustang-beta","mustang delta":"/collections/buy-mustang-delta","buy mustang delta":"/collections/buy-mustang-delta","mustang gamma":"/collections/buy-mustang-gamma","buy mustang gamma":"/collections/buy-mustang-gamma","mustang omega":"/collections/buy-mustang-omega","buy mustang omega":"/collections/buy-mustang-omega","nomad":"/collections/buy-nomad","buy nomad":"/collections/buy-nomad","crusader":"/collections/crusader-industries","crusader industries":"/collections/crusader-industries","ares inferno":"/collections/buy-ares-inferno","buy ares inferno":"/collections/buy-ares-inferno","ares ion":"/collections/buy-ares-ion","buy ares ion":"/collections/buy-ares-ion","genesis starliner":"/collections/buy-genesis-starliner","buy genesis starliner":"/collections/buy-genesis-starliner","hercules starlifter a2":"/collections/buy-hercules-starlifter-a2","buy hercules starlifter a2":"/collections/buy-hercules-starlifter-a2","hercules starlifter c2":"/collections/buy-hercules-starlifter-c2","buy hercules starlifter c2":"/collections/buy-hercules-starlifter-c2","hercules starlifter m2":"/collections/buy-hercules-starlifter-m2","buy hercules starlifter m2":"/collections/buy-hercules-starlifter-m2","intrepid":"/collections/intrepid","mercury star runner":"/collections/buy-mercury-star-runner","buy mercury star runner":"/collections/buy-mercury-star-runner","spirit a1":"/collections/spirit-a1","spirit c1":"/collections/spirit-c1","spirit e1":"/collections/spirit-e1","drake":"/collections/drake-interplanetary","drake interplanetary":"/collections/drake-interplanetary","buccaneer":"/collections/buy-buccaneer","buy buccaneer":"/collections/buy-buccaneer","caterpillar":"/collections/caterpillar","clipper":"/collections/clipper","corsair":"/collections/buy-corsair","buy corsair":"/collections/buy-corsair","cutlass black":"/collections/buy-cutlass-black","buy cutlass black":"/collections/buy-cutlass-black","cutlass blue":"/collections/buy-cutlass-blue","buy cutlass blue":"/collections/buy-cutlass-blue","cutlass red":"/collections/buy-cutlass-red","buy cutlass red":"/collections/buy-cutlass-red","cutlass steel":"/collections/buy-cutlass-steel","buy cutlass steel":"/collections/buy-cutlass-steel","cutter":"/collections/cutter","cutter rambler":"/collections/cutter-rambler","cutter scout":"/collections/scout-scout","scout scout":"/collections/scout-scout","dragonfly black":"/collections/buy-dragonfly-black","buy dragonfly black":"/collections/buy-dragonfly-black","dragonfly star kitten":"/collections/buy-dragonfly-star-kitten","buy dragonfly star kitten":"/collections/buy-dragonfly-star-kitten","dragonfly yellowjacket":"/collections/buy-dragonfly-yellowjacket","buy dragonfly yellowjacket":"/collections/buy-dragonfly-yellowjacket","golem":"/collections/golem","golem ox":"/collections/golem-ox","herald":"/collections/buy-herald","buy herald":"/collections/buy-herald","ironclad":"/collections/ironclad","ironclad assault":"/collections/ironclad-assault","kraken":"/collections/buy-kraken","buy kraken":"/collections/buy-kraken","kraken privateer":"/collections/buy-kraken-privateer","buy kraken privateer":"/collections/buy-kraken-privateer","mule":"/collections/mule","pitbull":"/collections/pitbull","vulture":"/collections/buy-vulture","buy vulture":"/collections/buy-vulture","esperia":"/collections/esperia","blade":"/collections/buy-blade","buy blade":"/collections/buy-blade","glaive":"/collections/buy-glaive","buy glaive":"/collections/buy-glaive","prowler":"/collections/buy-prowler","buy prowler":"/collections/buy-prowler","prowler utility":"/collections/prowler-utility","talon":"/collections/buy-talon","buy talon":"/collections/buy-talon","talon shrike":"/collections/talon-shrike","stinger":"/collections/stinger","gatac":"/collections/gatac-manufacture","gatac manufacture":"/collections/gatac-manufacture","railen":"/collections/buy-railen","buy railen":"/collections/buy-railen","syulen":"/collections/gatac-syulen","gatac syulen":"/collections/gatac-syulen","tyilui":"/collections/Tyilui","greycat":"/collections/greycat","ptv buggy":"/collections/buy-ptv-buggy","buy ptv buggy":"/collections/buy-ptv-buggy","roc":"/collections/buy-roc","buy roc":"/collections/buy-roc","roc ds":"/collections/buy-roc-ds","buy roc ds":"/collections/buy-roc-ds","stv buggy":"/collections/greycat-stv","greycat stv":"/collections/greycat-stv","mdc":"/collections/mdc","mtc":"/collections/mtc","utv":"/collections/utv","greycat industrial":"/collections/greycat","grey s":"/collections/greys-market","greys market":"/collections/greys-market","shiv":"/collections/shiv","grey s market":"/collections/greys-market","kruger":"/collections/kruger-intergalactic","kruger intergalactic":"/collections/kruger-intergalactic","l 21 wolf":"/collections/l-21-wolf","l 22 alpha wolf":"/collections/l-22-wolf","l 22 wolf":"/collections/l-22-wolf","p 52 merlin":"/collections/buy-p-52-merlin","buy p 52 merlin":"/collections/buy-p-52-merlin","p 72 archimedes":"/collections/p-72-archimedes","misc":"/collections/misc","expanse":"/collections/expanse","endeavor":"/collections/buy-endeavor","buy endeavor":"/collections/buy-endeavor","fortune":"/collections/fortune","freelancer":"/collections/buy-freelancer","buy freelancer":"/collections/buy-freelancer","freelancer dur":"/collections/buy-freelancer-dur","buy freelancer dur":"/collections/buy-freelancer-dur","freelancer max":"/collections/buy-freelancer-max","buy freelancer max":"/collections/buy-freelancer-max","freelancer mis":"/collections/buy-freelancer-mis","buy freelancer mis":"/collections/buy-freelancer-mis","hull a":"/collections/buy-hull-a","buy hull a":"/collections/buy-hull-a","hull b":"/collections/buy-hull-b","buy hull b":"/collections/buy-hull-b","hull c":"/collections/buy-hull-c","buy hull c":"/collections/buy-hull-c","hull d":"/collections/buy-hull-d","buy hull d":"/collections/buy-hull-d","hull e":"/collections/buy-hull-e","buy hull e":"/collections/buy-hull-e","odyssey":"/collections/buy-odyssey","buy odyssey":"/collections/buy-odyssey","prospector":"/collections/buy-prospector","buy prospector":"/collections/buy-prospector","reliant kore":"/collections/buy-reliant-kore","buy reliant kore":"/collections/buy-reliant-kore","reliant mako":"/collections/buy-reliant-mako","buy reliant mako":"/collections/buy-reliant-mako","reliant sen":"/collections/buy-reliant-sen","buy reliant sen":"/collections/buy-reliant-sen","reliant tana":"/collections/buy-reliant-tana","buy reliant tana":"/collections/buy-reliant-tana","starfarer":"/collections/buy-starfarer","buy starfarer":"/collections/buy-starfarer","starfarer gemini":"/collections/buy-starfarer-gemini","buy starfarer gemini":"/collections/buy-starfarer-gemini","starlancer max":"/collections/starlancer-max","starlancer tac":"/collections/starlancer-tac","starlite":"/collections/starlite","musashi industrial and starflight concern":"/collections/misc","mirai":"/collections/mirai","fury":"/collections/fury","fury lx":"/collections/fury-lx","fury mx":"/collections/fury-mx","guardian":"/collections/guardian","guardian mx":"/collections/guardian-mx","guardian qi":"/collections/guardian-qi","pulse":"/collections/pulse","pulse lx":"/collections/pulse-lx","razor":"/collections/buy-razor","buy razor":"/collections/buy-razor","razor ex":"/collections/buy-razor-ex","buy razor ex":"/collections/buy-razor-ex","razor lx":"/collections/buy-razor-lx","buy razor lx":"/collections/buy-razor-lx","origin":"/collections/origin-jumpworks","origin jumpworks":"/collections/origin-jumpworks","100i":"/collections/100i","125a":"/collections/buy-125a","buy 125a":"/collections/buy-125a","135c":"/collections/buy-135c","buy 135c":"/collections/buy-135c","300i":"/collections/buy-300i","buy 300i":"/collections/buy-300i","315p":"/collections/buy-315p","buy 315p":"/collections/buy-315p","325a":"/collections/buy-325a","buy 325a":"/collections/buy-325a","350r":"/collections/buy-350r","buy 350r":"/collections/buy-350r","400i":"/collections/buy-400i","buy 400i":"/collections/buy-400i","600i explorer":"/collections/buy-600i-explorer","buy 600i explorer":"/collections/buy-600i-explorer","600i touring":"/collections/buy-600i-touring","buy 600i touring":"/collections/buy-600i-touring","85x":"/collections/buy-85x","buy 85x":"/collections/buy-85x","890 jump":"/collections/890-jump","m50":"/collections/buy-m50","buy m50":"/collections/buy-m50","m80":"/collections/m80","g12":"/collections/buy-g12","buy g12":"/collections/buy-g12","g12a":"/collections/buy-g12a","buy g12a":"/collections/buy-g12a","g12r":"/collections/buy-g12r","buy g12r":"/collections/buy-g12r","x1":"/collections/buy-x1","buy x1":"/collections/buy-x1","x1 force":"/collections/buy-x1-force","buy x1 force":"/collections/buy-x1-force","x1 velocity":"/collections/buy-x1-velocity","buy x1 velocity":"/collections/buy-x1-velocity","rsi":"/collections/roberts-space-industries","roberts space industries":"/collections/roberts-space-industries","apollo medivac":"/collections/buy-apollo-medivac","buy apollo medivac":"/collections/buy-apollo-medivac","apollo triage":"/collections/buy-apollo-triage","buy apollo triage":"/collections/buy-apollo-triage","aurora cl":"/collections/buy-aurora-cl","buy aurora cl":"/collections/buy-aurora-cl","aurora es":"/collections/buy-aurora-es","buy aurora es":"/collections/buy-aurora-es","aurora ln":"/collections/buy-aurora-ln","buy aurora ln":"/collections/buy-aurora-ln","aurora lx":"/collections/buy-aurora-lx","buy aurora lx":"/collections/buy-aurora-lx","aurora mk ii":"/collections/aurora-mk-ii","aurora mr":"/collections/buy-aurora-mr","buy aurora mr":"/collections/buy-aurora-mr","arrastra":"/collections/arrastra","constellation andromeda":"/collections/buy-constellation-andromeda","buy constellation andromeda":"/collections/buy-constellation-andromeda","constellation aquila":"/collections/buy-constellation-aquila","buy constellation aquila":"/collections/buy-constellation-aquila","constellation phoenix":"/collections/constellation-phoenix","constellation taurus":"/collections/constellation-taurus","galaxy":"/collections/galaxy","hermes":"/collections/hermes","lynx rover":"/collections/lynx-rover","mantis":"/collections/buy-mantis","buy mantis":"/collections/buy-mantis","meteor":"/collections/meteor","ursa medivac":"/collections/ursa-medivac","ursa rover":"/collections/buy-ursa-rover","buy ursa rover":"/collections/buy-ursa-rover","orion":"/collections/buy-orion","buy orion":"/collections/buy-orion","perseus":"/collections/buy-perseus","buy perseus":"/collections/buy-perseus","polaris":"/collections/polaris","scorpius":"/collections/buy-scorpius","buy scorpius":"/collections/buy-scorpius","scorpius antares":"/collections/scorpius-antares","salvation":"/collections/salvation","zeus cl":"/collections/zeus-cl","zeus es":"/collections/zeus-es","zeus mr":"/collections/zeus-mr","tumbril":"/collections/tumbril","cyclone":"/collections/buy-cyclone","buy cyclone":"/collections/buy-cyclone","cyclone aa":"/collections/buy-cyclone-aa","buy cyclone aa":"/collections/buy-cyclone-aa","cyclone mt":"/collections/buy-cyclone-mt","buy cyclone mt":"/collections/buy-cyclone-mt","cyclone rc":"/collections/buy-cyclone-rc","buy cyclone rc":"/collections/buy-cyclone-rc","cyclone rn":"/collections/buy-cyclone-rn","buy cyclone rn":"/collections/buy-cyclone-rn","cyclone tr":"/collections/buy-cyclone-tr","buy cyclone tr":"/collections/buy-cyclone-tr","nova tank":"/collections/buy-nova-tank","buy nova tank":"/collections/buy-nova-tank","ranger cv":"/collections/buy-ranger-cv","buy ranger cv":"/collections/buy-ranger-cv","ranger rc":"/collections/buy-ranger-rc","buy ranger rc":"/collections/buy-ranger-rc","ranger tr":"/collections/buy-ranger-tr","buy ranger tr":"/collections/buy-ranger-tr","storm":"/collections/storm","storm aa":"/collections/storm-aa","tumbril land systems":"/collections/tumbril","vanduul":"/collections/vanduul","best in show":"/collections/best-in-show","best in show 2949":"/collections/best-in-show-2949","best in show 2950":"/collections/best-in-show-2950","best in show 2951":"/collections/best-in-show-2951","best in show 2952":"/collections/best-in-show-2952","best in show 2953":"/collections/best-in-show-2953","best in show 2954":"/collections/best-in-show-2954","best in show 2955":"/collections/best-in-show-2955","best in show ship showdown":"/collections/best-in-show","roles and packages":"/collections/ships-vehicles-and-packages","ships vehicles and packages":"/collections/ships-vehicles-and-packages","multirole":"/collections/multirole-ships","multirole ships":"/collections/multirole-ships","combat":"/collections/combat-ships","combat ships":"/collections/combat-ships","cargo":"/collections/cargo-ships","cargo ships":"/collections/cargo-ships","exploration":"/collections/exploration-ships","exploration ships":"/collections/exploration-ships","industrial":"/collections/industrial-ships-and-vehicles","industrial ships and vehicles":"/collections/industrial-ships-and-vehicles","support":"/collections/support-ships","support ships":"/collections/support-ships","racing":"/collections/racing-ships","racing ships":"/collections/racing-ships","ground vehicles":"/collections/ground-vehicles","upgrades":"/collections/upgrades","other":"/collections/other-star-citizen-items","other star citizen items":"/collections/other-star-citizen-items","personal gear":"/collections/personal-gear","armors":"/collections/personal-gear","clothing":"/collections/clothing","weapons":"https://theimpound.com/collections/personal-gear/Weapon","personal gear weapon":"https://theimpound.com/collections/personal-gear/Weapon","tools":"https://theimpound.com/collections/personal-gear/Tool","personal gear tool":"https://theimpound.com/collections/personal-gear/Tool","decorations":"https://theimpound.com/collections/personal-gear/Decoration","personal gear decoration":"https://theimpound.com/collections/personal-gear/Decoration","trophies":"https://theimpound.com/collections/star-citizen-trophies","star citizen trophies":"https://theimpound.com/collections/star-citizen-trophies","conversion kits":"https://theimpound.com/collections/other-star-citizen-items/Conversion-Kit","other star citizen items conversion kit":"https://theimpound.com/collections/other-star-citizen-items/Conversion-Kit","endeavor modules":"https://theimpound.com/collections/endeavor-modules","retaliator modules":"https://theimpound.com/collections/retaliator-modules","galaxy modules":"https://theimpound.com/collections/galaxy-modules","paints":"/collections/paints-and-skins","paints and skins":"/collections/paints-and-skins","land claims":"https://theimpound.com/collections/other-star-citizen-items/Land-Claim","other star citizen items land claim":"https://theimpound.com/collections/other-star-citizen-items/Land-Claim","drop sales":"https://theimpound.com/collections/drop-sale","drop sale":"https://theimpound.com/collections/drop-sale"};
  const globoMenuTitles = ["100i","125a","135c","2949","2950","2951","2952","2953","2954","2955","300i","315p","325a","350R","400i","600i Explorer","600i Touring","85x","890 Jump","Aegis Dynamics","Anvil Aerospace","Apollo Medivac","Apollo Triage","Ares Inferno","Ares Ion","Argo Astronautics","Armors","Arrastra","Arrow","Asgard","ATLS","ATLS GEO","Aurora CL","Aurora ES","Aurora LN","Aurora LX","Aurora MK II","Aurora MR","Avenger Stalker","Avenger Titan","Avenger Titan Renegade","Avenger Warlock","Ballista","Banu Souli","Best In Show","Best In Show - Ship Showdown","Blade","Buccaneer","Cargo","Carrack","Carrack Expedition","Caterpillar","Centurion","Clipper","Clothing","CNOU","Combat","Constellation Andromeda","Constellation Aquila","Constellation Phoenix","Constellation Taurus","Conversion Kits","Corsair","Crucible","Crusader Industries","CSV-SM","Cutlass Black","Cutlass Blue","Cutlass Red","Cutlass Steel","Cutter","Cutter Rambler","Cutter Scout","Cyclone","Cyclone AA","Cyclone MT","Cyclone RC","Cyclone RN","Cyclone TR","Decorations","Defender","Dragonfly Black","Dragonfly Star Kitten","Dragonfly Yellowjacket","Drake Interplanetary","Drop Sales","Eclipse","Endeavor","Endeavor Modules","Expanse","Exploration","F7A Hornet","F7C Hornet MK I","F7C Hornet MK II","F7C Hornet Wildfire MK I","F7C-M SH Heartseeker MK I","F7C-M Super Hornet MK I","F7C-M Super Hornet MK II","F7C-R Hornet Tracker MK I","F7C-R Hornet Tracker MK II","F7C-S Hornet Ghost MK I","F7C-S Hornet Ghost MK II","F8C Lightning","Fortune","Freelancer","Freelancer DUR","Freelancer MAX","Freelancer MIS","Fury","Fury LX","Fury MX","G12","G12a","G12r","Galaxy","Galaxy Modules","Gatac Manufacture","Genesis Starliner","Gladiator","Gladius","Gladius Valiant","Glaive","Golem","Golem OX","Grey's","Grey's Market","Greycat Industrial","Ground Vehicles","Guardian","Guardian MX","Guardian QI","Hammerhead","Hawk","Herald","Hercules Starlifter A2","Hercules Starlifter C2","Hercules Starlifter M2","Hermes","HoverQuad","Hull A","Hull B","Hull C","Hull D","Hull E","Hurricane","Idris","Industrial","Intrepid","Ironclad","Ironclad Assault","Javelin","Khartu-al","Kraken","Kraken Privateer","Kruger Intergalactic","L-21 Wolf","L-22 Alpha Wolf","Land Claims","Legionnaire","Liberator","Lynx Rover","M50","M80","Mantis","MDC","Merchantman","Mercury Star Runner","Meteor","MOLE","MOTH","MPUV Cargo","MPUV Personnel","MPUV Tractor","MTC","Mule","Multirole","Musashi Industrial and Starflight Concern","Mustang Alpha","Mustang Beta","Mustang Delta","Mustang Gamma","Mustang Omega","Nautilus","Nomad","Nova Tank","Nox","Nox Kue","Odyssey","Origin Jumpworks","Orion","Other","P-52 Merlin","P-72 Archimedes","Paints","Paladin","Perseus","Personal Gear","Pioneer","Pisces","Pisces C8R","Pisces C8X Expedition","Pitbull","Polaris","Prospector","Prowler","Prowler Utility","PTV Buggy","Pulse","Pulse LX","Racing","RAFT","Railen","Ranger CV","Ranger RC","Ranger TR","Razor","Razor EX","Razor LX","Reclaimer","Redeemer","Reliant Kore","Reliant Mako","Reliant Sen","Reliant Tana","Retaliator","Retaliator Bomber","Retaliator Modules","Roberts Space Industries","ROC","ROC-DS","Roles and Packages","Sabre","Sabre Comet","Sabre Firebird","Sabre Peregrine","Sabre Raven","Salvation","San'tok'yai","Scorpius","Scorpius Antares","Shiv","Spartan","Spirit A1","Spirit C1","Spirit E1","SRV","Starfarer","Starfarer Gemini","Starlancer MAX","Starlancer TAC","Starlite","Stinger","Storm","Storm AA","STV Buggy","Support","Syulen","Talon","Talon Shrike","Terrapin","Terrapin Medic","Tiburon","Tools","Trophies","Tumbril Land Systems","Tyilui","Upgrades","Ursa Medivac","Ursa Rover","UTV","Valkyrie","Valkyrie Liberator","Vanguard Harbinger","Vanguard Hoplite","Vanguard Sentinel","Vanguard Warden","Vulcan","Vulture","Weapons","X1","X1 Force","X1 Velocity","Zeus CL","Zeus ES","Zeus MR"];
  const menuLinks = {
    'constellation mk v centaurus': '/collections/constellation-mk-v-centaurus',
    'rsi constellation mk v centaurus': '/collections/constellation-mk-v-centaurus',
    'sabre raven ex': '/collections/sabre-raven-ex',
    'aegis sabre raven ex': '/collections/sabre-raven-ex',
    basher: '/collections/basher',
    stingray: '/collections/stingray',
    's 65 stingray': '/collections/stingray',
    'kruger s 65 stingray': '/collections/stingray',
    ...globoMenuLinks
  };
  const menuTitles = globoMenuTitles.concat(['S-65 Stingray', 'Sabre Raven EX', 'Constellation Mk V Centaurus']);
  root.querySelectorAll('[data-guide-menu-link]').forEach((link) => {
    const title = normalize(link.dataset.title || link.textContent);
    const rawTitle = String(link.dataset.title || link.textContent || '').trim();
    if (title && !menuLinks[title]) menuLinks[title] = link.getAttribute('href');
    if (rawTitle) menuTitles.push(rawTitle);
  });

  const collectionUrl = (ship) => {
    const accountFinderNames = normalize(`${ship.shipmatrix_name || ''} ${ship.name || ''} ${ship.game_name || ''}`);
    if (accountFinderNames.includes('kraken') || accountFinderNames.includes('javelin') || accountFinderNames.includes('idris') || /\bodin\b/.test(accountFinderNames)) {
      return '/pages/accountfinder';
    }
    const rawNames = [ship.shipmatrix_name, ship.name, ship.game_name].filter(Boolean).map((name) => String(name));
    const collectionAliases = rawNames.flatMap((name) => {
      const aliases = [];
      const hercules = name.match(/\b([ACM]2)\s+Hercules\b/i);
      if (hercules) aliases.push(`Hercules Starlifter ${hercules[1].toUpperCase()}`);
      const pisces = name.match(/\b(C8R|C8X)\s+Pisces\b/i);
      if (pisces) aliases.push(`Pisces ${pisces[1].toUpperCase()}`);
      if (pisces && pisces[1].toUpperCase() === 'C8X') aliases.push('Pisces C8X Expedition');
      return aliases;
    });
    const candidates = [
      ship.shipmatrix_name,
      ship.name,
      ship.game_name,
      ...collectionAliases,
      String(ship.name || '').replace(/^Vanduul\s+/i, ''),
      String(ship.name || '').replace(/\s+Starlifter$/i, ''),
      String(ship.name || '').replace(/\s+Mk\s+II/i, '')
    ].map(normalize).filter(Boolean);
    for (const key of candidates) if (menuLinks[key]) return menuLinks[key];
    return `/collections/${handleize(ship.name)}`;
  };

  const SHIP_GUIDE_BASES = new Set(`
    100i 125a 135c 300i 315p 325a 350r 400i 600i-explorer 600i-touring 85x 890-jump
    apollo-medivac apollo-triage ares-inferno ares-ion arrastra arrow asgard atls atls-geo
    aurora-cl aurora-es aurora-ln aurora-lx aurora-mk-ii aurora-mr
    avenger-stalker avenger-titan avenger-titan-renegade avenger-warlock
    ballista basher blade buccaneer c2-hercules carrack carrack-expedition caterpillar centurion clipper
    constellation-andromeda constellation-aquila constellation-phoenix constellation-taurus
    corsair crucible csv-sm cutlass-black cutlass-blue cutlass-red cutlass-steel
    cutter cutter-rambler cutter-scout cyclone cyclone-aa cyclone-mt cyclone-rc cyclone-rn cyclone-tr
    defender dragonfly-black dragonfly-star-kitten dragonfly-yellowjacket eclipse endeavor expanse
    f7a-hornet f7a-hornet-mk-ii f7c-hornet-mk-i f7c-hornet-mk-ii f7c-hornet-wildfire-mk-i
    f7c-m-sh-heartseeker-mk-i f7c-m-super-hornet-mk-i f7c-m-super-hornet-mk-ii
    f7c-r-hornet-tracker-mk-i f7c-r-hornet-tracker-mk-ii f7c-s-hornet-ghost-mk-i f7c-s-hornet-ghost-mk-ii
    f8c-lightning fortune freelancer freelancer-dur freelancer-max freelancer-mis
    fury fury-lx fury-mx g12 g12a g12r galaxy genesis-starliner gladiator gladius gladius-valiant
    glaive golem golem-ox guardian guardian-mx guardian-qi hammerhead hawk herald
    hercules-starlifter-a2 hercules-starlifter-m2 hermes hoverquad
    hull-a hull-b hull-c hull-d hull-e hurricane idris intrepid ironclad ironclad-assault javelin
    khartu-al kraken l-21-wolf l-22-alpha-wolf legionnaire liberator lynx-rover
    m50 m80 mantis mdc merchantman mercury-star-runner meteor mole moth
    mpuv-cargo mpuv-personnel mpuv-tractor mtc mule
    mustang-alpha mustang-beta mustang-delta mustang-gamma mustang-omega
    nautilus nomad nova-tank nox nox-kue odyssey orion p-52-merlin p-72-archimedes
    paladin perseus pioneer pisces pisces-c8r pisces-c8x-expedition pitbull polaris prospector
    prowler prowler-utility ptv-buggy pulse pulse-lx raft railen
    ranger-cv ranger-rc ranger-tr razor razor-ex razor-lx reclaimer redeemer
    reliant-kore reliant-mako reliant-sen reliant-tana retaliator retaliator-bomber roc roc-ds
    sabre sabre-comet sabre-firebird sabre-peregrine sabre-raven salvation santokyai
    scorpius scorpius-antares spartan spirit-a1 spirit-c1 spirit-e1 srv
    starfarer starfarer-gemini starlancer-max starlancer-tac starlite stinger
    storm storm-aa stv-buggy syulen talon talon-shrike terrapin terrapin-medic tiburon
    ursa-medivac ursa-rover utv valkyrie valkyrie-liberator
    vanguard-harbinger vanguard-hoplite vanguard-sentinel vanguard-warden
    vulcan vulture x1 x1-force x1-velocity zeus-cl zeus-es zeus-mr
  `.trim().split(/\s+/));
  const SHIP_GUIDE_ALIASES = {
    'a1-spirit': 'spirit-a1',
    'a2-hercules': 'hercules-starlifter-a2',
    'a2-hercules-starlifter': 'hercules-starlifter-a2',
    'anvil-ballista-dunestalker': 'ballista',
    'anvil-ballista-snowblind': 'ballista',
    'argo-atls-geo-ikti': 'atls-geo',
    'argo-atls-ikti': 'atls',
    'argo-atls-ikti-rad': 'atls',
    'argo-mole-carbon-edition': 'mole',
    'argo-mole-talus-edition': 'mole',
    'c1-spirit': 'spirit-c1',
    'caterpillar-pirate-edition': 'caterpillar',
    'm2-hercules': 'hercules-starlifter-m2',
    'm2-hercules-starlifter': 'hercules-starlifter-m2',
    'c2-hercules-starlifter': 'c2-hercules',
    'hercules-starlifter-c2': 'c2-hercules',
    'c8-pisces': 'pisces',
    'c8-pisces-expedition': 'pisces',
    'c8r-pisces': 'pisces-c8r',
    'c8r-pisces-rescue': 'pisces-c8r',
    'c8x-pisces': 'pisces-c8x-expedition',
    'c8x-pisces-expedition': 'pisces-c8x-expedition',
    'constellation-phoenix-emerald': 'constellation-phoenix',
    'e1-spirit': 'spirit-e1',
    'f7a-hornet-mk-i': 'f7a-hornet',
    'gladius-pirate-edition': 'gladius',
    'golem-alliance': 'golem',
    'idris-m': 'idris',
    'idris-p': 'idris',
    'lynx': 'lynx-rover',
    'pisces-c8': 'pisces',
    'misc-prospector-alliance': 'prospector',
    'mole-alliance': 'mole',
    'nautilus-solstice-edition': 'nautilus',
    'p-72-archimedes-emerald': 'p-72-archimedes',
    'ursa': 'ursa-rover',
    'ursa-fortuna': 'ursa-rover',
    'zeus-mk-ii-cl': 'zeus-cl',
    'zeus-mk-ii-es': 'zeus-es',
    'zeus-mk-ii-mr': 'zeus-mr',
    'f7c-m-super-hornet-heartseeker': 'f7c-m-sh-heartseeker-mk-i',
    'f7c-m-super-hornet-heartseeker-mk-i': 'f7c-m-sh-heartseeker-mk-i',
    'kraken-privateer': 'kraken',
    'mole-carbon': 'mole',
    'mole-talus': 'mole',
    'mustang-alpha-vindicator': 'mustang-alpha',
    'nautilus-solstice': 'nautilus',
    'nova': 'nova-tank',
    'ptv': 'ptv-buggy',
    'san-tok-yai': 'santokyai',
    'stv': 'stv-buggy',
    'valkyrie-liberator-edition': 'valkyrie-liberator'
  };
  const shipGuideUrl = (ship) => {
    if ([ship.shipmatrix_name, ship.name, ship.game_name].some(value => /^(?:anvil (?:aerospace )?)?odin$/.test(normalize(value)))) return '/pages/meet-the-anvil-odin';
    if ([ship.shipmatrix_name, ship.name, ship.game_name].some(value => /^(?:rsi )?constellation mk v centaurus$/.test(normalize(value)))) return '/blogs/ships-and-vehicles/constellation-mk-v-centaurus-weapons-cargo-and-life-aboard';
    if ([ship.shipmatrix_name, ship.name, ship.game_name].some(value => /^(?:aegis )?sabre raven ex$/.test(normalize(value)))) return '/blogs/star-citizen-news/sabre-raven-ex-solo-hunter-specs-paints';
    const candidates = new Set();
    const add = (value) => {
      const base = handleize(value);
      if (!base) return;
      candidates.add(base);
      if (SHIP_GUIDE_ALIASES[base]) candidates.add(SHIP_GUIDE_ALIASES[base]);
      candidates.add(base.replace(/^vanduul-/, ''));
      candidates.add(base.replace(/-(?:w|with)-c8x$/, ''));
      candidates.add(base.replace(/-edition$/, ''));
      candidates.add(base.replace(/-(?:carbon|talus|solstice|vindicator)$/, ''));
      if (/^aurora-mk-i-(cl|es|ln|lx|mr)$/.test(base)) candidates.add(base.replace('aurora-mk-i-', 'aurora-'));
      if (/^zeus-mk-ii-(cl|es|mr)$/.test(base)) candidates.add(base.replace('zeus-mk-ii-', 'zeus-'));
    };
    [
      overrideFor(ship)?.displayName,
      ship.shipmatrix_name,
      ship.name,
      ship.game_name,
      ship.slug
    ].forEach(add);
    for (const base of candidates) {
      if (SHIP_GUIDE_BASES.has(base)) return `/blogs/ships-and-vehicles/${base}-guide-specs-features`;
    }
    return '/blogs/ships-and-vehicles';
  };

  const isCapitalShip = (ship) => {
    if (isGroundVehicle(ship)) return false;
    const matrixSize = normalize(ship.size?.en_EN || ship.size);
    if (matrixSize === 'capital') return true;
    if (matrixSize && matrixSize !== 'capital') return false;
    const name = normalize(publicName(ship));
    return [
      '890 jump',
      'endeavor',
      'hull d',
      'hull e',
      'idris m',
      'idris p',
      'idris k',
      'javelin',
      'kraken',
      'kraken privateer',
      'orion',
      'pioneer',
      'polaris'
    ].includes(name);
  };

  const shipNameText = (ship) => normalize(`${ship.name} ${ship.shipmatrix_name} ${ship.game_name}`);
  const dedicatedCombatShip = (ship) => {
    const name = shipNameText(ship);
    const core = normalize(`${ship.career} ${ship.role} ${ship.type?.en_EN || ship.type || ''} ${(ship.foci || []).map((f) => f.en_EN).join(' ')}`);
    if (name.includes('blade')) return true;
    if (name.includes('a2 hercules')) return true;
    const dedicatedTerms = ['fighter', 'interceptor', 'bomber', 'gunship'];
    const supportTerms = ['freight', 'cargo', 'transport', 'expedition', 'exploration', 'touring', 'medical', 'mining', 'salvage', 'repair', 'refuel'];
    return core.includes('combat') && dedicatedTerms.some((term) => core.includes(term)) && !supportTerms.some((term) => core.includes(term));
  };
  const primaryRoleOverride = (ship) => {
    if (core.profile(ship).primaryRole) return core.profile(ship).primaryRole;
    const name = shipNameText(ship);
    if (name.includes('x1')) return 'Racing';
    if (isGroundVehicle(ship)) {
      if (name.includes('ursa medivac')) return 'Medical';
      if (name.includes('nox') || name.includes('pulse lx') || name.includes('g12r') || name.includes('cyclone rc')) return 'Racing';
      if (name.includes('stv')) return 'Racing';
      if ((name.includes('ursa') && !name.includes('medivac')) || name.includes('lynx') || name.includes('mtc')) return 'Exploration';
      return '';
    }

    if (dedicatedCombatShip(ship)) return 'Combat';
    if (name.includes('freelancer max')) return 'Cargo';
    if (name.includes('freelancer dur')) return 'Exploration';
    if (name.includes('starlite')) return 'Industrial';
    if (name.includes('tyilui')) return 'Cargo';
    if (name.includes('retaliator bomber')) return 'Combat';
    if (name === 'retaliator' || (name.includes('retaliator') && !name.includes('bomber'))) return 'Multirole';
    if (name.includes('prowler utility')) return 'Cargo';
    if (name.includes('odyssey')) return 'Exploration';
    if (name.includes('polaris')) return 'Combat';
    if (name.includes('starfarer')) return 'Industrial';
    if (name.includes('vulture')) return 'Industrial';
    if (name.includes('terrapin') && !name.includes('medic')) return 'Exploration';
    if (name.includes('zeus mk ii cl') || name.includes('zeus cl')) return 'Cargo';
    if (name.includes('zeus mk ii es') || name.includes('zeus es')) return 'Multirole';
    if (name.includes('c2 hercules') || name.includes('caterpillar') || name.includes('constellation taurus') || /\bhull\s+[abcde]\b/.test(name)) return 'Cargo';
    if (name.includes('crucible')) return 'Industrial';
    if (name.includes('corsair') || name.includes('carrack') || name.includes('constellation aquila')) return 'Exploration';
    if (name.includes('c1 spirit') || name === 'clipper' || name.includes(' clipper') || name.includes('galaxy')) return 'Multirole';

    if (
      name.includes('c8r') ||
      name.includes('pisces rescue') ||
      name.includes('terrapin medic') ||
      name.includes('cutlass red') ||
      name.includes('apollo') ||
      name.includes('endeavor hope')
    ) return 'Medical';
    if (
      name.includes('890 jump') ||
      name.includes('600i touring') ||
      name.includes('400i')
    ) return 'Luxury';
    if (name.includes('600i explorer')) return 'Exploration';
    return '';
  };
  const majorRoleTags = (tags) => Array.from(tags).filter((tag) => [
    'Combat',
    'Cargo',
    'Industrial',
    'Exploration',
    'Medical',
    'Racing',
    'Luxury'
  ].includes(tag));
  const cleanupTagsForPrimary = (ship, tags, primaryOverride) => {
    const name = shipNameText(ship);
    if (number(ship.msrp) > 80) tags.delete('Starter');
    if (name.includes('aurora')) tags.delete('Luxury');
    if (!primaryOverride && number(ship.msrp) > 70 && majorRoleTags(tags).length >= 3) tags.add('Multirole');
    if (!primaryOverride) return;
    if (primaryOverride === 'Medical') {
      ['Exploration', 'Luxury', 'Racing', 'Combat', 'Cargo'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Luxury') {
      ['Exploration', 'Combat', 'Industrial', 'Cargo'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Cargo') {
      ['Combat', 'Exploration', 'Medical', 'Racing', 'Luxury', 'Industrial', 'Multirole'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Combat') {
      ['Medical', 'Racing', 'Luxury', 'Industrial', 'Multirole'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Industrial') {
      ['Combat', 'Cargo', 'Exploration', 'Medical', 'Racing', 'Luxury', 'Multirole'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Exploration') {
      ['Combat', 'Industrial', 'Medical', 'Racing', 'Multirole'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Multirole') {
      ['Industrial', 'Racing', 'Medical', 'Luxury'].forEach((tag) => tags.delete(tag));
    }
    if (primaryOverride === 'Racing') {
      tags.delete('Exploration');
      if (shipNameText(ship).includes('x1')) tags.delete('Luxury');
    }
    tags.add(primaryOverride);
  };
  const cleanMappedShipTags = (ship) => {
    const tags = new Set(ship.tags || []);
    cleanupTagsForPrimary(ship, tags, primaryRoleOverride(ship));
    return {
      ...ship,
      tags: Array.from(tags),
      search: normalize(`${ship.name} ${ship.manufacturer} ${ship.role} ${ship.career} ${Array.from(tags).join(' ')} ${(ship.subroles || []).join(' ')}`)
    };
  };

  const buyerTags = (ship) => {
    if (core.profile(ship).activities) return [...new Set([...core.profile(ship).activities, ...(isGroundVehicle(ship) ? ['Ground', 'Surface Support'] : [])])];
    const coreText = normalize(`${ship.name} ${ship.shipmatrix_name} ${ship.game_name} ${ship.career} ${ship.role} ${ship.type?.en_EN || ship.type || ''} ${(ship.foci || []).map((f) => f.en_EN).join(' ')}`);
    const text = coreText;
    const tags = new Set();
    const has = (...words) => words.some((word) => text.includes(word));
    const hasCore = (...words) => words.some((word) => coreText.includes(word));
    const primaryOverride = primaryRoleOverride(ship);

    if (isGroundVehicle(ship)) {
      const vehicleName = normalize(`${ship.name} ${ship.shipmatrix_name} ${ship.game_name}`);
      tags.add('Ground');
      tags.add('Surface Support');
      if (has('combat', 'anti air', 'anti vehicle', 'tank', 'storm', 'nova', 'ballista', 'centurion', 'missile', 'turret')) tags.add('Combat');
      if (has('cargo', 'freight', 'hauler', 'mule', 'csv', 'atls')) tags.add('Cargo');
      if (has('mining', 'industrial', 'tractor', 'roc', 'atls')) tags.add('Industrial');
      if (hasCore('medical', 'medivac', 'hospital', 'ambulance')) tags.add('Medical');
      if (has('racing', 'racer', 'competition', ' velocity', ' rc', 'g12r', 'pulse lx', 'x1')) tags.add('Racing');
      if ((vehicleName.includes('ursa') && !vehicleName.includes('medivac')) || vehicleName.includes('lynx') || vehicleName.includes('mtc')) tags.add('Exploration');
      if (has('lynx', 'g12', 'x1')) tags.add('Luxury');
      if (primaryOverride) tags.add(primaryOverride);
      cleanupTagsForPrimary(ship, tags, primaryOverride);
      return Array.from(tags);
    }

    if (has('starter', 'aurora', 'mustang', 'cutter', 'nomad', 'titan', '100i', '300i', 'intrepid', 'syulen', 'cutlass black', 'freelancer', 'c1 spirit')) tags.add('Starter');
    if (has('combat', 'fighter', 'interceptor', 'bomber', 'dropship', 'gunship', 'military', 'missile', 'emp', 'torpedo', 'hornet')) tags.add('Combat');
    if (has('cargo', 'freight', 'hauler', 'trading', 'merchantman', 'hull a', 'hull b', 'hull c', 'hull d', 'hull e') || number(ship.cargo_capacity) >= 8) tags.add('Cargo');
    if (has('mining', 'salvage', 'repair', 'refuel', 'tractor', 'building', 'construction', 'refining')) tags.add('Industrial');
    if (!primaryOverride && !has('hornet') && has('exploration', 'pathfinder', 'expedition', 'scout', 'carrack', 'corsair', 'aquila', 'odyssey', 'terrapin', '315p', 'pisces')) tags.add('Exploration');
    if (hasCore('medical', 'medivac', 'triage', 'hospital', 'ambulance')) tags.add('Medical');
    if (has('racing', 'racer', 'competition', 'm50', 'razor', '350r', 'mustang gamma')) tags.add('Racing');
    if (has('luxury', 'touring', 'executive', 'phoenix', '890 jump', '600i', '400i', 'e1 spirit')) tags.add('Luxury');
    if (primaryOverride) tags.add(primaryOverride);
    if (isCapitalShip(ship)) tags.add('Capital');
    cleanupTagsForPrimary(ship, tags, primaryOverride);
    if (!tags.size) tags.add(ship.career || 'Multirole');
    return Array.from(tags);
  };

  const subrolesFor = (ship, tags) => {
    if (core.profile(ship).subroles) return core.profile(ship).subroles;
    const text = normalize(`${ship.name} ${ship.shipmatrix_name} ${ship.game_name} ${ship.career} ${ship.role} ${(ship.foci || []).map((f) => f.en_EN).join(' ')}`);
    const subroles = new Set();
    const has = (...words) => words.some((word) => text.includes(word));
    const size = sizeName(ship);
    const cargo = number(ship.cargo_capacity);
    const crewMax = number(ship.crew?.max) || number(ship.crew?.min) || 1;

    if (isGroundVehicle(ship)) {
      if (has('tank', 'nova', 'storm', 'ballista', 'centurion', 'anti air', 'anti vehicle')) subroles.add('Combat vehicle');
      if (has('rover', 'ursa', 'lynx', 'g12', 'cyclone', 'stv')) subroles.add('Rover');
      if (has('bike', 'ranger', 'pulse', 'x1', 'nox', 'dragonfly')) subroles.add('Bike / gravlev');
      if (has('roc')) subroles.add('Ground mining');
      if (has('mule', 'csv', 'cargo')) subroles.add('Cargo vehicle');
      if (has('medical', 'medivac')) subroles.add('Medical vehicle');
      if (has('atls')) subroles.add('Power suit');
      if (tags.includes('Racing') && has('bike', 'nox', 'x1', 'pulse', 'dragonfly')) subroles.add('Gravlev racer');
      if (tags.includes('Racing') && has('racing', 'racer', 'g12r', 'cyclone rc')) subroles.add('Racer');
      if (!subroles.size) subroles.add('Ground utility');
      return Array.from(subroles).sort();
    }

    if (tags.includes('Starter')) {
      if (has('fighter', 'combat', 'avenger', 'mustang delta')) subroles.add('Combat starter');
      if (cargo > 0 || has('cargo', 'freight', 'nomad', 'cutter', 'c1 spirit')) subroles.add('Cargo starter');
      if (has('exploration', 'pathfinder', 'scout', 'syulen', 'cutter scout', '315p')) subroles.add('Exploration starter');
      subroles.add('Solo starter');
    }

    if (tags.includes('Combat')) {
      const multiroleCombat = has('freight', 'cargo', 'transport', 'multi role') && has('fighter', 'combat');
      if (multiroleCombat) subroles.add('Multirole combat');
      const fighterClass = has('heavy fighter', 'vanguard', 'scorpius', 'hurricane', 'f8c', 'guardian') ? 'Heavy fighter'
        : has('medium fighter', 'hornet') ? 'Medium fighter'
        : has('light fighter', 'snub fighter') || (size === 'Small' && crewMax <= 1 && has('fighter')) ? 'Light fighter' : '';
      if (!multiroleCombat && fighterClass) subroles.add(fighterClass);
      if (has('bomber', 'eclipse', 'gladiator', 'a2 hercules') && !/^retaliator$/.test(normalize(ship.shipmatrix_name || ship.name))) subroles.add('Bomber');
      if (has('gunship', 'redeemer', 'hammerhead', 'perseus')) subroles.add('Gunship');
      if (has('dropship', 'boarding', 'valkyrie', 'hoplite', 'prowler', 'legionnaire')) subroles.add('Dropship');
      if (has('interdiction', 'mantis', 'hawk', 'warlock', 'emp', 'antares')) subroles.add('Interdiction / EMP');
      if (has('tracker', 'radar', 'scanner', 'recon')) subroles.add('Recon / scanning');
      if (tags.includes('Capital') && has('capital', 'idris', 'javelin', 'polaris', 'kraken')) subroles.add('Capital combat');
    }

    if (tags.includes('Cargo')) {
      if (cargo > 0 && cargo <= 24) subroles.add('Light cargo');
      if (cargo >= 25 && cargo <= 99) subroles.add('Medium cargo');
      if (cargo >= 100 && cargo <= 499) subroles.add('Heavy cargo');
      if (cargo >= 500) subroles.add('Bulk cargo');
      if (has('hull a', 'hull b', 'hull c', 'hull d', 'hull e')) subroles.add('Hull series');
      if (has('hercules', 'liberator', 'ironclad', 'caterpillar', 'snub carrier', 'tyilui')) subroles.add('Vehicle carrier');
      if (has('merchantman', 'banu merchantman')) subroles.add('Trading hub');
    }

    if (tags.includes('Industrial')) {
      if (has('mining', 'prospector', 'mole', 'arrastra', 'orion', 'roc')) subroles.add('Mining');
      if (has('salvage', 'vulture', 'reclaimer', 'fortune')) subroles.add('Salvage');
      if (has('refuel', 'starfarer')) subroles.add('Refueling');
      if (has('repair', 'crucible')) subroles.add('Repair');
      if (has('tractor', 'srv', 'mpuv tractor', 'atls')) subroles.add('Tractor / recovery');
      if (has('construction', 'building', 'pioneer')) subroles.add('Base building');
      if (has('refining', 'expanse')) subroles.add('Refining support');
    }

    if (tags.includes('Exploration')) {
      if (!has('hornet') && (size === 'Small' || has('pathfinder', 'scout', 'terrapin', '315p'))) subroles.add('Pathfinder');
      if (has('carrack', 'odyssey', '600i', 'corsair', 'aquila')) subroles.add('Expedition');
      if (has('luxury', '400i', '600i')) subroles.add('Luxury explorer');
    }

    if (tags.includes('Medical')) {
      if (has('c8r', 'pisces', 'ursa medivac')) subroles.add('Ambulance');
      if (has('cutlass red')) subroles.add('Rescue ship');
      if (has('apollo', 'triage', 'medivac')) subroles.add('Triage ship');
      if (has('endeavor')) subroles.add('Hospital ship');
    }

    if (tags.includes('Racing')) {
      if (has('razor', 'm50', '350r', 'mustang gamma')) subroles.add('Racer');
      if (has('p 52', 'p 72', 'fury')) subroles.add('Snub racer');
      if (has('bike', 'nox', 'x1', 'pulse', 'dragonfly')) subroles.add('Gravlev racer');
    }

    if (tags.includes('Luxury')) {
      if (has('touring', '400i', '600i', '890')) subroles.add('Touring');
      if (has('genesis', 'e1 spirit', 'passenger') && tags.includes('Luxury')) subroles.add('Passenger');
      if (has('phoenix', 'executive')) subroles.add('Executive');
    }

    if (tags.includes('Multirole')) {
      subroles.add('Daily driver');
      if (cargo > 0) subroles.add(cargo >= 25 ? 'Cargo-flex multirole' : 'Utility multirole');
      if (crewMax <= 3) subroles.add('Solo-flex multirole');
      if (crewMax > 3) subroles.add('Crew-flex multirole');
      if (has('combat', 'gunship', 'fighter', 'retaliator', 'cutlass')) subroles.add('Combat-flex multirole');
      if (has('exploration', 'expedition', 'scout', 'clipper', 'zeus', 'galaxy')) subroles.add('Exploration-flex multirole');
      if (has('vehicle', 'carrier', 'garage', 'cargo', 'freight')) subroles.add('Vehicle support multirole');
      if (has('modular', 'module', 'galaxy', 'retaliator')) subroles.add('Modular platform');
    }

    if (tags.includes('Capital')) {
      if (has('carrier', 'kraken', 'idris', 'javelin', 'bengal')) subroles.add('Carrier / flagship');
      if (has('corvette', 'polaris')) subroles.add('Corvette');
      if (has('frigate', 'idris')) subroles.add('Frigate');
      if (has('industrial', 'orion', 'endeavor', 'pioneer', 'merchantman', 'hull d', 'hull e')) subroles.add('Capital industry');
      if (has('cargo', 'ironclad', 'hull', 'merchantman')) subroles.add('Capital cargo');
    }

    return Array.from(subroles).sort();
  };

  const textValue = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') return value.en_EN || value.name || '';
    return '';
  };

  const publicName = (ship) => overrideFor(ship)?.displayName || ship.shipmatrix_name || ship.name || ship.game_name || 'Unknown vehicle';
  const searchAliasesFor = (ship) => normalize(publicName(ship)) === 'tyilui' ? 'tylui' : '';

  const rawKind = (ship) => {
    const matrixSize = normalize(textValue(ship.size));
    const matrixType = normalize(textValue(ship.type));
    if (matrixSize === 'vehicle' || matrixType === 'ground' || isGroundVehicle(ship)) return 'ground vehicle';
    return 'ship';
  };

  const rawStatusText = (ship) => {
    const raw = normalize(textValue(ship.production_status) || textValue(ship.production_note));
    if (raw.includes('flight ready') || raw.includes('released')) return 'flight-ready';
    if (raw.includes('concept')) return 'in-concept';
    if (raw.includes('production') || raw.includes('greybox') || raw.includes('whitebox')) return 'in-production';
    return 'current availability';
  };

  const rawSizeText = (ship) => {
    const matrixSize = normalize(textValue(ship.size));
    if (matrixSize && matrixSize !== 'vehicle') return matrixSize;
    if (matrixSize === 'vehicle' || rawKind(ship) === 'ground vehicle') return 'ground';
    const map = { 1: 'snub', 2: 'small', 3: 'medium', 4: 'large', 5: 'large', 6: 'capital' };
    return map[Number(ship.size_class)] || 'specialized';
  };

  const rawCrewPhrase = (ship) => {
    const min = number(ship.crew?.min) || 1;
    const max = number(ship.crew?.max) || min;
    return min === max ? `${max}-person operation` : `${min}-${max} crew support`;
  };

  const rawCargoPhrase = (ship) => {
    const override = overrideFor(ship);
    const cargo = number(override?.cargo ?? ship.cargo_capacity);
    if (cargo > 0) return `${cargo.toLocaleString()} SCU cargo capacity`;
    return 'a focused non-cargo layout';
  };

  const rawRoleText = (ship) => textValue(ship.role) || textValue(ship.career) || textValue(ship.type) || 'multi-role gameplay';

  const rawBuyerFocus = (ship, tags) => {
    if (tags.includes('Combat')) return 'combat pilots';
    if (tags.includes('Cargo')) return 'haulers and logistics players';
    if (tags.includes('Industrial')) return 'industrial players';
    if (tags.includes('Exploration')) return 'explorers and long-range crews';
    if (tags.includes('Medical')) return 'medical and rescue teams';
    if (tags.includes('Racing')) return 'racing-focused pilots';
    if (tags.includes('Ground')) return 'surface crews';
    if (tags.includes('Luxury')) return 'touring and luxury buyers';
    return 'Star Citizen players';
  };

  const sentenceJoin = (parts) => parts.filter(Boolean).join(' ');

  const familyPitch = (ship, tags) => {
    const name = normalize(publicName(ship));
    const text = normalize(`${publicName(ship)} ${rawRoleText(ship)} ${ship.career} ${textValue(ship.type)} ${(ship.foci || []).map((f) => f.en_EN).join(' ')}`);
    const cargo = number(overrideFor(ship)?.cargo ?? ship.cargo_capacity);
    const crewMax = number(ship.crew?.max) || number(ship.crew?.min) || 1;
    const crewMin = number(ship.crew?.min) || 1;

    if (name.includes('freelancer')) return `MISC's Freelancer line is a self-contained hauler for pilots who want beds, range, and useful cargo without stepping into a true large ship. It is practical solo, while the extra interior space and turret coverage make it a natural pick for a friend or small crew.`;
    if (name.includes('cutlass black')) return `The Cutlass Black is Drake's rough-and-ready multirole workhorse, mixing a useful cargo bay, vehicle access, side doors, and turret support in one flexible hull. It suits solo pilots who want room to grow and small crews who want one ship for hauling, bunkers, and trouble.`;
    if (name.includes('cutlass red')) return `The Cutlass Red trades Drake's usual cargo focus for rescue work, giving medical teams a fast response ship with beds, utility space, and a manageable footprint. It is a strong choice for players who want to support friends without committing to a hospital-scale platform.`;
    if (name.includes('cutlass blue')) return `The Cutlass Blue is the law-enforcement version of the Cutlass, aimed at interdiction, prisoner transport, and security work. It keeps the approachable Drake frame but shifts the value toward stopping targets and supporting bounty crews.`;
    if (name.includes('c1 spirit')) return `The C1 Spirit is Crusader's clean, fast medium hauler, built around a straightforward cargo bay and a more refined flying experience than most utilitarian freight ships. It is a smart daily driver for pilots who want cargo income, style, and easy handling in one ship.`;
    if (name.includes('a1 spirit')) return `The A1 Spirit turns the Spirit frame into a compact bomber for pilots who want ground-strike gameplay without moving into a huge military platform. It keeps Crusader's speed and presentation while focusing its value on explosive support rather than freight.`;
    if (name.includes('e1 spirit')) return `The E1 Spirit is the passenger-focused Spirit, aimed at players who like VIP transport, touring style, and a more polished civilian role. It is less about raw cargo or firepower and more about future passenger missions and premium presentation.`;
    if (name.includes('constellation')) return `RSI's Constellation series is a larger multirole step-up with serious weapons, cargo room, vehicle utility, and meaningful crew stations. It can be handled by a determined solo pilot, but it becomes far more capable with friends covering turrets and support roles.`;
    if (name.includes('carrack')) return `The Carrack is Anvil's deep-expedition platform, built for long trips, mapping, med support, vehicles, and a crew that wants a mobile base. It is best for exploration groups that value endurance and utility over simple point-to-point flying.`;
    if (name.includes('corsair')) return `The Corsair is Drake's aggressive explorer, giving pilots long-range utility with a heavy forward-gun personality. It works for crews who want exploration gameplay but still expect their ship to hit hard when a route turns hostile.`;
    if (name.includes('caterpillar')) return `The Caterpillar is Drake's modular heavy hauler, famous for its long cargo spine and industrial, crew-friendly layout. It is for traders who care more about loading space, future modularity, and gritty utility than luxury or polish.`;
    if (name.includes('ironclad')) return `The Ironclad is Drake's armored bulk freighter concept, built for moving very large loads through dangerous space. It suits logistics-minded players who want huge cargo capacity, vehicle handling, and a tougher alternative to cleaner civilian haulers.`;
    if (name.includes('hull')) return `MISC's Hull series is pure freight design: external cargo capacity, efficient hauling, and little wasted space on anything that does not move boxes. Pick it when trading volume matters more than combat flexibility or luxury interiors.`;
    if (name.includes('prospector')) return `The Prospector is the classic solo mining ship, small enough to operate alone and focused enough to make the mining loop easy to understand. It is ideal for players who want steady industrial income without managing a crew.`;
    if (name.includes('mole')) return `The MOLE is Argo's cooperative mining ship, built around multiple mining stations and a crewed industrial workflow. It rewards coordinated players who want bigger mining sessions than a Prospector can comfortably handle.`;
    if (name.includes('orion')) return `The Orion is RSI's capital-scale mining platform, intended for organizations and dedicated industrial crews. It is less a casual ship purchase and more a long-term mining operation built around scale, support, and protection.`;
    if (name.includes('vulture')) return `The Vulture is Drake's entry salvage ship, giving solo industrial players a focused way to strip wrecks and turn cleanup into profit. It fits pilots who want a practical earning loop with a compact, no-frills ship.`;
    if (name.includes('reclaimer')) return `The Reclaimer is Aegis's heavy salvage ship, built for crews who want the large-scale version of the salvage profession. It brings industrial presence, major storage potential, and a much bigger commitment than solo salvage ships.`;
    if (name.includes('starfarer')) return `The Starfarer is MISC's refueling tanker, a specialized support ship for fleets, org logistics, and long-range operations. It is best for players who enjoy enabling other ships rather than chasing fast solo gameplay.`;
    if (name.includes('apollo')) return `The Apollo is RSI's dedicated medical ship, aimed at rescue teams that want more capability than a small ambulance craft. It is for players who expect medical gameplay to become a serious profession, not just an emergency backup.`;
    if (name.includes('c8r') || name.includes('terrapin medic') || name.includes('ursa medivac')) return `This is a compact medical support option for players who want fast rescue utility without committing to a large dedicated hospital ship. It is best used close to the action, supporting friends, bunker runs, and small-team recovery.`;
    if (name.includes('890 jump')) return `The 890 Jump is Origin's luxury capital yacht, built around presence, comfort, and prestige rather than efficiency. It is a statement ship for events, touring, roleplay, and groups that want a flagship with unmistakable style.`;
    if (name.includes('600i')) return `The 600i is Origin's large luxury ship for pilots who want high-end interiors wrapped around exploration or touring gameplay. It offers comfort and presence first, with enough capability to serve as a premium long-range home.`;
    if (name.includes('400i')) return `The 400i is Origin's sleek touring explorer, made for stylish travel, personal storage, and comfortable small-crew trips. It is a lifestyle pick for players who want exploration flavor without flying an industrial-looking ship.`;
    if (name.includes('avenger titan')) return `The Avenger Titan is one of the strongest everyday starter upgrades, pairing a real cargo bay with simple handling and enough combat ability for early missions. It is ideal for solo pilots who want one ship that can earn, fight, and travel.`;
    if (name.includes('nomad')) return `The Nomad is Consolidated Outland's pickup-style starter, with an open cargo bed, interior living space, and a strong utility identity. It is a good fit for solo players who want hauling, vehicle-adjacent gameplay, and a distinctive daily driver.`;
    if (name.includes('cutter')) return `The Cutter is Drake's compact utility starter, built like a small space van with a bed, cargo room, and forgiving operating costs. It is for practical solo pilots who value function and personality over speed or polish.`;
    if (name.includes('aurora') || name.includes('mustang') || name.includes('100i') || name.includes('300i')) return `This is an approachable starter or personal ship built for learning the game, running early missions, and traveling without crew overhead. It is best for solo pilots who want a simple first step before specializing.`;
    if (name.includes('fury') || name.includes('p-52') || name.includes('p-72')) return `This snub craft is built for short-range flying, carrier support, and quick bursts of specialized gameplay rather than independent long-distance travel. It is best treated as a companion craft, racer, or local defense option.`;
    if (text.includes('bomber')) return `This bomber is built to deliver heavy ordnance against larger targets or ground objectives, making positioning and timing more important than cargo or comfort. It suits combat pilots who want a specific strike role in group fights.`;
    if (text.includes('interceptor') || text.includes('emp') || name.includes('mantis')) return `This ship is built around control and pursuit, helping pilots catch, disrupt, or hold targets instead of simply trading damage. It is strongest when used by players who enjoy bounty hunting, interdiction, or coordinated combat support.`;
    if (text.includes('fighter') || tags.includes('Combat')) return `This combat ship focuses on weapons, agility, and survivability rather than cargo or living space. It is for pilots who want bounties, escorts, PvP practice, or a dedicated fighter role instead of a general-purpose daily driver.`;
    if (tags.includes('Ground')) {
      if (text.includes('tank') || text.includes('ballista') || text.includes('storm')) return `This ground combat vehicle is built for surface fights, bunker support, and combined-arms operations where armor and firepower matter more than travel range. It is best paired with a ship that can carry it into the fight.`;
      if (text.includes('bike') || text.includes('nox') || text.includes('x1') || text.includes('pulse') || text.includes('dragonfly')) return `This small gravlev or bike-style vehicle is made for fast surface movement, scouting, racing, and quick local travel. It is a lightweight add-on for players who want mobility once they are planetside.`;
      return `This ground vehicle is designed for planetside utility, giving crews a way to scout, haul, fight, mine, or move around after landing. Its value depends on the ship that carries it and the surface gameplay you plan to run.`;
    }
    if (tags.includes('Cargo')) return `This cargo ship is built around moving freight efficiently, with ${cargo ? `${cargo.toLocaleString()} SCU of capacity` : 'cargo utility'} shaping how it fits into trade routes and logistics. It is a strong pick for buyers who want earning potential and practical transport gameplay.`;
    if (tags.includes('Exploration')) return `This exploration ship is built for travel, scouting, and spending more time away from stations. It suits players who value range, utility, and a sense of home between destinations more than a single narrow profession.`;
    if (tags.includes('Industrial')) return `This industrial ship supports profession gameplay where the ship itself is the tool: mining, salvage, repair, refuel, transport, or construction. It is for buyers who want a clear money-making loop rather than a generic all-rounder.`;
    return `This ${rawRoleText(ship).toLowerCase()} ${rawKind(ship)} gives ${rawBuyerFocus(ship, tags)} a focused way to approach Star Citizen gameplay without guessing from price or appearance alone. Its value comes from how its crew, cargo, size, and role line up with the way you actually play.`;
  };

  const crewFitPhrase = (ship) => {
    const max = number(ship.crew?.max) || number(ship.crew?.min) || 1;
    const min = number(ship.crew?.min) || 1;
    const text = normalize(`${ship.name} ${ship.role} ${ship.career} ${ship.type}`);
    const soloSmallGroup = ['freelancer', 'cutlass', 'spirit', 'zeus', 'constellation', 'corsair', 'mercury star runner', 'raft', 'reliant', 'terrapin', '400i'].some((name) => text.includes(name));
    if (soloSmallGroup) return 'It can be flown solo, but the extra seats make it better with one or two friends.';
    if (max <= 1) return 'It is a true single-pilot option with no crew dependency.';
    if (min <= 1 && max <= 4) return 'It can be flown solo, but the extra seats make it better with one or two friends.';
    if (max <= 8) return 'It is happiest with a coordinated crew covering turrets, stations, or support jobs.';
    return 'It is an organization-scale asset that needs planning, crew, and support ships to shine.';
  };

  const buyerDescription = (ship, tags) => {
    const intro = familyPitch(ship, tags);
    const cargo = number(overrideFor(ship)?.cargo ?? ship.cargo_capacity);
    const status = rawStatusText(ship).replace(/-/g, ' ');
    const specLine = sentenceJoin([
      crewFitPhrase(ship),
      cargo > 0 ? `Cargo capacity is ${cargo.toLocaleString()} SCU.` : '',
      status ? `Current status is ${status}.` : ''
    ]);
    return `${intro} ${specLine}`;
  };

  const storedDescription = (ship) => {
    const value = textValue(ship.description).replace(/\s+/g, ' ').trim();
    if (!value || value.length <= 40) return '';
    const germanSignals = /\b(und|oder|der|die|das|ist|mit|für|von|werden|wurde|einem|einer|schiff|raumschiff|flugbereit|verfügt|besatzung|fracht|erkundung)\b/i;
    const englishSignals = /\b(the|and|or|with|for|from|ship|vehicle|built|designed|features|cargo|crew|flight|ready)\b/i;
    if (germanSignals.test(value) && !englishSignals.test(value)) return '';
    return value;
  };

  const displayDescription = (ship, tags) => storedDescription(ship) || buyerDescription(ship, tags);

  const imageFor = (ship) => {
    const images = Array.isArray(ship.images) ? ship.images : [];
    const image = images.find((item) => item.original_url) || images.find((item) => item.thumbnail_url);
    return resolveGuideImageUrl(image?.original_url || image?.thumbnail_url) || FALLBACK_IMAGE;
  };

  const imageListFor = (ship) => {
    const images = Array.isArray(ship.images) ? ship.images : [];
    const urls = images
      .map((image) => resolveGuideImageUrl(image.original_url || image.thumbnail_url))
      .filter(Boolean);
    urls.unshift(imageFor(ship));
    return Array.from(new Set(urls)).filter(Boolean).slice(0, 12);
  };

  const imageFallbackAttributes = (primary, candidates, width = 720) => {
    const current = String(primary || '');
    const queue = Array.from(new Set((candidates || [])
      .map((url) => optimizedImageUrl(url, width))
      .filter((url) => url && url !== current)));
    if (!queue.includes(FALLBACK_IMAGE) && current !== FALLBACK_IMAGE) queue.push(FALLBACK_IMAGE);
    return queue.length ? ` data-image-fallbacks="${escapeHtml(JSON.stringify(queue))}"` : '';
  };

  const shipImageFallbackAttributes = (ship, primary, width = 720) => imageFallbackAttributes(primary, [
    ship.imageOriginal,
    ...(ship.gallery || []),
    ...localGalleryFor(ship)
  ], width);

  const imageTitleVariants = (vehicle) => {
    const override = overrideFor(vehicle);
    const names = [
      ...(override?.wikiTitles || []),
      vehicle.shipmatrix_name,
      vehicle.name,
      vehicle.game_name,
      String(vehicle.name || '').replace(/^Vanduul\s+/i, ''),
      String(vehicle.name || '').replace(/\s+Starlifter$/i, ''),
      String(vehicle.name || '').replace(/\s+Mk\s+II/i, '')
    ].map((name) => String(name || '').replace(/\s+/g, ' ').trim()).filter(Boolean);
    return Array.from(new Set(names));
  };

  const wikiPageImage = async (title) => {
    if (!title) return '';
    if (wikiImageCache.has(title)) return wikiImageCache.get(title);
    const url = new URL('https://starcitizen.tools/api.php');
    url.searchParams.set('action', 'query');
    url.searchParams.set('format', 'json');
    url.searchParams.set('origin', '*');
    url.searchParams.set('prop', 'pageimages');
    url.searchParams.set('piprop', 'thumbnail|original');
    url.searchParams.set('pithumbsize', '1000');
    url.searchParams.set('titles', title);
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Wiki image lookup failed');
      const payload = await response.json();
      const page = Object.values(payload.query?.pages || {})[0];
      const image = page?.original?.source || page?.thumbnail?.source || '';
      wikiImageCache.set(title, image);
      return image;
    } catch (error) {
      wikiImageCache.set(title, '');
      return '';
    }
  };

  const wikiPageGallery = async (title) => {
    if (!title) return [];
    const fileUrlForTitles = async (fileTitles) => {
      if (!fileTitles.length) return [];
      const imageUrl = new URL('https://starcitizen.tools/api.php');
      imageUrl.searchParams.set('action', 'query');
      imageUrl.searchParams.set('format', 'json');
      imageUrl.searchParams.set('origin', '*');
      imageUrl.searchParams.set('prop', 'imageinfo');
      imageUrl.searchParams.set('iiprop', 'url');
      imageUrl.searchParams.set('iiurlwidth', '1200');
      imageUrl.searchParams.set('titles', fileTitles.slice(0, 8).join('|'));
      const imageResponse = await fetch(imageUrl, { headers: { Accept: 'application/json' } });
      if (!imageResponse.ok) throw new Error('Wiki gallery image lookup failed');
      const imagePayload = await imageResponse.json();
      return Object.values(imagePayload.query?.pages || {})
        .map((filePage) => filePage?.imageinfo?.[0]?.thumburl || filePage?.imageinfo?.[0]?.url)
        .filter(Boolean);
    };

    const mediaLinkAllowed = (url) => /\.(jpe?g|png|webp)(\?|$)/i.test(url) && !/logo|icon|paint|livery|decal|svg/i.test(url);

    try {
      const parseUrl = new URL('https://starcitizen.tools/api.php');
      parseUrl.searchParams.set('action', 'parse');
      parseUrl.searchParams.set('format', 'json');
      parseUrl.searchParams.set('origin', '*');
      parseUrl.searchParams.set('prop', 'text');
      parseUrl.searchParams.set('page', title);
      const parseResponse = await fetch(parseUrl, { headers: { Accept: 'application/json' } });
      if (!parseResponse.ok) throw new Error('Wiki page parse failed');
      const parsePayload = await parseResponse.json();
      const html = parsePayload.parse?.text?.['*'] || '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const galleryHeading = doc.getElementById('Gallery')?.closest('h2,h3,h4');
      const galleryShell = doc.createElement('div');
      if (galleryHeading) {
        let sibling = galleryHeading.nextElementSibling;
        while (sibling && !/^H2$/i.test(sibling.tagName)) {
          galleryShell.appendChild(sibling.cloneNode(true));
          sibling = sibling.nextElementSibling;
        }
      }
      const scope = galleryShell.childElementCount ? galleryShell : doc;
      const panels = Array.from(scope.querySelectorAll('[role="tabpanel"], .tabber__panel, .tabber__section, .tabbertab'));
      const officialPanel = panels.find((panel) => /official/i.test(`${panel.getAttribute('aria-label') || ''} ${panel.getAttribute('data-title') || ''} ${panel.getAttribute('title') || ''}`)) || panels[0] || scope;
      const directMedia = Array.from(officialPanel.querySelectorAll('a[href*="media.starcitizen.tools"]'))
        .map((link) => link.href)
        .filter(mediaLinkAllowed);
      if (directMedia.length) return Array.from(new Set(directMedia)).slice(0, 5);
      const fileTitles = Array.from(officialPanel.querySelectorAll('a[href*="/File:"], a[href^="./File:"]'))
        .map((link) => decodeURIComponent(link.getAttribute('href') || '').replace(/^.*\/File:/, 'File:').replace(/^\.\/File:/, 'File:'))
        .filter((fileTitle) => /^File:/i.test(fileTitle) && mediaLinkAllowed(fileTitle))
        .slice(0, 8);
      const officialFiles = await fileUrlForTitles(Array.from(new Set(fileTitles)));
      if (officialFiles.length) return officialFiles.slice(0, 5);
    } catch (error) {}

    const pageUrl = new URL('https://starcitizen.tools/api.php');
    pageUrl.searchParams.set('action', 'query');
    pageUrl.searchParams.set('format', 'json');
    pageUrl.searchParams.set('origin', '*');
    pageUrl.searchParams.set('prop', 'images');
    pageUrl.searchParams.set('imlimit', '24');
    pageUrl.searchParams.set('titles', title);
    try {
      const pageResponse = await fetch(pageUrl, { headers: { Accept: 'application/json' } });
      if (!pageResponse.ok) throw new Error('Wiki gallery lookup failed');
      const pagePayload = await pageResponse.json();
      const page = Object.values(pagePayload.query?.pages || {})[0];
      const fileTitles = (page?.images || [])
        .map((item) => item.title)
        .filter((fileTitle) => /\.(jpe?g|png|webp)$/i.test(fileTitle) && !/logo|icon|paint|livery|decal|svg/i.test(fileTitle))
        .slice(0, 12);
      if (!fileTitles.length) return [];
      return (await fileUrlForTitles(fileTitles)).slice(0, 5);
    } catch (error) {
      return [];
    }
  };

  const enrichImage = async (vehicle) => {
    const images = Array.isArray(vehicle.images) ? vehicle.images : [];
    if (images.some((image) => image.original_url || image.thumbnail_url)) return vehicle;

    for (const title of imageTitleVariants(vehicle)) {
      const image = await wikiPageImage(title);
      if (image) return { ...vehicle, images: [{ original_url: image, thumbnail_url: image }] };
    }

    return vehicle;
  };

  const isPurchasable = (ship) => {
    const hasSku = Array.isArray(ship.skus) && ship.skus.some((sku) => sku.available || number(sku.price) > 0);
    const knownPledgeSaleShips = new Set([
      'kraken',
      'odin',
      'sabre raven'
    ]);
    return hasSku || number(ship.msrp) > 0 || Boolean(ship.pledge_url) || knownPledgeSaleShips.has(normalize(publicName(ship)));
  };

  const isCosmeticRecord = (ship) => {
    const text = normalize(`${ship.name} ${ship.game_name} ${ship.class_name} ${ship.role} ${ship.type?.en_EN}`);
    return /\b(paint|skin|livery|color|colour|collector|grad|orange line|snowland|cool metal|pyam exec|executive edition|teach s special|wikelo|dunlevy|star kitten|best in show|bis|limited edition|geotack|planetary beacon)\b/.test(text);
  };

  const manufacturerDisplay = (manufacturer) => {
    const value = normalize(manufacturer);
    const displays = {
      'aegis dynamics': 'Aegis Dynamics',
      'aegis': 'Aegis Dynamics',
      'anvil aerospace': 'Anvil Aerospace',
      'anvil': 'Anvil Aerospace',
      'argo astronautics': 'Argo Astronautics',
      'argo': 'Argo Astronautics',
      'aopoa': 'Aopoa',
      'banu': 'Banu',
      'banu souli': 'Banu',
      'consolidated outland': 'Consolidated Outland',
      'cnou': 'Consolidated Outland',
      'crusader industries': 'Crusader Industries',
      'crusader': 'Crusader Industries',
      'drake interplanetary': 'Drake Interplanetary',
      'drake': 'Drake Interplanetary',
      'esperia': 'Esperia',
      'gatac manufacture': 'Gatac Manufacture',
      'gatac': 'Gatac Manufacture',
      'greycat industrial': 'Greycat Industrial',
      'greycat': 'Greycat Industrial',
      'grey s market': "Grey's Market",
      'greys market': "Grey's Market",
      'kruger intergalactic': 'Kruger Intergalactic',
      'kruger intergalatic': 'Kruger Intergalactic',
      'kruger': 'Kruger Intergalactic',
      'mirai': 'Mirai',
      'misc': 'MISC',
      'musashi industrial starflight concern': 'MISC',
      'musashi industrial and starflight concern': 'MISC',
      'origin jumpworks': 'Origin Jumpworks',
      'origin': 'Origin Jumpworks',
      'roberts space industries': 'RSI',
      'rsi': 'RSI',
      'tumbril land systems': 'Tumbril',
      'tumbril': 'Tumbril',
      'vanduul': 'Vanduul'
    };
    return displays[value] || manufacturer || 'Unknown';
  };

  const compactVehicle = (vehicle) => {
    const enriched = vehicle;
    return ({
      uuid: enriched.uuid,
      id: enriched.id,
      name: enriched.name,
      shipmatrix_name: enriched.shipmatrix_name,
      game_name: enriched.game_name,
      class_name: enriched.class_name,
      slug: enriched.slug,
      manufacturer: enriched.manufacturer ? { name: enriched.manufacturer.name, code: enriched.manufacturer.code } : null,
      career: enriched.career,
      role: enriched.role,
      type: enriched.type,
      foci: enriched.foci,
      size: enriched.size,
      size_class: enriched.size_class,
      dimension: enriched.dimension,
      sizes: enriched.sizes,
      crew: enriched.crew,
      cargo_capacity: enriched.cargo_capacity,
      ore_capacity: enriched.ore_capacity,
      speed: enriched.speed ? { scm: enriched.speed.scm, max: enriched.speed.max } : null,
      shield_hp: enriched.shield_hp,
      shield: enriched.shield ? { hp: enriched.shield.hp } : null,
      health: enriched.health,
      mass_total: enriched.mass_total,
      armor: enriched.armor ? { health: enriched.armor.health } : null,
      signature: enriched.signature ? {
        ir_quantum: enriched.signature.ir_quantum,
        ir_shields: enriched.signature.ir_shields,
        em_quantum: enriched.signature.em_quantum,
        em_shields: enriched.signature.em_shields
      } : null,
      power: enriched.power ? {
        generation_segments: enriched.power.generation_segments,
        used_segments_shields: enriched.power.used_segments_shields,
        used_segments_quantum: enriched.power.used_segments_quantum
      } : null,
      cooling: enriched.cooling ? {
        generation_segments: enriched.cooling.generation_segments,
        usage_shields_pct: enriched.cooling.usage_shields_pct,
        usage_quantum_pct: enriched.cooling.usage_quantum_pct
      } : null,
      is_vehicle: enriched.is_vehicle,
      is_gravlev: enriched.is_gravlev,
      is_spaceship: enriched.is_spaceship,
      production_status: enriched.production_status,
      production_note: enriched.production_note,
      description: enriched.description || {},
      buyer_profile: enriched.buyer_profile,
      images: Array.isArray(enriched.images) ? enriched.images.map((image) => ({ original_url: image.original_url, thumbnail_url: image.thumbnail_url })) : [],
      skus: Array.isArray(enriched.skus) ? enriched.skus.map((sku) => ({ price: sku.price, available: sku.available })) : [],
      msrp: enriched.msrp,
      pledge_url: enriched.pledge_url,
      link: enriched.link,
      web_url: enriched.web_url,
      updated_at: enriched.updated_at
    });
  };

  const mapShip = (ship) => {
    const gameplay = core.profile(ship);
    ship = { ...ship, ...(core.validCrew(gameplay.crew) ? { crew: gameplay.crew } : {}), ...(gameplay.cargo !== undefined ? { cargo_capacity: gameplay.cargo } : {}), ...(gameplay.ore !== undefined ? { ore_capacity: gameplay.ore } : {}) };
    const override = overrideFor(ship);
    const holo = holoFor(ship);
    const tags = buyerTags(ship);
    const subroles = subrolesFor(ship, tags);
    const description = displayDescription(ship, tags);
    const role = ship.role || ship.foci?.[0]?.en_EN || ship.career || ship.type?.en_EN || 'Multirole';
    const crew = core.validCrew(ship.crew) ? ship.crew : core.validCrew(holo?.crew) ? holo.crew : null;
    const crewMin = core.finite(crew?.min);
    const crewMax = core.finite(crew?.max);
    const length = number(ship.dimension?.length) || number(ship.sizes?.length);
    const size = sizeName(ship);
    return {
      name: override?.displayName || ship.shipmatrix_name || ship.name,
      className: ship.class_name || '',
      slug: ship.slug || '',
      uuid: ship.uuid || '',
      sourcePriority: core.priority(ship),
      buyerCapabilities: ship.buyer_capabilities || {},
      buyerProfile: gameplay,
      known: {
        cargo: override?.cargo !== undefined || core.finite(ship.cargo_capacity) !== null,
        ore: core.finite(ship.ore_capacity) !== null,
        crewMin: crewMin > 0, crewMax: crewMax > 0,
        length: length > 0, scm: number(ship.speed?.scm) > 0, nav: number(ship.speed?.max) > 0,
        shield: number(ship.shield?.hp ?? ship.shield_hp) > 0,
        health: number(ship.health) > 0, armor: number(ship.armor?.health) > 0,
        mass: number(ship.mass_total) > 0, em: number(ship.signature?.em_quantum || ship.signature?.em_shields) > 0, ir: number(ship.signature?.ir_quantum || ship.signature?.ir_shields) > 0
      },
      manufacturer: manufacturerDisplay(ship.manufacturer?.name),
      role,
      career: ship.career || ship.type?.en_EN || 'General',
      tags,
      subroles,
      size,
      sizeRaw: Number(ship.size_class) || 0,
      cargo: override?.cargo ?? number(ship.cargo_capacity),
      ore: number(ship.ore_capacity),
      crewMin,
      crewMax,
      scm: number(ship.speed?.scm),
      nav: number(ship.speed?.max),
      length,
      shield: number(ship.shield?.hp || ship.shield_hp),
      health: number(ship.health),
      armor: number(ship.armor?.health),
      mass: number(ship.mass_total),
      em: number(ship.signature?.em_quantum || ship.signature?.em_shields),
      ir: number(ship.signature?.ir_quantum || ship.signature?.ir_shields),
      msrp: number(ship.msrp),
      status: ship.production_status?.en_EN || holo?.status || ship.production_note?.en_EN || 'Unknown',
      type: size === 'Ground vehicle' || isGroundVehicle(ship) ? 'Vehicle' : 'Ship',
      image: optimizedImageUrl(imageFor(ship), 720),
      imageOriginal: imageFor(ship),
      gallery: imageListFor(ship),
      wikiTitle: imageTitleVariants(ship)[0] || (override?.displayName || ship.shipmatrix_name || ship.name),
      hasRealImage: imageFor(ship) !== FALLBACK_IMAGE,
      description,
      holoSpecs: holo || null,
      url: collectionUrl(ship),
      shipGuide: shipGuideUrl(ship),
      search: normalize(`${ship.name} ${ship.shipmatrix_name} ${ship.game_name} ${ship.manufacturer?.name} ${ship.role} ${ship.career} ${tags.join(' ')} ${subroles.join(' ')} ${searchAliasesFor(ship)}`)
    };
  };

  const fallbackShips = [
    { name: 'Avenger Titan', manufacturer: 'Aegis Dynamics', role: 'Light Freight', career: 'Transport', tags: ['Starter','Cargo','Combat'], size: 'Small', sizeRaw: 2, cargo: 8, ore: 0, crewMin: 1, crewMax: 1, scm: 260, nav: 1425, length: 22.5, shield: 5610, msrp: 60, status: 'flight-ready', type: 'Ship', image: FALLBACK_IMAGE, description: 'A compact everyday ship with a cargo bay, bed, and enough combat ability to serve as a practical first upgrade from true starters.', url: '/collections/avenger-titan' },
    { name: 'Cutlass Black', manufacturer: 'Drake Interplanetary', role: 'Medium Freight', career: 'Transport', tags: ['Starter','Cargo','Combat','Multirole'], size: 'Medium', sizeRaw: 3, cargo: 46, ore: 0, crewMin: 1, crewMax: 3, scm: 225, nav: 1300, length: 36, shield: 11500, msrp: 110, status: 'flight-ready', type: 'Ship', image: FALLBACK_IMAGE, description: 'A rugged multirole hauler for pilots who want cargo space, vehicle utility, co-op turret play, and enough firepower to stay useful in messy situations.', url: '/collections/cutlass-black' },
    { name: 'Prospector', manufacturer: 'MISC', role: 'Mining', career: 'Industrial', tags: ['Industrial'], size: 'Small', sizeRaw: 2, cargo: 0, ore: 32, crewMin: 1, crewMax: 1, scm: 200, nav: 1200, length: 26, shield: 5610, msrp: 155, status: 'flight-ready', type: 'Ship', image: FALLBACK_IMAGE, description: 'A solo mining ship built for players who want a focused earning loop with low crew requirements and straightforward progression.', url: '/collections/prospector' }
  ].map((ship) => ({ ...ship, shipGuide: shipGuideUrl(ship), subroles: [], search: normalize(`${ship.name} ${ship.manufacturer} ${ship.role} ${ship.career} ${ship.tags.join(' ')}`) }));

  const supplementalShips = [
    { name: 'Ironclad', manufacturer: 'Drake Interplanetary', role: 'Heavy Freight', career: 'Transport', tags: ['Cargo'], size: 'Large', sizeRaw: 4, cargo: 2204, ore: 0, crewMin: 1, crewMax: 6, scm: 0, nav: 0, length: 120, shield: 0, msrp: 0, status: 'active production', type: 'Ship', image: FALLBACK_IMAGE, hasRealImage: false, description: 'The Drake Ironclad is a large armored cargo ship designed for dangerous hauling routes, with a massive cargo bay, heavy armor, tractor-beam loading support, and a detachable command module.', url: collectionUrl({ name: 'Ironclad' }) },
    { name: 'Ironclad Assault', manufacturer: 'Drake Interplanetary', role: 'Combined Arms Platform', career: 'Combat', tags: ['Cargo','Combat'], size: 'Large', sizeRaw: 4, cargo: 1440, ore: 0, crewMin: 1, crewMax: 6, scm: 0, nav: 0, length: 120, shield: 0, msrp: 0, status: 'active production', type: 'Ship', image: FALLBACK_IMAGE, hasRealImage: false, description: 'The Ironclad Assault adapts the Ironclad platform for combined-arms deployment, trading some cargo focus for heavier vehicle deployment, troop support, and additional defensive firepower.', url: collectionUrl({ name: 'Ironclad Assault' }) }
  ].map((ship) => ({ ...ship, shipGuide: shipGuideUrl(ship), subroles: ship.name === 'Ironclad' ? ['Bulk cargo','Vehicle carrier'] : ['Dropship','Gunship','Vehicle carrier'], search: normalize(`${ship.name} ${ship.manufacturer} ${ship.role} ${ship.career} ${ship.tags.join(' ')}`) }));

  const els = {
    results: root.querySelector('[data-ibg-results]'),
    empty: root.querySelector('[data-ibg-empty]'),
    count: root.querySelector('[data-ibg-count]'),
    search: root.querySelector('[data-filter-search]'),
    role: root.querySelector('[data-filter-role]'),
    subrole: root.querySelector('[data-filter-subrole]'),
    makerButtons: root.querySelector('[data-filter-makers]'),
    size: root.querySelector('[data-filter-size]'),
    cargo: root.querySelector('[data-filter-cargo]'),
    crew: root.querySelector('[data-filter-crew]'),
    status: root.querySelector('[data-filter-status]'),
    sort: root.querySelector('[data-filter-sort]'),
    clearFilters: root.querySelector('[data-clear-filters]'),
    activeFilters: root.querySelector('[data-active-filters]'),
    filterDrawer: root.querySelector('[data-filter-drawer]'),
    compareBar: root.querySelector('[data-compare-bar]'),
    compareCopy: root.querySelector('[data-compare-copy]'),
    compareCards: root.querySelector('[data-compare-cards]'),
    clearCompare: root.querySelector('[data-clear-compare]'),
    moreWrap: root.querySelector('[data-ibg-more-wrap]'),
    moreButton: root.querySelector('[data-show-more]'),
    detailModal: root.querySelector('[data-detail-modal]'),
    detailContent: root.querySelector('[data-detail-content]'),
    viewTabs: Array.from(root.querySelectorAll('[data-view-tab]'))
  };

  const INITIAL_VISIBLE = 20;
  const VISIBLE_INCREMENT = 20;

  let ships = [];
  let mode = 'all';
  let catalogView = 'catalog';
  let rankingMetric = 'cargo';
  let rankingReadyOnly = false;
  let onlyDifferences = false;
  let quizComplete = false;
  let initialized = false;
  let quizStartedAt = 0;
  let snapshotGeneratedAt = '';
  const favoriteIds = new Set();
  let sharedShortlist = null;
  const recommendationById = new Map();
  const STATE_KEY = 'impoundBuyerPreferences:v1:' + location.pathname;
  const FAVORITES_KEY = 'impoundBuyerFavorites:v1';
  const persistence = {
    read(key) { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } },
    write(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch { return false; } }
  };
  let quizStep = 0;
  let visibleLimit = INITIAL_VISIBLE;
  let currentMatchCount = 0;
  let holoVehicles = [];
  const holoIndex = new Map();
  const selectedMakers = new Set();
  const compareIds = [];
  let activeDetailId = '';
  let detailTrigger = null;
  const galleryCache = new Map();
  const galleryLoading = new Set();
  const galleryIndexes = new Map();
  const quizAnswers = {};

  let noticeTimer;
  const announce = message => {
    clearTimeout(noticeTimer);
    const notice = root.querySelector('[data-guide-notice]');
    notice.textContent = message;
    notice.hidden = !message;
    if (message) noticeTimer = setTimeout(() => { notice.hidden = true; }, 6000);
  };
  const track = (event, parameters = {}) => {
    if (!config.analyticsEnabled) return;
    try {
      if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
      const payload = { event: `ibg_${event}`, guide_version: 2, ...parameters };
      // No raw search text, customer identifiers, cookies or external requests.
      if (typeof window.gtag === 'function') { const { event: name, ...data } = payload; window.gtag('event', name, data); }
      else if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
      document.dispatchEvent(new CustomEvent('impound:buyer-guide-analytics', { detail: payload }));
    } catch {}
  };
  const captureState = () => ({
    v: 1, view: catalogView, metric: rankingMetric, ready: rankingReadyOnly,
    filters: Object.fromEntries(['search', 'role', 'subrole', 'size', 'cargo', 'crew', 'status', 'sort'].map(field => [field, els[field].value])),
    makers: [...selectedMakers], compare: [...compareIds], detail: activeDetailId,
    quiz: mode === 'quiz' && quizComplete ? { ...quizAnswers } : null,
    short: sharedShortlist ? [...sharedShortlist] : null
  });
  const saveState = () => {
    if (!initialized) return;
    const state = captureState();
    state.detail = '';
    state.quiz = null;
    state.short = null;
    state.scroll = Math.round(window.scrollY);
    state.limit = visibleLimit;
    persistence.write(STATE_KEY, state);
    root.querySelector('[data-saved-count]').textContent = favoriteIds.size;
  };
  const readSharedState = () => {
    try {
      const raw = new URL(location.href).searchParams.get('ibg');
      return raw && raw.length <= 12000 ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const restoreState = state => {
    if (!state || state.v !== 1 || typeof state !== 'object') return;
    const validIds = new Set(ships.map(compareId));
    catalogView = ['catalog', 'rankings', 'saved'].includes(state.view) ? state.view : 'catalog';
    rankingMetric = ['cargo', 'scm', 'length', 'shield'].includes(state.metric) ? state.metric : 'cargo';
    rankingReadyOnly = state.ready === true;
    visibleLimit = Math.min(ships.length || INITIAL_VISIBLE, Math.max(INITIAL_VISIBLE, Number(state.limit) || INITIAL_VISIBLE));
    for (const field of ['search', 'role', 'subrole', 'size', 'cargo', 'crew', 'status', 'sort']) {
      const value = typeof state.filters?.[field] === 'string' ? state.filters[field].slice(0, 120) : '';
      els[field].value = field === 'search' || [...els[field].options].some(option => option.value === value) ? value : '';
      if (field === 'role') updateSubroleFilter();
    }
    selectedMakers.clear();
    const makers = new Set(ships.map(ship => ship.manufacturer));
    if (Array.isArray(state.makers)) state.makers.slice(0, 30).filter(maker => makers.has(maker)).forEach(maker => selectedMakers.add(maker));
    compareIds.splice(0, compareIds.length, ...new Set(Array.isArray(state.compare) ? state.compare.filter(id => validIds.has(id)).slice(0, 2) : []));
    sharedShortlist = Array.isArray(state.short) ? new Set(state.short.slice(0, 100).filter(id => validIds.has(id))) : null;
    activeDetailId = validIds.has(state.detail) ? state.detail : '';
    mode = 'all';
    if (state.quiz && typeof state.quiz === 'object') {
      clearQuizTheme();
      let invalidQuiz = false;
      for (const key of quizQuestionOrder) {
        const value = state.quiz[key];
        if (value === undefined) continue;
        const question = quizQuestionDefinitions().find(q => q.id === key);
        if (!question) { if (value) invalidQuiz = true; continue; }
        const options = question.dynamic ? dynamicOptionsForQuiz(question) : question.options;
        if (value === '' && question.optional) quizAnswers[key] = '';
        else if (options.some(option => option[0] === value) || (key === 'cargo' && /^\d+-\d+ SCU$/.test(value || ''))) quizAnswers[key] = value;
        else invalidQuiz = true;
      }
      if (invalidQuiz || !core.recommend(ships, quizAnswers).length) {
        clearQuizTheme(); quizComplete = false;
        announce('Your saved quiz no longer matches the current catalog. Please take the quiz again.');
      } else if (quizAnswers.vehicleType && quizAnswers.availability && quizAnswers.playstyle) {
        mode = 'quiz'; quizComplete = true; quizStep = activeQuizQuestions().length - 1; applyQuizTheme(); renderQuiz();
      }
    }
  };
  const shareState = async kind => {
    const state = captureState();
    if (kind === 'comparison') { state.view = 'catalog'; state.filters = {}; state.makers = []; state.quiz = null; state.detail = ''; state.short = null; }
    if (kind === 'saved') { state.view = 'saved'; state.short = [...(sharedShortlist || favoriteIds)]; state.filters = {}; state.makers = []; state.quiz = null; state.detail = ''; }
    const url = new URL(location.href);
    url.searchParams.set('ibg', JSON.stringify(state));
    url.hash = '';
    try { await navigator.clipboard.writeText(url.href); announce('Link copied.'); }
    catch {
      const field = root.querySelector('[data-share-field]');
      const input = field.querySelector('input');
      field.hidden = false; input.value = url.href; input.focus(); input.select();
      announce('Your share link is ready.');
    }
    track('share', { share_type: kind });
  };
  const favoriteButton = ship => `<button type="button" class="ibg-save${favoriteIds.has(compareId(ship)) ? ' is-saved' : ''}" data-save-id="${compareId(ship)}" aria-pressed="${favoriteIds.has(compareId(ship))}" aria-label="${favoriteIds.has(compareId(ship)) ? 'Unsave' : 'Save'} ${escapeHtml(ship.name)}" title="${favoriteIds.has(compareId(ship)) ? 'Unsave ship' : 'Save ship'}">${icon('Bookmark')}</button>`;
  const recommendationMarkup = ship => {
    const entry = recommendationById.get(compareId(ship));
    if (!entry) return '';
    const rows = [['Why it fits', entry.reasons], ['Trade-offs', entry.tradeoffs], ['Choose another when', entry.alternatives]];
    const editions = entry.editions?.length ? `<div><h4>Other editions</h4>${entry.editions.map(edition => `<a class="ibg-edition-link" href="${escapeHtml(edition.url)}">${escapeHtml(edition.name)}</a>`).join('')}</div>` : '';
    return `<div class="ibg-recommendation"><strong class="ibg-recommendation-label">${entry.label}</strong>${rows.map(([heading, items]) => `<div><h4>${heading}</h4><ul>${items.map(item => `<li>${escapeHtml(item)}</li>`).join('')}</ul></div>`).join('')}${editions}</div>`;
  };

  const setFilterDrawer = (open) => {
    root.classList.toggle('is-filter-open', open);
    if (els.filterDrawer) els.filterDrawer.classList.toggle('is-open', open);
    root.querySelectorAll('[data-filter-drawer-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = button.querySelector('[data-filter-toggle-label]');
      if (label) label.textContent = open ? 'Hide filters' : 'Filters';
    });
  };

  const quizThemes = {
    Starter: ['#a3e635', 'rgba(163,230,53,0.16)'],
    Combat: ['#ef233c', 'rgba(239,35,60,0.18)'],
    Cargo: ['#f97316', 'rgba(249,115,22,0.18)'],
    Industrial: ['#facc15', 'rgba(250,204,21,0.18)'],
    Exploration: ['#3b82f6', 'rgba(59,130,246,0.18)'],
    Medical: ['#10b981', 'rgba(16,185,129,0.17)'],
    Racing: ['#d946ef', 'rgba(217,70,239,0.17)'],
    Luxury: ['#f8fafc', 'rgba(248,250,252,0.13)', '#111827'],
    Multirole: ['#14b8a6', 'rgba(20,184,166,0.17)'],
    Specialist: ['#a1a1aa', 'rgba(161,161,170,0.17)'],
    Ground: ['#8b5cf6', 'rgba(139,92,246,0.18)'],
    'Surface Support': ['#06b6d4', 'rgba(6,182,212,0.18)']
  };

  const roleTheme = (role) => quizThemes[role] || null;
  const vehicleKeyVariants = (ship) => {
    const aliases = Array.isArray(ship.aliases) ? ship.aliases : [];
    return [
      ship.name,
      ship.shipmatrix_name,
      ship.game_name,
      ship.slug,
      ship.className,
      ship.class_name,
      ...(aliases || [])
    ].map(normalize).filter(Boolean);
  };
  const indexHoloVehicles = (vehicles) => {
    holoIndex.clear();
    holoVehicles = Array.isArray(vehicles) ? vehicles : [];
    holoVehicles.forEach((vehicle) => {
      vehicleKeyVariants(vehicle).forEach((key) => {
        if (key && !holoIndex.has(key)) holoIndex.set(key, vehicle);
      });
    });
  };
  const holoFor = (ship) => {
    for (const key of vehicleKeyVariants(ship)) {
      if (holoIndex.has(key)) return holoIndex.get(key);
    }
    // An approximate name must not give a base variant another variant's equipment.
    return null;
  };
  const primaryCategory = (ship) => {
    const override = primaryRoleOverride(ship);
    if (override) return override;
    if (ship.type === 'Vehicle') {
      const vehiclePriority = ['Medical', 'Racing', 'Combat', 'Industrial', 'Cargo', 'Exploration', 'Luxury', 'Surface Support'];
      return vehiclePriority.find((tag) => ship.tags.includes(tag)) || 'Surface Support';
    }
    if (ship.tags.includes('Starter') && number(ship.msrp) <= 70) return 'Starter';
    if (ship.tags.includes('Multirole')) return 'Multirole';
    const roleCount = majorRoleTags(new Set(ship.tags || [])).length;
    if (number(ship.msrp) > 70 && roleCount >= 3) return 'Multirole';
    const priority = ['Medical', 'Racing', 'Luxury', 'Combat', 'Industrial', 'Cargo', 'Exploration', 'Capital', 'Starter'];
    return priority.find((tag) => ship.tags.includes(tag)) || ship.tags[0] || ship.type || 'Ship';
  };
  const categoryThemeStyle = (category) => {
    if (category === 'Luxury') return ' style="--role-color:#f8fafc;--role-soft:rgba(248,250,252,0.92);--role-contrast:#111827;"';
    const theme = roleTheme(category) || (category === 'Capital' ? ['#94a3b8', 'rgba(148,163,184,0.18)'] : null);
    return theme ? ` style="--role-color:${theme[0]};--role-soft:${theme[1]};--role-contrast:${theme[2] || theme[0]};"` : '';
  };

  const applyQuizTheme = () => {
    const theme = quizThemes[quizAnswers.playstyle];
    if (!theme) {
      root.style.removeProperty('--quiz-accent');
      root.style.removeProperty('--quiz-accent-soft');
      root.style.removeProperty('--quiz-accent-contrast');
      root.style.removeProperty('--quiz-chrome-opacity');
      return;
    }
    root.style.setProperty('--quiz-accent', theme[0]);
    root.style.setProperty('--quiz-accent-soft', theme[1]);
    root.style.setProperty('--quiz-accent-contrast', theme[2] || '#fff');
    root.style.setProperty('--quiz-chrome-opacity', '1');
  };

  const clearQuizTheme = () => {
    quizComplete = false;
    Object.keys(quizAnswers).forEach((key) => delete quizAnswers[key]);
    root.style.removeProperty('--quiz-accent');
    root.style.removeProperty('--quiz-accent-soft');
    root.style.removeProperty('--quiz-accent-contrast');
    root.style.removeProperty('--quiz-chrome-opacity');
  };

  const baseQuizQuestion = {
    id: 'vehicleType',
    title: 'Are you looking for a ship or a ground vehicle?',
    options: [
      ['Ship', 'Spacecraft, snubs, multi-crew ships, haulers, fighters, and capital ships.'],
      ['Vehicle', 'Ground vehicles, gravlev bikes, rovers, tanks, mining vehicles, and power suits.']
    ]
  };

  const shipQuizQuestions = [
    {
      id: 'playstyle',
      title: 'What ship gameplay do you care about most?',
      options: [
        ['Starter', 'A first ship, daily driver, or practical all-rounder.'],
        ['Multirole', 'Flexible ships that cover several jobs without being true budget starters.'],
        ['Combat', 'Fighting, escort, bounties, interdiction, or fleet defense.'],
        ['Cargo', 'Trading, hauling, vehicle transport, or freight logistics.'],
        ['Industrial', 'Mining, salvage, refuel, repair, tractor, or base-building support.'],
        ['Exploration', 'Long-range travel, scouting, expedition, and living aboard.'],
        ['Medical', 'Rescue, recovery, respawn, and org support.'],
        ['Racing', 'Fast, agile ships for racing or pure handling.'],
        ['Luxury', 'Touring, interiors, passenger comfort, and prestige.'],
        ['Specialist', 'Science, reporting, data delivery, or dedicated personnel transport.']
      ]
    },
    { id: 'subrole', title: 'Which ship subrole fits best?', dynamic: true },
    {
      id: 'crew',
      title: 'How many players will usually crew it?',
      dynamic: true,
      options: [
        ['Single seater', 'I want a true one-seat ship.'],
        ['Solo or small group', 'Mostly solo, but useful with one or two friends.'],
        ['Group crew', 'A small group or regular org team.'],
        ['Org crew', 'Large crew, fleet, or long-term org ship.']
      ]
    },
    {
      id: 'cargo',
      title: 'How much cargo should the ship carry?',
      dynamic: true,
      options: [
        ['No cargo', 'Cargo does not matter.'],
        ['1-24 SCU', 'Just enough utility space.'],
        ['25-99 SCU', 'Useful medium cargo.'],
        ['100-499 SCU', 'Cargo is a major feature.'],
        ['500-999 SCU', 'Serious hauling capacity.'],
        ['1000+ SCU', 'Maximum hauling is the point.']
      ]
    },
    {
      id: 'size',
      title: 'What ship size feels right?',
      dynamic: true,
      options: [
        ['Snub', 'Tiny, fast, or carrier-dependent.'],
        ['Small', 'Easy solo daily driver.'],
        ['Medium', 'Flexible ship with more room.'],
        ['Large', 'Big ship with major capability.'],
        ['Capital', 'Long-term fleet or org asset.']
      ]
    }
  ];

  const vehicleQuizQuestions = [
    {
      id: 'playstyle',
      title: 'What should the vehicle do for you?',
      options: [
        ['Combat', 'Tanks, anti-air, armored support, or surface fighting.'],
        ['Industrial', 'Ground mining, tractor support, cargo handling, or utility work.'],
        ['Cargo', 'Moving boxes, supplies, or small freight around planetside locations.'],
        ['Racing', 'Fast bikes, gravlev vehicles, or racing rovers.'],
        ['Medical', 'Ground rescue and recovery.'],
        ['Exploration', 'Surface scouting and away-team support.'],
        ['Surface Support', 'A practical planetside runabout for bunkers, bases, and general travel.']
      ]
    },
    { id: 'subrole', title: 'Which vehicle type fits best?', dynamic: true },
    {
      id: 'crew',
      title: 'How many players should use it?',
      dynamic: true,
      options: [
        ['Single seater', 'Mostly one driver/operator.'],
        ['Solo or small group', 'One driver with room for a passenger or support role.'],
        ['Group crew', 'A larger vehicle crew.']
      ]
    },
    {
      id: 'cargo',
      title: 'Does vehicle cargo matter?',
      dynamic: true,
      options: [
        ['No cargo', 'No cargo needed.'],
        ['1-24 SCU', 'A small amount is useful.'],
        ['25-99 SCU', 'I want a dedicated cargo vehicle.']
      ]
    }
  ];

  const subrolesByPlaystyle = {
    Starter: ['Solo starter', 'Combat starter', 'Cargo starter', 'Exploration starter'],
    Combat: ['Light fighter', 'Medium fighter', 'Heavy fighter', 'Multirole combat', 'Bomber', 'Gunship', 'Dropship', 'Interdiction / EMP', 'Prisoner transport', 'Recon / scanning', 'Capital combat', 'Combat vehicle'],
    Specialist: ['Science', 'Reporting', 'Data courier', 'Passenger transport'],
    Cargo: ['Light cargo', 'Medium cargo', 'Heavy cargo', 'Bulk cargo', 'Hull series', 'Vehicle carrier', 'Trading hub', 'Capital cargo', 'Cargo vehicle'],
    Industrial: ['Mining', 'Salvage', 'Refueling', 'Repair', 'Tractor / recovery', 'Base building', 'Refining support', 'Ground mining', 'Power suit', 'Cargo vehicle'],
    Exploration: ['Pathfinder', 'Expedition', 'Luxury explorer', 'Rover', 'Ground utility'],
    Medical: ['Ambulance', 'Rescue ship', 'Triage ship', 'Hospital ship', 'Medical vehicle'],
    Racing: ['Racer', 'Snub racer', 'Gravlev racer', 'Bike / gravlev'],
    Luxury: ['Touring', 'Passenger', 'Executive'],
    Multirole: [
      'Daily driver',
      'Solo-flex multirole',
      'Crew-flex multirole',
      'Cargo-flex multirole',
      'Utility multirole',
      'Combat-flex multirole',
      'Exploration-flex multirole',
      'Vehicle support multirole',
      'Modular platform',
      'Multirole combat',
      'Vehicle carrier',
      'Expedition'
    ],
    Ground: ['Combat vehicle', 'Rover', 'Bike / gravlev', 'Ground mining', 'Cargo vehicle', 'Medical vehicle', 'Power suit', 'Ground utility'],
    'Surface Support': ['Rover', 'Bike / gravlev', 'Cargo vehicle', 'Power suit', 'Ground utility'],
    Capital: ['Carrier / flagship', 'Corvette', 'Frigate', 'Capital industry', 'Capital cargo', 'Capital combat']
  };

  const cargoRelevant = () => {
    if (quizAnswers.playstyle === 'Starter' && quizAnswers.subrole === 'Cargo starter') return true;
    if (quizAnswers.playstyle === 'Cargo') return true;
    if (quizAnswers.playstyle === 'Industrial' && ['Mining', 'Salvage', 'Cargo vehicle', 'Ground mining'].includes(quizAnswers.subrole)) return true;
    if (quizAnswers.playstyle === 'Surface Support' && ['Cargo vehicle', 'Ground mining'].includes(quizAnswers.subrole)) return true;
    if (quizAnswers.playstyle === 'Exploration' && quizAnswers.subrole === 'Expedition') return true;
    return false;
  };

  const sizeRelevant = () => {
    if (quizAnswers.vehicleType === 'Vehicle') return false;
    if (!quizAnswers.playstyle) return true;
    if (quizAnswers.playstyle === 'Starter') return false;
    if (['Cargo', 'Industrial', 'Exploration', 'Luxury'].includes(quizAnswers.playstyle)) return true;
    if (quizAnswers.playstyle === 'Medical') return ['Rescue ship', 'Triage ship', 'Hospital ship'].includes(quizAnswers.subrole);
    if (quizAnswers.playstyle === 'Combat') return ['Gunship', 'Dropship', 'Capital combat'].includes(quizAnswers.subrole);
    return false;
  };

  const quizQuestionOrder = ['vehicleType', 'availability', 'playstyle', 'subrole', 'crew', 'travel', 'carrier', 'cargo', 'size', 'equipment', 'priority'];
  const quizQuestionDefinitions = () => {
    const base = quizAnswers.vehicleType === 'Vehicle' ? vehicleQuizQuestions : shipQuizQuestions;
    return [baseQuizQuestion,
      { id: 'availability', title: 'Do you want to use it in the game now?', options: [['Flight ready only', 'Only ships or vehicles marked flight ready.'], ['Include unreleased', 'Also consider future ships, clearly marked.']] },
      base.find(q => q.id === 'playstyle'),
      { ...base.find(q => q.id === 'subrole'), title: quizAnswers.vehicleType === 'Vehicle' ? 'Which vehicle job fits best?' : 'Which specialization do you need?', optional: true },
      { ...base.find(q => q.id === 'crew'), optional: true },
      ...(quizAnswers.vehicleType === 'Ship' ? [{ id: 'travel', title: 'Does it need to travel independently?', dynamic: true, optional: true, options: [['Independent travel', 'A quantum drive is essential. I do not want to depend on a carrier.'], ['Carrier supported', 'I can transport a snub or use it locally; independent ships are also fine.']] }] : []),
      ...(quizAnswers.vehicleType === 'Vehicle' ? [{ id: 'carrier', title: 'Does it need a documented fit in one of these carriers?', dynamic: true, optional: true }] : []),
      { id: 'cargo', title: 'What is your minimum freight capacity?', dynamic: true, optional: true },
      ...(quizAnswers.vehicleType === 'Ship' ? [{ id: 'size', title: 'Do you need a particular ship size?', dynamic: true, optional: true }] : []),
      { id: 'equipment', title: 'Which additional equipment is essential?', dynamic: true, optional: true },
      { id: 'priority', title: 'Which trade-off matters most?', dynamic: true, optional: true }
    ];
  };
  const activeQuizQuestions = () => {
    if (!quizAnswers.vehicleType) return [baseQuizQuestion];
    const prefix = {}, questions = [];
    for (const question of quizQuestionDefinitions()) {
      const answered = Object.prototype.hasOwnProperty.call(quizAnswers, question.id);
      const pool = quizCandidateShips('', prefix);
      const options = question.dynamic ? dynamicOptionsForQuiz(question, prefix) : question.options;
      let useful = !question.optional;
      if (question.optional && new Set(pool.map(core.family)).size > 1) {
        const baseline = core.recommend(pool, prefix).map(pick => core.family(pick.ship)).join('|');
        useful = options.some(([value]) => core.recommend(pool, { ...prefix, [question.id]: value }).map(pick => core.family(pick.ship)).join('|') !== baseline);
        if (['crew', 'subrole', 'size'].includes(question.id) && options.length < 2) useful = false;
      }
      if (answered || useful) questions.push(question);
      if (answered) prefix[question.id] = quizAnswers[question.id];
    }
    return questions;
  };

  const optionList = (select, label, values) => {
    select.innerHTML = [`<option value="">${label}</option>`].concat(values.map((value) => `<option value="${String(value).replace(/"/g, '&quot;')}">${value}</option>`)).join('');
  };

  const renderManufacturerButtons = (makers, disabledMakers = new Set()) => {
    selectedMakers.forEach((maker) => {
      if (!makers.includes(maker)) selectedMakers.delete(maker);
    });

    els.makerButtons.innerHTML = '';
    const allButton = document.createElement('button');
    allButton.type = 'button';
    allButton.className = `ibg-maker-button${selectedMakers.size ? '' : ' is-active'}`;
    allButton.textContent = 'All manufacturers';
    allButton.dataset.maker = '';
    allButton.setAttribute('aria-pressed', selectedMakers.size ? 'false' : 'true');
    els.makerButtons.appendChild(allButton);

    makers.forEach((maker) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ibg-maker-button${selectedMakers.has(maker) ? ' is-active' : ''}`;
      button.textContent = maker;
      button.dataset.maker = maker;
      button.setAttribute('aria-pressed', selectedMakers.has(maker) ? 'true' : 'false');
      if (!selectedMakers.has(maker) && disabledMakers.has(maker)) {
        button.disabled = true;
        button.title = 'No matches with the current filters';
      }
      els.makerButtons.appendChild(button);
    });
  };

  const resetVisibleLimit = () => {
    visibleLimit = INITIAL_VISIBLE;
  };

  const cargoBand = (cargo) => {
    if (cargo <= 0) return 'No cargo';
    if (cargo <= 24) return '1-24 SCU';
    if (cargo <= 99) return '25-99 SCU';
    if (cargo <= 499) return '100-499 SCU';
    if (cargo <= 999) return '500-999 SCU';
    return '1000+ SCU';
  };

  const soloSmallGroupNames = [
    'freelancer',
    'cutlass',
    'spirit',
    'zeus',
    'constellation',
    'corsair',
    'mercury star runner',
    'raft',
    'reliant',
    'terrapin',
    '400i'
  ];

  const soloSmallGroupShip = (ship) => {
    if (ship.type === 'Vehicle') return false;
    const text = normalize(ship.name);
    if (!ship.crewMin || !ship.crewMax || ship.crewMax <= 1 || ship.crewMax > 4 || ship.crewMin > 3) return false;
    if (soloSmallGroupNames.some((name) => text.includes(name))) return true;
    return ship.crewMax > 1 && ship.crewMin <= 1 && ship.crewMax <= 4 && (ship.size === 'Medium' || ship.size === 'Large') && (ship.cargo > 0 || ship.tags.includes('Cargo') || ship.tags.includes('Exploration') || ship.tags.includes('Industrial'));
  };

  const singleSeatShip = (ship) => {
    if (!ship.crewMax) return false;
    if (ship.type === 'Vehicle') return ship.crewMax === 1;
    if (soloSmallGroupShip(ship)) return false;
    return ship.crewMax <= 1;
  };

  const crewBand = (ship) => {
    if (!ship.crewMin || !ship.crewMax) return 'Unknown';
    if (singleSeatShip(ship)) return 'Single seater';
    if (soloSmallGroupShip(ship) || ship.crewMax <= 3) return 'Solo or small group';
    if (ship.crewMax <= 8) return 'Group crew';
    return 'Org crew';
  };
  const quizCrewBands = (ship) => {
    return core.crewBands(ship);
  };
  const crewText = (ship) => !ship.crewMin || !ship.crewMax ? 'Unknown' : ship.crewMin === ship.crewMax ? String(ship.crewMax) : `${ship.crewMin}-${ship.crewMax}`;
  const statText = (ship, field, suffix = '') => core.known(ship, field) === null ? 'Unknown' : `${core.known(ship, field).toLocaleString()}${suffix}`;
  const displayStatus = (status) => {
    const value = normalize(status);
    if (value.includes('flight ready') || value.includes('released') || value === 'flightready') return ['Flight ready', 'ready'];
    if (value.includes('concept')) return ['In concept', 'concept'];
    if (value.includes('production') || value.includes('greybox') || value.includes('whitebox')) return ['In production', 'production'];
    return [status && status !== 'Unknown' ? status : 'Status TBD', 'production'];
  };
  const statusBand = (ship) => {
    const status = displayStatus(ship.status)[1];
    if (status === 'ready') return 'Flight ready';
    if (status === 'concept') return 'In concept';
    return 'In production / TBD';
  };
  const catalogFilterState = (overrides = {}) => ({
    term: normalize(Object.prototype.hasOwnProperty.call(overrides, 'term') ? overrides.term : els.search.value),
    role: Object.prototype.hasOwnProperty.call(overrides, 'role') ? overrides.role : els.role.value,
    subrole: Object.prototype.hasOwnProperty.call(overrides, 'subrole') ? overrides.subrole : els.subrole.value,
    makers: Object.prototype.hasOwnProperty.call(overrides, 'makers') ? overrides.makers : selectedMakers,
    size: Object.prototype.hasOwnProperty.call(overrides, 'size') ? overrides.size : els.size.value,
    cargo: Object.prototype.hasOwnProperty.call(overrides, 'cargo') ? overrides.cargo : els.cargo.value,
    crew: Object.prototype.hasOwnProperty.call(overrides, 'crew') ? overrides.crew : els.crew.value,
    status: Object.prototype.hasOwnProperty.call(overrides, 'status') ? overrides.status : els.status.value
  });
  const shipMatchesCatalogFilters = (ship, overrides = {}) => {
    const state = catalogFilterState(overrides);
    if (catalogView === 'rankings' && rankingReadyOnly && !core.isReady(ship)) return false;
    if (state.term && !ship.search.includes(state.term)) return false;
    if (state.role && !ship.tags.includes(state.role)) return false;
    if (state.subrole && !ship.subroles.includes(state.subrole)) return false;
    if (state.makers?.size && !state.makers.has(ship.manufacturer)) return false;
    if (state.size && ship.size !== state.size) return false;
    if (state.cargo && core.cargoBand(core.known(ship, 'cargo')) !== state.cargo) return false;
    if (state.crew && !core.crewBands(ship).includes(state.crew)) return false;
    if (state.status && statusBand(ship) !== state.status) return false;
    return true;
  };
  const sizeRank = (size) => ['Ground vehicle','Snub','Small','Medium','Large','Capital','Unknown'].indexOf(size);
  const sortShips = (items) => {
    const sort = els.sort?.value || '';
    const byName = (a, b) => a.name.localeCompare(b.name);
    const sorted = items.slice();
    if (sort === 'Manufacturer') sorted.sort((a, b) => a.manufacturer.localeCompare(b.manufacturer) || byName(a, b));
    else if (sort === 'Cargo high to low') sorted.sort((a, b) => number(b.cargo) - number(a.cargo) || byName(a, b));
    else if (sort === 'Crew solo first') sorted.sort((a, b) => number(a.crewMax) - number(b.crewMax) || byName(a, b));
    else if (sort === 'Speed high to low') sorted.sort((a, b) => number(b.nav || b.scm) - number(a.nav || a.scm) || byName(a, b));
    else if (sort === 'Size small to large') sorted.sort((a, b) => sizeRank(a.size) - sizeRank(b.size) || byName(a, b));
    else if (sort === 'Size large to small') sorted.sort((a, b) => sizeRank(b.size) - sizeRank(a.size) || byName(a, b));
    else sorted.sort(byName);
    return sorted;
  };
  const compareId = (ship) => core.identity(ship);
  const comparedShips = () => compareIds.map((id) => ships.find((ship) => compareId(ship) === id)).filter(Boolean);
  const isCompareBetter = (ship, field) => {
    const pair = comparedShips();
    if (pair.length !== 2 || !field) return false;
    if (!compareIds.includes(compareId(ship))) return false;
    const other = pair.find((item) => compareId(item) !== compareId(ship));
    return core.difference(ship, other, field, ['em', 'ir'].includes(field) ? 'lower' : ['length', 'crewMin', 'crewMax', 'mass'].includes(field) ? 'neutral' : 'higher').better;
  };
  const spec = (ship, label, display, field = '', highlight = false) => {
    const better = highlight && field && isCompareBetter(ship, field) ? ' is-better' : '';
    return `<div class="ibg-spec${better}"><span>${escapeHtml(label)}</span><strong>${escapeHtml(display)}</strong></div>`;
  };
  const stat = (ship, label, value, suffix = '', field = '', highlight = false) => number(value) ? spec(ship, label, `${number(value).toLocaleString()}${suffix}`, field, highlight) : '';
  const advancedSpecs = (ship, highlight = false) => [
    stat(ship, 'Hull HP', ship.health, '', 'health', highlight),
    stat(ship, 'Armor', ship.armor, '', 'armor', highlight),
    stat(ship, 'Mass', ship.mass, ' kg'),
    stat(ship, 'EM sig', ship.em, '', 'em', highlight),
    stat(ship, 'IR sig', ship.ir, '', 'ir', highlight)
  ].join('');
  const compareSpecs = (ship) => {
    const other = comparedShips().find(item => compareId(item) !== compareId(ship));
    const fields = [['Cargo', 'cargo', ' SCU'], ['Ore', 'ore', ' SCU'], ['Crew min', 'crewMin', '', 'neutral'], ['Crew max', 'crewMax', '', 'neutral'], ['Length', 'length', ' m', 'neutral'], ['SCM', 'scm', ' m/s'], ['NAV', 'nav', ' m/s'], ['Shield', 'shield', ' HP'], ['Hull HP', 'health', ''], ['Armor', 'armor', ''], ['Mass', 'mass', ' kg', 'neutral']];
    const cells = fields.map(([label, field, suffix, direction]) => {
      const diff = other ? core.difference(ship, other, field, direction) : { delta: null };
      if (onlyDifferences && other && diff.same) return '';
      const delta = diff.delta !== null && diff.delta !== 0 ? `<small class="ibg-delta">${diff.delta > 0 ? '+' : ''}${diff.delta.toLocaleString()}${suffix}</small>` : '';
      return `<div class="ibg-spec${diff.better ? ' is-better' : ''}" data-comparison-stat="${field}"><span>${label}</span><strong>${statText(ship, field, suffix)}</strong>${delta}</div>`;
    });
    const own = core.capabilities(ship), theirs = other ? core.capabilities(other) : {};
    for (const [key, capability] of Object.entries(own)) {
      if (onlyDifferences && other && capability.value !== null && capability.value === theirs[key]?.value) continue;
      cells.push(`<div class="ibg-spec ibg-spec--capability" data-comparison-stat="${key}"><span>${capability.label}</span><strong>${capability.value === true ? (core.isReady(ship) ? 'Listed' : 'Planned') : capability.value === false ? 'Not equipped' : 'Not confirmed'}</strong></div>`);
    }
    return cells.filter(Boolean).join('') || '<p class="ibg-muted">No confirmed differences in the compared fields.</p>';
  };
  const compareImageFor = (ship) => ship.imageOriginal || ship.image || FALLBACK_IMAGE;
  const comparePanelCard = (ship) => `
    <article class="ibg-compare-card">
      <img src="${escapeHtml(compareImageFor(ship))}" alt="${escapeHtml(ship.name)}" loading="lazy" decoding="async" referrerpolicy="no-referrer"${shipImageFallbackAttributes(ship, compareImageFor(ship), 720)}>
      <div>
        <h3>${escapeHtml(ship.name)}</h3>
        <div class="ibg-maker">${escapeHtml(ship.manufacturer)}</div>
        <div class="ibg-specs">${compareSpecs(ship)}</div>
        <button class="ibg-compare-toggle is-active" type="button" data-compare-id="${compareId(ship)}" aria-pressed="true">Remove</button>
      </div>
    </article>
  `;
  const updateCompareBar = () => {
    const pair = comparedShips();
    els.compareBar.hidden = compareIds.length === 0;
    els.compareCards.innerHTML = pair.map(comparePanelCard).join('');
    if (!compareIds.length) {
      els.compareCopy.textContent = 'Select two ships or vehicles to compare key stats.';
      return;
    }
    if (pair.length === 1) {
      els.compareCopy.innerHTML = `<strong>${pair[0].name}</strong> selected. Pick one more to compare.`;
      return;
    }
    els.compareCopy.innerHTML = `<strong>${escapeHtml(pair[0].name)}</strong> vs <strong>${escapeHtml(pair[1].name)}</strong>. Green marks higher capacity, speed or durability, not an overall winner. Listed equipment and stock stats may change.`;
  };

  const quizCandidateShips = (excludeId = '', sourceAnswers = quizAnswers) => ships.filter(ship => {
    const answers = { ...sourceAnswers };
    delete answers[excludeId];
    return core.satisfies(ship, answers, quizCrewBands);
  });

  const dedupeShips = (items) => {
    const best = new Map();
    const makerKey = (maker) => {
      const value = normalize(maker);
      const aliases = {
        'musashi industrial starflight concern': 'misc',
        'musashi industrial and starflight concern': 'misc',
        'roberts space industries': 'rsi',
        'argo astronautics': 'argo',
        'consolidated outland': 'cnou',
        'origin jumpworks': 'origin',
        'drake interplanetary': 'drake',
        'crusader industries': 'crusader',
        'aegis dynamics': 'aegis',
        'anvil aerospace': 'anvil',
        'tumbril land systems': 'tumbril',
        'tumbril': 'tumbril',
        'greycat industrial': 'greycat',
        'kruger intergalactic': 'kruger',
        'kruger intergalatic': 'kruger',
        'gatac manufacture': 'gatac',
        'esperia': 'alien-replica',
        'vanduul': 'alien-replica',
        'grey s market': 'greys-market'
      };
      return aliases[value] || value;
    };
    const publicNameKey = (ship) => {
      let name = ship.shipmatrix_name || ship.name || '';
      name = name.replace(/^Argo\s+ATLS\s+IKTI.*$/i, 'ATLS');
      name = name.replace(/^(Esperia|Vanduul)\s+/i, '');
      name = name.replace(/\s+(PYAM Exec|Wikelo.*|Teach's Special|Dunlevy|Star Kitten|Best In Show|BIS|ILW|IAE|Executive Edition|Warbond Edition|Limited Edition|Collector.*|Orange Line|Snowland Color|Cool Metal Color)$/i, '');
      name = name.replace(/\s+(Paint|Skin|Livery|Color|Colour)$/i, '');
      return name.trim();
    };
    const merge = (current, next) => core.mergeRecords(current, next);

    items.forEach((ship) => {
      const publicName = publicNameKey(ship);
      const maker = publicName && publicName.includes('ATLS') ? 'Argo Astronautics' : ship.manufacturer;
      const key = normalize(`${makerKey(maker)} ${publicName}`);
      best.set(key, merge(best.get(key), ship));
    });

    return Array.from(best.values()).map(cleanMappedShipTags);
  };

  const roleTagIsImplied = (tag, subroles) => {
    const normalizedTag = normalize(tag);
    const mappedSubroles = subrolesByPlaystyle[tag] || [];
    return subroles.some((subrole) => {
      const normalizedSubrole = normalize(subrole);
      return normalizedSubrole.includes(normalizedTag) || mappedSubroles.includes(subrole);
    });
  };

  const visibleTagsFor = (ship, category = primaryCategory(ship)) => ship.tags.filter((tag) => {
    if (tag === category) return false;
    if (ship.type === 'Vehicle' && (tag === 'Ground' || tag === 'Surface Support')) return false;
    return !roleTagIsImplied(tag, ship.subroles || []);
  });

  const matchReasons = (ship) => {
    if (mode !== 'quiz') return [];
    const reasons = [];
    if (quizAnswers.playstyle && ship.tags.includes(quizAnswers.playstyle)) reasons.push(quizAnswers.playstyle);
    if (quizAnswers.subrole && ship.subroles.includes(quizAnswers.subrole)) reasons.push(quizAnswers.subrole);
    if (quizAnswers.crew && quizCrewBands(ship).includes(quizAnswers.crew)) reasons.push(quizAnswers.crew);
    if (quizAnswers.cargo && cargoBand(ship.cargo) === quizAnswers.cargo) reasons.push(quizAnswers.cargo);
    if (quizAnswers.size && ship.size === quizAnswers.size) reasons.push(ship.size);
    return Array.from(new Set(reasons)).slice(0, 4);
  };

  const galleryImagesFor = (ship, id) => {
    const cached = galleryCache.get(id) || [];
    return Array.from(new Set([ship.image, ...(cached || [])].filter(Boolean))).slice(0, 6);
  };

  const renderGallery = (ship, id) => {
    const images = galleryImagesFor(ship, id);
    const loading = galleryLoading.has(id);
    const index = Math.min(galleryIndexes.get(id) || 0, Math.max(images.length - 1, 0));
    const image = index === 0 ? (ship.image || optimizedImageUrl(ship.imageOriginal, 720)) : optimizedImageUrl(images[index] || ship.imageOriginal || ship.image, 1200);
    const multiple = images.length > 1;
    const thumbs = multiple ? `
      <div class="ibg-gallery-thumbs" aria-label="${escapeHtml(ship.name)} gallery thumbnails">
        ${images.map((thumb, thumbIndex) => {
          const thumbImage = thumbIndex === 0 ? (ship.image || optimizedImageUrl(ship.imageOriginal, 220)) : optimizedImageUrl(thumb, 220);
          return `<button class="ibg-gallery-thumb${thumbIndex === index ? ' is-active' : ''}" type="button" data-gallery-thumb="${id}" data-gallery-index="${thumbIndex}" aria-label="Show image ${thumbIndex + 1}"><img src="${escapeHtml(thumbImage)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer"${imageFallbackAttributes(thumbImage, [ship.image, ship.imageOriginal], 220)}></button>`;
        }).join('')}
      </div>
    ` : '';
    return `
      <div class="ibg-detail-panel">
        <div class="ibg-gallery" data-gallery="${id}">
          <img src="${escapeHtml(image)}" alt="${escapeHtml(ship.name)} gallery image" loading="lazy" decoding="async" referrerpolicy="no-referrer"${imageFallbackAttributes(image, [...images.slice(index + 1), ...images.slice(0, index), ship.imageOriginal, ...localGalleryFor(ship)], 1200)}>
          <button class="ibg-gallery-nav ibg-gallery-nav--prev" type="button" data-gallery-prev="${id}" ${multiple ? '' : 'disabled'} aria-label="Previous image">&#8249;</button>
          <button class="ibg-gallery-nav ibg-gallery-nav--next" type="button" data-gallery-next="${id}" ${multiple ? '' : 'disabled'} aria-label="Next image">&#8250;</button>
          <div class="ibg-gallery-meta">${loading ? 'Loading gallery...' : `${images.length || 1} image${images.length === 1 ? '' : 's'} loaded`}</div>
        </div>
        ${thumbs}
      </div>
    `;
  };

  const ensureGallery = async (ship, id) => {
    if (!ship || galleryCache.has(id) || galleryLoading.has(id)) return;
    galleryLoading.add(id);
    renderDetailModal();
    const localImages = localGalleryFor(ship).filter((image) => image !== ship.image && image !== ship.imageOriginal).slice(0, 5);
    if (imageManifestLoaded) {
      galleryCache.set(id, localImages);
      galleryLoading.delete(id);
      if (!galleryIndexes.has(id)) galleryIndexes.set(id, 0);
      renderDetailModal();
      return;
    }
    const wikiImages = await wikiPageGallery(ship.wikiTitle || ship.name);
    const images = Array.from(new Set((wikiImages || []).filter((image) => image !== ship.image && image !== ship.imageOriginal))).slice(0, 5);
    galleryCache.set(id, images);
    galleryLoading.delete(id);
    if (!galleryIndexes.has(id)) galleryIndexes.set(id, 0);
    renderDetailModal();
  };

  const componentSizeValue = (component) => {
    const text = String(component.size || component.label || component.name || '');
    const numeric = text.match(/(?:S|Size)\s*(\d+)|\((\d+)\)/i)?.slice(1).find(Boolean);
    if (numeric) return Number(numeric);
    const token = text.match(/\b(C|L|M|S)\b/i)?.[1]?.toUpperCase();
    return ({ S: 1, M: 2, L: 3, C: 5 })[token] || 1;
  };

  const componentQuantityValue = (component) => {
    const text = String(component.label || component.name || '');
    const explicitQty = Number((text.match(/(?:^|,\s*)(\d+)x/i) || [])[1] || 0);
    const mounts = Number(component.mounts || (text.match(/(\d+)\s*mount/i) || [])[1] || 0);
    if (explicitQty && mounts > 1) return explicitQty * mounts;
    return Number(explicitQty || mounts || component.quantity || 1);
  };

  const componentGroups = (components) => {
    const wanted = ['weapons', 'turrets', 'missiles', 'utility_items', 'shield_generators', 'power_plants', 'coolers', 'quantum_drives', 'jump_modules', 'radar', 'computers', 'fuel_tanks', 'quantum_fuel_tanks', 'main_thrusters', 'maneuvering_thrusters'];
    const labels = {
      weapons: 'Weapons',
      turrets: 'Turrets',
      missiles: 'Missiles',
      utility_items: 'Utility items',
      shield_generators: 'Shield generators',
      power_plants: 'Power plants',
      coolers: 'Coolers',
      quantum_drives: 'Quantum drive',
      jump_modules: 'Jump module',
      radar: 'Radar',
      computers: 'Computers',
      fuel_tanks: 'Fuel system',
      quantum_fuel_tanks: 'Quantum fuel',
      main_thrusters: 'Main thrusters',
      maneuvering_thrusters: 'Maneuvering thrusters'
    };
    const consolidateLabels = (items) => {
      const counts = new Map();
      for (const component of items) {
        const label = component.label || component.name;
        if (!label) continue;
        counts.set(label, (counts.get(label) || 0) + 1);
      }
      return Array.from(counts.entries()).map(([label, count]) => count > 1 ? `${count}x ${label}` : label);
    };
    return wanted.map((type) => {
      const items = (components || []).filter((component) => component.type === type);
      if (!items.length) return '';
      const details = consolidateLabels(items).join(', ');
      return `<div class="ibg-component"><strong>${escapeHtml(labels[type] || type.replace(/_/g, ' '))}</strong><span>${escapeHtml(details)}</span></div>`;
    }).filter(Boolean).join('');
  };

  const roleSignals = (vehicle) => {
    const roleLine = `${vehicle?.role || ''} ${vehicle?.type || ''}`.toLowerCase();
    const fullText = `${roleLine} ${vehicle?.description || ''}`.toLowerCase();
    return {
      roleLine,
      fullText,
      combat: /combat|fighter|gunship|bomber|military|corvette|patrol|strike|interceptor/.test(roleLine),
      fighter: /fighter|interceptor|dogfight/.test(roleLine),
      bomber: /bomber|torpedo|anti-capital/.test(roleLine),
      carrier: /carrier|hangar|snub transport|landing pads?|small craft|snub craft/.test(fullText),
      freight: /cargo|freight|hauling|transport|merchant|logistics/.test(roleLine),
      explorer: /exploration|expedition|pathfinder|scout|mapping|reconnaissance/.test(fullText),
      industrial: /industrial|mining|salvage|tractor|refuel|rearm|repair services|repair facilities|repair capabilities/.test(fullText),
      medical: /medical|med bay|medbed|rescue/.test(fullText),
      serviceSupport: /refuel|rearm|repair services|repair facilities|repair capabilities|support services/.test(fullText),
      racing: /racing|racer|speed/.test(roleLine)
    };
  };

  const componentStats = (components) => {
    return (components || []).reduce((stats, component) => {
      const text = `${component.type || ''} ${component.name || ''} ${component.label || ''}`.toLowerCase();
      const qty = componentQuantityValue(component);
      const size = componentSizeValue(component);
      const power = size * qty;
      if (/weapons?/.test(component.type || '') || /\bweapon|laser|repeater|cannon|ballistic/.test(text)) {
        stats.weapon += power;
        stats.maxWeaponSize = Math.max(stats.maxWeaponSize, size);
      }
      if (/turrets?/.test(component.type || '') || /turret/.test(text)) {
        stats.turret += power * 0.7;
        stats.maxWeaponSize = Math.max(stats.maxWeaponSize, size);
      }
      if (/missiles?/.test(component.type || '') || /missile|torpedo/.test(text)) {
        stats.missile += power * (/torpedo/.test(text) ? 1.35 : 0.55);
        stats.maxWeaponSize = Math.max(stats.maxWeaponSize, size);
      }
      if (/shield/.test(text)) stats.shield += power;
      if (/power plant/.test(text) || /power_plants/.test(component.type || '')) stats.powerPlant += power;
      if (/cooler/.test(text)) stats.cooler += power;
      if (/utility|tractor|scanner|mining|salvage|repair|refuel|rearm/.test(text)) stats.utility += Math.max(1, qty);
      return stats;
    }, { weapon: 0, turret: 0, missile: 0, maxWeaponSize: 0, shield: 0, powerPlant: 0, cooler: 0, utility: 0 });
  };

  const estimatedScores = (vehicle) => {
    const components = vehicle?.components || [];
    const stats = componentStats(components);
    const signals = roleSignals(vehicle);
    const sizeKey = normalize(vehicle?.size);
    const crewMax = Number(vehicle?.crew?.max || vehicle?.crew?.min || 0);
    const cargo = Number(vehicle?.cargo || 0);
    const scm = Number(vehicle?.speed?.scm || 0);
    const sizeScale = ({ snub: 0, vehicle: 2, small: 5, medium: 10, large: 16, capital: 28 })[sizeKey] ?? 8;
    const weaponBase = stats.weapon + stats.turret + stats.missile;
    let firepower = weaponBase > 0 ? 8 + Math.sqrt(weaponBase) * 5.5 + stats.maxWeaponSize * 3 : 6;
    if (signals.combat) firepower += 16;
    if (signals.fighter) firepower += 8;
    if (signals.bomber) firepower += 14;
    if (signals.carrier && !signals.combat) firepower -= 16;
    if ((signals.freight || signals.explorer || signals.medical) && !signals.combat) firepower -= 8;
    const defense = 8 + Math.sqrt(stats.shield) * 12 + stats.powerPlant * 1.8 + sizeScale;
    let agility = scm ? scm / 4 : 16;
    agility += ({ snub: 24, small: 16, medium: 5, large: -12, capital: -24, vehicle: 8 })[sizeKey] ?? 0;
    if (signals.fighter || signals.racing) agility += 12;
    if (signals.carrier || signals.freight) agility -= 5;
    let cargoUtility = cargo <= 0 ? 6 : 12 + Math.log10(cargo + 1) * 24;
    if (signals.freight) cargoUtility += 18;
    if (signals.carrier || signals.serviceSupport || /landing pads?|cargo grids?/.test(signals.fullText)) cargoUtility += 10;
    let utility = stats.utility * 12;
    if (signals.explorer) utility += 40;
    if (signals.industrial) utility += 30;
    if (signals.carrier) utility += 30;
    if (/hangar|landing pads?|small craft|snub craft/.test(signals.fullText)) utility += 18;
    if (/tractor/.test(signals.fullText)) utility += 14;
    if (signals.medical) utility += 22;
    if (/scanner|sensor|mapping|reconnaissance/.test(signals.fullText)) utility += 16;
    let support = Math.min(crewMax * 7, 28);
    if (signals.carrier) support += 42;
    if (signals.serviceSupport) support += 34;
    if (signals.medical) support += 38;
    if (/rescue|recovery/.test(signals.fullText)) support += 22;
    if (/command|flagship|crew quarters|living|self-sufficient/.test(signals.fullText)) support += 12;
    const clamp = (value, minimum = 6) => Math.max(minimum, Math.min(100, Math.round(value || 0)));
    const combatRoleDampener = signals.combat && !signals.carrier ? 0.82 : 1;
    return {
      firepower: clamp(firepower),
      defense: clamp(defense),
      crewDemand: clamp((crewMax / 10) * 100),
      cargoUtility: clamp(cargoUtility),
      agility: clamp(agility),
      combat: clamp(firepower, 8),
      speed: clamp(agility, 8),
      cargo: clamp(cargoUtility, 8),
      utility: clamp(utility * combatRoleDampener, 8),
      support: clamp(support * combatRoleDampener, 8)
    };
  };

  const scoreLabel = (score) => {
    if (score >= 84) return 'Extreme';
    if (score >= 68) return 'High';
    if (score >= 45) return 'Medium';
    if (score >= 24) return 'Low';
    return 'Light';
  };

  const operationalProfileMarkup = (vehicle) => {
    if (!vehicle) return '';
    const scores = estimatedScores(vehicle);
    const crewMax = Number(vehicle.crew?.max || vehicle.crew?.min || 0);
    const cargo = Number(vehicle.cargo || 0);
    const scm = Number(vehicle.speed?.scm || 0);
    const profiles = [
      { name: 'Firepower', color: '#ef233c', soft: 'rgba(239,35,60,0.30)', score: scores.firepower, detail: `${scoreLabel(scores.firepower)} output` },
      { name: 'Defense', color: '#3b82f6', soft: 'rgba(59,130,246,0.30)', score: scores.defense, detail: `${scoreLabel(scores.defense)} resilience` },
      { name: 'Crew Demand', color: '#10b981', soft: 'rgba(16,185,129,0.30)', score: scores.crewDemand, detail: crewMax > 1 ? `Up to ${crewMax} crew` : 'Solo friendly' },
      { name: 'Cargo Utility', color: '#f97316', soft: 'rgba(249,115,22,0.30)', score: scores.cargoUtility, detail: cargo ? `${fmt(cargo, ' SCU')}` : 'Minimal' },
      { name: 'Agility', color: '#d946ef', soft: 'rgba(217,70,239,0.30)', score: scores.agility, detail: scm ? `${fmt(scm, ' m/s')} SCM` : 'TBD' }
    ].map((profile) => ({ ...profile, score: Math.max(6, Math.min(100, profile.score || 0)) }));
    return `
      <div class="ibg-holo-panel">
        <h3>Operational Profile</h3>
        <div class="ibg-profile-list">
          ${profiles.map((profile) => `
            <div class="ibg-profile-row" style="--ibg-profile-score: ${profile.score}%; --ibg-profile-color: ${profile.color}; --ibg-profile-soft: ${profile.soft};">
              <span class="ibg-profile-name">${escapeHtml(profile.name)}</span>
              <span class="ibg-profile-track" aria-hidden="true"></span>
              <span class="ibg-profile-value">${escapeHtml(profile.detail)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  };

  const roleDialMarkup = (vehicle) => {
    if (!vehicle) return '';
    const scoresEstimate = estimatedScores(vehicle);
    const clampScore = (value) => Math.max(8, Math.min(100, Math.round(value || 0)));
    const scores = [
      { key: 'combat', label: 'Combat', color: '#ef233c', soft: 'rgba(239,35,60,0.32)', score: clampScore(scoresEstimate.combat) },
      { key: 'speed', label: 'Speed', color: '#d946ef', soft: 'rgba(217,70,239,0.30)', score: clampScore(scoresEstimate.speed) },
      { key: 'cargo', label: 'Cargo', color: '#f97316', soft: 'rgba(249,115,22,0.30)', score: clampScore(scoresEstimate.cargo) },
      { key: 'utility', label: 'Utility', color: '#facc15', soft: 'rgba(250,204,21,0.30)', score: clampScore(scoresEstimate.utility) },
      { key: 'support', label: 'Support', color: '#10b981', soft: 'rgba(16,185,129,0.30)', score: clampScore(scoresEstimate.support) }
    ];
    const signals = roleSignals(vehicle);
    const dominantBias = (score) => {
      if (score.key === 'combat' && signals.combat) return 18;
      if (score.key === 'cargo' && signals.freight) return 18;
      if (score.key === 'support' && (signals.carrier || signals.medical || signals.serviceSupport)) return 18;
      if (score.key === 'utility' && (signals.explorer || signals.industrial)) return 18;
      if (score.key === 'speed' && signals.racing) return 18;
      return 0;
    };
    const dominant = scores.slice().sort((a, b) => (b.score + dominantBias(b)) - (a.score + dominantBias(a)) || b.score - a.score)[0] || scores[0];
    const center = 130;
    const radius = 82;
    const axes = [-90, -18, 54, 126, 198];
    const point = (angle, value) => {
      const rad = angle * Math.PI / 180;
      const distance = radius * (value / 100);
      return { x: center + Math.cos(rad) * distance, y: center + Math.sin(rad) * distance };
    };
    const ringPoints = (scale) => axes.map((angle) => {
      const p = point(angle, scale);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');
    const areaPoints = scores.map((score, index) => {
      const p = point(axes[index], score.score);
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');
    const labelPositions = axes.map((angle) => point(angle, 132));
    const anchor = (index) => index === 0 ? 'middle' : index < 3 ? 'start' : 'end';
    return `
      <div class="ibg-holo-panel ibg-role-dial" style="--ibg-dial-primary: ${dominant.color}; --ibg-dial-soft: ${dominant.soft};">
        <h3>Capability Dial</h3>
        <svg viewBox="0 0 260 260" role="img" aria-label="${escapeHtml(vehicle.name)} capability dial">
          <polygon class="dial-grid" points="${ringPoints(100)}"></polygon>
          <polygon class="dial-grid" points="${ringPoints(66)}"></polygon>
          <polygon class="dial-grid" points="${ringPoints(33)}"></polygon>
          ${axes.map((angle) => {
            const p = point(angle, 100);
            return `<line class="dial-axis" x1="${center}" y1="${center}" x2="${p.x.toFixed(1)}" y2="${p.y.toFixed(1)}"></line>`;
          }).join('')}
          <polygon class="dial-area" points="${areaPoints}"></polygon>
          ${scores.map((score, index) => {
            const p = point(axes[index], score.score);
            const label = labelPositions[index];
            return `
              <circle class="dial-point" cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4" style="fill:${score.color};"></circle>
              <text class="dial-label" x="${label.x.toFixed(1)}" y="${label.y.toFixed(1)}" text-anchor="${anchor(index)}" style="fill:${score.color};">${escapeHtml(score.label)}</text>
              <text class="dial-score" x="${label.x.toFixed(1)}" y="${(label.y + 13).toFixed(1)}" text-anchor="${anchor(index)}">${score.score}</text>
            `;
          }).join('')}
        </svg>
      </div>
    `;
  };

  const scriptCache = {};
  const loadScript = (src) => {
    if (scriptCache[src]) return scriptCache[src];
    scriptCache[src] = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        if (existing.dataset.loaded === 'true') resolve();
        return;
      }
      const scriptTag = document.createElement('script');
      scriptTag.src = src;
      scriptTag.async = true;
      scriptTag.addEventListener('load', () => {
        scriptTag.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      scriptTag.addEventListener('error', reject, { once: true });
      document.head.appendChild(scriptTag);
    });
    return scriptCache[src];
  };

  const ensureViewerLibraries = async () => {
    if (window.THREE?.CTMLoader && window.CTM) return;
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.106.1/build/three.min.js');
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.106.1/examples/js/controls/OrbitControls.js');
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.106.1/examples/js/libs/lzma.js');
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.106.1/examples/js/libs/ctm.js');
    await loadScript('https://cdn.jsdelivr.net/npm/three@0.106.1/examples/js/loaders/ctm/CTMLoader.js');
  };

  const loadCtmGeometry = async (url, timeoutMs = 25000) => {
    const THREE = window.THREE;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Model request failed: ${response.status}`);
      const binaryData = new Uint8Array(await response.arrayBuffer());
      const stream = new window.CTM.Stream(binaryData);
      const file = new window.CTM.File(stream);
      return await new Promise((resolve) => {
        new THREE.CTMLoader()._createGeometry(file, resolve);
      });
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const startHoloTelemetry = (stage, vehicle) => {
    const rows = Array.from(stage.querySelectorAll('[data-ibg-telemetry-value]'));
    if (!rows.length || stage.dataset.ibgTelemetry === 'true') return;
    stage.dataset.ibgTelemetry = 'true';
    const seed = Number(vehicle.id || vehicle.mass || vehicle.cargo || 1);
    let tick = seed % 997;
    const labels = ['SYS', 'MASS', 'VEC', 'SCAN'];
    const makeValue = (index) => {
      tick = (tick * 1664525 + 1013904223 + index) % 4294967296;
      const length = index === 1 ? 7 : 5;
      return String(tick % (10 ** length)).padStart(length, '0');
    };
    const update = () => {
      rows.forEach((row, index) => {
        const label = row.previousElementSibling;
        if (label) label.textContent = labels[(index + tick) % labels.length];
        row.textContent = makeValue(index);
      });
    };
    update();
    const interval = window.setInterval(update, 120);
    stage.addEventListener('ibg:holo-destroy', () => window.clearInterval(interval), { once: true });
  };

  const mountHoloviewer = async (ship) => {
    const vehicle = ship?.holoSpecs;
    if (!vehicle?.holoviewer?.model_3d) return;
    const stage = els.detailContent.querySelector('[data-ibg-holoviewer-stage]');
    if (!stage || stage.dataset.ibgMounted === 'true') return;
    stage.dataset.ibgMounted = 'true';
    stage.classList.add('is-loading');
    startHoloTelemetry(stage, vehicle);
    try {
      await ensureViewerLibraries();
      const THREE = window.THREE;
      if (!THREE?.CTMLoader) throw new Error('3D loader unavailable');
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, stage.clientWidth / stage.clientHeight, 0.1, 100000);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(stage.clientWidth, stage.clientHeight);
      renderer.setClearColor('#000000', 0.08);
      renderer.domElement.className = 'ibg-holo-canvas';
      stage.appendChild(renderer.domElement);
      const controls = new THREE.OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.18;
      controls.enablePan = false;
      controls.zoomSpeed = 0.28;
      scene.add(new THREE.AmbientLight(0x9b9b9b, 0.78));
      const keyLight = new THREE.DirectionalLight(0xf2f2f2, 0.96);
      keyLight.position.set(3, 4, 5);
      scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(0xa5a5a5, 0.34);
      rimLight.position.set(-4, 2, -5);
      scene.add(rimLight);
      const geometry = await loadCtmGeometry(absoluteRsiUrl(vehicle.holoviewer.model_3d));
      geometry.computeBoundingBox();
      geometry.center();
      geometry.computeVertexNormals();
      geometry.computeBoundingSphere();
      const box = geometry.boundingBox;
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const material = new THREE.MeshStandardMaterial({
        color: '#8a8a8a',
        roughness: 1,
        metalness: 0,
        emissive: '#000000',
        emissiveIntensity: 0,
        side: THREE.DoubleSide
      });
      const mesh = new THREE.Mesh(geometry, material);
      const rotation = vehicle.holoviewer.data || {};
      mesh.rotation.set(
        THREE.Math.degToRad(Number(rotation.rotation_x) || 0),
        THREE.Math.degToRad(Number(rotation.rotation_y) || 0),
        THREE.Math.degToRad(Number(rotation.rotation_z) || 0)
      );
      scene.add(mesh);
      stage.classList.remove('is-loading');
      stage.classList.add('is-loaded');
      camera.near = Math.max(maxDim / 100, 0.1);
      camera.far = maxDim * 100;
      camera.position.set(maxDim * 0.52, maxDim * 0.24, maxDim * 0.84);
      camera.updateProjectionMatrix();
      controls.target.set(0, 0, 0);
      const frameBox = new THREE.Box3().setFromObject(mesh);
      const frameCorners = [];
      for (const x of [frameBox.min.x, frameBox.max.x]) {
        for (const y of [frameBox.min.y, frameBox.max.y]) {
          for (const z of [frameBox.min.z, frameBox.max.z]) frameCorners.push(new THREE.Vector3(x, y, z));
        }
      }
      let fittedDistance = 0;
      const fitCamera = () => {
        const verticalHalfFov = THREE.Math.degToRad(camera.fov) / 2;
        const horizontalHalfFov = Math.atan(Math.tan(verticalHalfFov) * camera.aspect);
        const zoomRatio = fittedDistance ? camera.position.distanceTo(controls.target) / fittedDistance : 1;
        const direction = camera.position.clone().sub(controls.target).normalize();
        const right = new THREE.Vector3().crossVectors(camera.up, direction).normalize();
        const up = new THREE.Vector3().crossVectors(direction, right).normalize();
        const distance = 1.12 * Math.max(...frameCorners.map((corner) => corner.dot(direction) + Math.max(
          Math.abs(corner.dot(right)) / Math.tan(horizontalHalfFov),
          Math.abs(corner.dot(up)) / Math.tan(verticalHalfFov)
        )));
        camera.position.copy(controls.target).addScaledVector(direction, distance * zoomRatio);
        controls.minDistance = distance * 0.35;
        controls.maxDistance = distance * 2.2;
        fittedDistance = distance;
      };
      fitCamera();
      controls.update();
      const resize = () => {
        if (!stage.clientWidth || !stage.clientHeight) return;
        camera.aspect = stage.clientWidth / stage.clientHeight;
        fitCamera();
        camera.updateProjectionMatrix();
        renderer.setSize(stage.clientWidth, stage.clientHeight);
      };
      window.addEventListener('resize', resize);
      document.addEventListener('fullscreenchange', resize);
      document.addEventListener('webkitfullscreenchange', resize);
      const animate = () => {
        if (!stage.isConnected) {
          stage.dispatchEvent(new CustomEvent('ibg:holo-destroy'));
          window.removeEventListener('resize', resize);
          document.removeEventListener('fullscreenchange', resize);
          document.removeEventListener('webkitfullscreenchange', resize);
          controls.dispose();
          geometry.dispose();
          material.dispose();
          renderer.dispose();
          return;
        }
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();
    } catch (error) {
      stage.classList.remove('is-loading');
      stage.dataset.ibgMounted = 'false';
      const status = stage.querySelector('.ibg-holo-status');
      if (status) status.textContent = '3D model unavailable';
    }
  };

  const holoviewerMarkup = (ship) => {
    const vehicle = ship.holoSpecs;
    if (!vehicle?.holoviewer?.model_3d) return '';
    return `
      <div class="ibg-holo-panel ibg-holoviewer" aria-label="${escapeHtml(ship.name)} 3D model">
        <div class="ibg-holoviewer-stage" data-ibg-holoviewer-stage tabindex="0">
          <div class="ibg-holo-blueprint" aria-hidden="true"></div>
          <div class="ibg-holo-telemetry" aria-hidden="true">
            <span><b>SYS</b><i data-ibg-telemetry-value>${String((vehicle.id || 0) * 37).slice(0, 5).padStart(5, '0')}</i></span>
            <span><b>MASS</b><i data-ibg-telemetry-value>${String(Math.round(vehicle.mass || 0)).padStart(7, '0')}</i></span>
            <span><b>VEC</b><i data-ibg-telemetry-value>${String(Math.round((vehicle.dimensions?.length || 0) * 113)).padStart(5, '0')}</i></span>
            <span><b>SCAN</b><i data-ibg-telemetry-value>${String(Math.round((vehicle.cargo || 0) * 9 + (vehicle.crew?.max || 0) * 17)).padStart(5, '0')}</i></span>
          </div>
          <img class="ibg-holo-logo" src="https://cdn.shopify.com/s/files/1/0613/3569/9709/files/Group_156.png?v=1778971460" alt="" loading="lazy">
          <p class="ibg-holo-note">3D model is an official RSI visual reference and may be simplified, outdated, or different from the current in-game Star Citizen ship.</p>
          <div class="ibg-holo-loader" aria-hidden="true"></div>
          <button class="ibg-holo-prompt" type="button" data-ibg-holo-load><span>Initialize 3D model</span></button>
          <button class="ibg-holo-fullscreen" type="button" data-ibg-holo-fullscreen>Fullscreen</button>
          <div class="ibg-holo-status">Loading 3D model</div>
        </div>
      </div>
    `;
  };

  const holoDetailsMarkup = (ship) => {
    const vehicle = ship.holoSpecs;
    if (!vehicle) return '';
    const components = componentGroups(vehicle.components || []);
    return `
      <div class="ibg-holo-grid">
        ${holoviewerMarkup(ship) || `<div class="ibg-holo-panel ibg-holo-empty">No 3D holoviewer model is available in the current holo snapshot for this item.</div>`}
        ${operationalProfileMarkup(vehicle)}
        ${roleDialMarkup(vehicle)}
        ${components ? `<div class="ibg-holo-panel" style="grid-column: 1 / -1;"><h3>Components</h3><div class="ibg-component-grid">${components}</div></div>` : ''}
      </div>
    `;
  };

  const prepareHoloviewer = (ship) => {
    const stage = els.detailContent.querySelector('[data-ibg-holoviewer-stage]');
    if (!stage || stage.dataset.ibgPrepared === 'true') return;
    stage.dataset.ibgPrepared = 'true';
    startHoloTelemetry(stage, ship.holoSpecs);
    const load = () => mountHoloviewer(ship);
    stage.addEventListener('click', load);
    stage.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        load();
      }
    });
    const fullscreenButton = stage.querySelector('[data-ibg-holo-fullscreen]');
    const syncFullscreenButton = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenButton) fullscreenButton.textContent = fullscreenElement === stage ? 'Exit fullscreen' : 'Fullscreen';
    };
    document.addEventListener('fullscreenchange', syncFullscreenButton);
    document.addEventListener('webkitfullscreenchange', syncFullscreenButton);
    stage.addEventListener('ibg:holo-destroy', () => {
      document.removeEventListener('fullscreenchange', syncFullscreenButton);
      document.removeEventListener('webkitfullscreenchange', syncFullscreenButton);
    }, { once: true });
    fullscreenButton?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (fullscreenElement === stage) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
        if (exitFullscreen) exitFullscreen.call(document);
        return;
      }
      const fullscreen = stage.requestFullscreen || stage.webkitRequestFullscreen;
      if (fullscreen) fullscreen.call(stage);
      load();
    });
  };

  const fullSpecs = (ship) => `
    <div class="ibg-specs">
      ${spec(ship, 'Role', ship.role)}
      ${spec(ship, 'Size', ship.size)}
      ${spec(ship, 'Cargo', statText(ship, 'cargo', ' SCU'))}
      ${spec(ship, 'Crew', crewText(ship))}
      ${spec(ship, 'Length', fmtPositive(ship.length, ' m'))}
      ${spec(ship, 'Type', ship.type)}
      ${spec(ship, 'SCM', fmtPositive(ship.scm, ' m/s'))}
      ${spec(ship, 'NAV', fmtPositive(ship.nav, ' m/s'))}
      ${spec(ship, 'Shield', ship.shield ? ship.shield.toLocaleString() : 'TBD')}
      ${advancedSpecs(ship)}
    </div>
  `;

  const renderDetailModal = () => {
    if (!els.detailModal || !els.detailContent) return;
    els.detailContent.querySelectorAll('[data-ibg-holoviewer-stage]').forEach((stage) => stage.dispatchEvent(new CustomEvent('ibg:holo-destroy')));
    const ship = activeDetailId ? ships.find((item) => compareId(item) === activeDetailId) : null;
    if (!ship) {
      els.detailModal.hidden = true;
      els.detailContent.innerHTML = '';
      document.body.classList.remove('ibg-modal-open');
      return;
    }
    els.detailModal.hidden = false;
    document.body.classList.add('ibg-modal-open');
    const category = primaryCategory(ship);
    const visibleTags = visibleTagsFor(ship, category);
    els.detailContent.innerHTML = `
      <div class="ibg-modal-layout">
        <div class="ibg-modal-media">
          ${renderGallery(ship, activeDetailId)}
        </div>
        <div class="ibg-modal-main">
          <div class="ibg-detail-tools">${favoriteButton(ship)}<button class="ibg-action" type="button" data-share-view>Share ship</button></div>
          <span class="ibg-role-badge"${categoryThemeStyle(category)}>${category}</span>
          <h2 id="ibg-detail-title-${config.sectionId}">${escapeHtml(ship.name)}</h2>
          <div class="ibg-maker">${escapeHtml(ship.manufacturer)}</div>
          <p class="ibg-modal-desc">${escapeHtml(ship.description)}</p>
          <div class="ibg-tags">
            ${visibleTags.map((tag) => `<span class="ibg-tag">${escapeHtml(tag)}</span>`).join('')}
            ${ship.subroles.map((subrole) => `<span class="ibg-tag">${escapeHtml(subrole)}</span>`).join('')}
          </div>
          <div class="ibg-modal-actions">
            <a class="ibg-link" href="${ship.url}">Buy Now ${icon('ArrowUpRight')}</a>
            <a class="ibg-link ibg-link--secondary" href="${ship.shipGuide}">${icon('BookOpen')}Ship Guide</a>
          </div>
        </div>
        <div class="ibg-modal-specs">${fullSpecs(ship)}</div>
      </div>
      ${holoDetailsMarkup(ship)}
    `;
    prepareHoloviewer(ship);
  };

  const openDetailModal = (id) => {
    if (!ships.some(ship => compareId(ship) === id)) return;
    detailTrigger = document.activeElement;
    activeDetailId = id;
    galleryIndexes.set(id, galleryIndexes.get(id) || 0);
    renderDetailModal();
    els.detailModal.querySelector('.ibg-modal-close')?.focus({ preventScroll: true });
    ensureGallery(ships.find((item) => compareId(item) === id), id);
    track('ship_details', { ship: id });
    if (initialized) history.pushState({ ibgDetail: id }, '', location.href);
  };

  const closeDetailModal = () => {
    activeDetailId = '';
    renderDetailModal();
    if (detailTrigger?.isConnected) detailTrigger.focus({ preventScroll: true });
    detailTrigger = null;
    if (history.state?.ibgDetail) history.back();
    else {
      const shared = readSharedState();
      if (shared?.detail) { shared.detail = ''; const url = new URL(location.href); url.searchParams.set('ibg', JSON.stringify(shared)); history.replaceState(history.state, '', url); }
    }
  };

  const card = (ship) => {
    const id = compareId(ship);
    const selected = compareIds.includes(id);
    const category = primaryCategory(ship);
    const visibleTags = visibleTagsFor(ship, category);
    const reasons = quizComplete ? [] : matchReasons(ship);
    const cardTags = [...new Set([...ship.subroles, ...visibleTags])];
    return `
    <article class="ibg-card${selected ? ' is-compare-selected' : ''}">
      <div class="ibg-image-wrap" data-detail-id="${id}" role="button" tabindex="0" aria-label="Show more details for ${escapeHtml(ship.name)}">
        <img class="ibg-image" src="${escapeHtml(ship.image)}" alt="${escapeHtml(ship.name)}" loading="lazy" decoding="async" referrerpolicy="no-referrer"${shipImageFallbackAttributes(ship, ship.image, 720)}>
        <span class="ibg-role-badge"${categoryThemeStyle(category)}>${category}</span>
        <span class="ibg-status ibg-status--header ibg-status--${displayStatus(ship.status)[1]}">${displayStatus(ship.status)[0]}</span>
        <span class="ibg-image-open" aria-hidden="true">${icon('ArrowUpRight')}</span>
      </div>
      <div class="ibg-card-body">
        <div class="ibg-top">
          <div>
            <div class="ibg-maker">${escapeHtml(ship.manufacturer)}</div>
            <h3>${escapeHtml(ship.name)}</h3>
          </div>
          ${favoriteButton(ship)}
        </div>
        <div class="ibg-tags">
          ${cardTags.slice(0, 2).map((tag) => `<span class="ibg-tag">${escapeHtml(tag)}</span>`).join('')}
          ${cardTags.length > 2 ? `<span class="ibg-tag" title="${escapeHtml(cardTags.slice(2).join(', '))}">+${cardTags.length - 2}</span>` : ''}
        </div>
        ${reasons.length ? `<div class="ibg-match">${reasons.map((reason) => `<span>${reason}</span>`).join('')}</div>` : ''}
        <p class="ibg-desc">${escapeHtml(ship.description)}</p>
        ${mode === 'quiz' ? recommendationMarkup(ship) : ''}
        <button class="ibg-detail-toggle" type="button" data-detail-id="${id}">Show more details ${icon('ArrowRight')}</button>
        <div class="ibg-specs">
          ${spec(ship, 'Cargo', statText(ship, 'cargo', ' SCU'))}
          ${spec(ship, 'Crew', crewText(ship))}
          ${spec(ship, 'Size', ship.size)}
          ${spec(ship, 'Length', fmtPositive(ship.length, ' m'))}
          ${spec(ship, 'SCM speed', fmtPositive(ship.scm, ' m/s'))}
          ${spec(ship, 'Shield', ship.shield ? ship.shield.toLocaleString() : 'TBD')}
        </div>
        <div class="ibg-utility-actions">
          <button class="ibg-compare-toggle${selected ? ' is-active' : ''}" type="button" data-compare-id="${id}" aria-pressed="${selected ? 'true' : 'false'}">${icon(selected ? 'X' : 'Plus')}${selected ? 'Remove' : 'Add to compare'}</button>
          <a class="ibg-link ibg-link--secondary" href="${ship.shipGuide}">${icon('BookOpen')}Ship Guide</a>
        </div>
        <a class="ibg-link" href="${ship.url}">Buy Now ${icon('ArrowUpRight')}</a>
      </div>
    </article>
  `;
  };

  const rankingSpecs = [
    { title: 'Cargo capacity', field: 'cargo', icon: 'Box', suffix: 'SCU', copy: 'The biggest haulers, ranked by total cargo capacity.' },
    { title: 'SCM speed', field: 'scm', icon: 'Gauge', suffix: 'm/s', copy: 'The fastest ships and vehicles at listed SCM speed.' },
    { title: 'Length', field: 'length', icon: 'Ruler', suffix: 'm', copy: 'The largest hulls in the fleet, measured from bow to stern.' },
    { title: 'Shield strength', field: 'shield', icon: 'Shield', suffix: 'HP', copy: 'The strongest shield pools, ranked by total shield health.' }
  ];

  const rankingValue = (ship, field) => number(ship[field]);
  const rankingCard = (ship, specItem, index, highest) => {
    const id = compareId(ship);
    const value = rankingValue(ship, specItem.field);
    const medalClass = index === 0 ? ' is-gold' : index === 1 ? ' is-silver' : index === 2 ? ' is-bronze' : '';
    return `
      <article class="ibg-rank-card${medalClass}">
        <div class="ibg-rank-number" aria-label="Rank ${index + 1}">${String(index + 1).padStart(2, '0')}</div>
        <img src="${escapeHtml(ship.image)}" alt="${escapeHtml(ship.name)}" loading="lazy" decoding="async" referrerpolicy="no-referrer" role="button" tabindex="0" data-detail-id="${id}" aria-label="Show more details for ${escapeHtml(ship.name)}"${shipImageFallbackAttributes(ship, ship.image, 360)}>
        <div class="ibg-rank-name">
          <strong>${escapeHtml(ship.name)}</strong>
          <div class="ibg-maker">${escapeHtml(ship.manufacturer)}</div>
          <span class="ibg-status ibg-status--inline ibg-status--${displayStatus(ship.status)[1]}">${displayStatus(ship.status)[0]}</span>
        </div>
        <span class="ibg-status ibg-status--${displayStatus(ship.status)[1]}">${displayStatus(ship.status)[0]}</span>
        <div class="ibg-rank-value">${value.toLocaleString()}<small>${specItem.suffix}</small><div class="ibg-rank-meter" aria-hidden="true" style="--rank-fill:${Math.max(1, value / highest * 100)}%"><span></span></div></div>
        <div class="ibg-rank-actions">
          <button class="ibg-detail-toggle" type="button" data-detail-id="${id}">Details</button>
          <a class="ibg-link" href="${ship.url}">Buy Now</a>
        </div>
      </article>
    `;
  };

  const renderRankings = () => {
    const specItem = rankingSpecs.find((item) => item.field === rankingMetric) || rankingSpecs[0];
    const eligible = ships.filter(ship => shipMatchesCatalogFilters(ship));
      const ranked = ships
        .filter(ship => eligible.includes(ship) && core.known(ship, specItem.field) !== null)
        .sort((a, b) => rankingValue(b, specItem.field) - rankingValue(a, specItem.field) || a.name.localeCompare(b.name))
        .slice(0, 10);
    currentMatchCount = eligible.length;
    els.results.innerHTML = `
      <div class="ibg-ranking-metrics" role="group" aria-label="Rank ships by">
        ${rankingSpecs.map((item) => `<button class="ibg-metric${item.field === specItem.field ? ' is-active' : ''}" type="button" data-ranking-metric="${item.field}" aria-pressed="${item.field === specItem.field}">${icon(item.icon)}${item.title}</button>`).join('')}
      </div>
      <div class="ibg-ranking-scope"><label class="ibg-check"><input type="checkbox" data-ranking-ready ${rankingReadyOnly ? 'checked' : ''}>Flight ready only</label><span>${rankingReadyOnly ? 'Unreleased ships excluded' : 'Unreleased ships included'} / ${eligible.length} matching ships and vehicles</span></div>
      <section class="ibg-ranking-panel" aria-label="${specItem.title} ranking">
        <div class="ibg-ranking-heading"><div><h3>${specItem.title}</h3><p>${specItem.copy}</p></div><span class="ibg-rank-limit">TOP ${ranked.length}</span></div>
        ${ranked.map((ship, index) => rankingCard(ship, specItem, ranked.findIndex(item => rankingValue(item, specItem.field) === rankingValue(ship, specItem.field)), Math.max(1, rankingValue(ranked[0], specItem.field)))).join('')}
      </section>`;
    els.results.hidden = false;
    els.results.classList.add('is-ranking');
    els.empty.textContent = 'No confirmed stats match these filters. Try another ranking or adjust your filters.';
    els.empty.classList.toggle('is-visible', !ranked.length);
    els.moreWrap.hidden = true;
    els.compareBar.hidden = true;
    els.count.textContent = `${ranked.length} ranked / ${eligible.length} matches`;
    updateActiveFilters();
    updateFilterAvailability();
    saveState();
    renderDetailModal();
  };

  const updateActiveFilters = (matchCount = 0) => {
    const chips = [];
    for (const field of ['search', 'role', 'subrole', 'size', 'cargo', 'crew', 'status']) {
      if (els[field].value.trim()) chips.push({ field, value: els[field].value });
    }
    selectedMakers.forEach((maker) => chips.push({ field: 'maker', value: maker }));
    els.activeFilters.innerHTML = chips.map(({ field, value }) => `<button class="ibg-filter-chip" type="button" data-remove-filter="${field}" data-filter-value="${escapeHtml(value)}" aria-label="Remove filter: ${escapeHtml(value)}">${escapeHtml(value)}${icon('X')}</button>`).join('');
  };

  const optionHasMatches = (field, value) => ships.some((ship) => shipMatchesCatalogFilters(ship, { [field]: value }));
  const updateSelectAvailability = (select, field) => {
    if (!select) return;
    Array.from(select.options).forEach((option) => {
      if (!option.value) {
        option.disabled = false;
        option.title = '';
        if (option.dataset.label) option.textContent = option.dataset.label;
        return;
      }
      const available = option.value === select.value || optionHasMatches(field, option.value);
      if (!option.dataset.label) option.dataset.label = option.textContent;
      option.disabled = !available;
      option.title = available ? '' : 'No matches with the current filters';
      option.textContent = available ? option.dataset.label : `${option.dataset.label} - no matches`;
    });
  };

  const updateFilterAvailability = () => {
    if (!ships.length || mode !== 'all') return;
    updateSelectAvailability(els.role, 'role');
    updateSelectAvailability(els.subrole, 'subrole');
    updateSelectAvailability(els.size, 'size');
    updateSelectAvailability(els.cargo, 'cargo');
    updateSelectAvailability(els.crew, 'crew');
    updateSelectAvailability(els.status, 'status');

    const makers = Array.from(new Set(ships.map((ship) => ship.manufacturer))).sort();
    const disabledMakers = new Set(makers.filter((maker) => (
      !selectedMakers.has(maker) && !ships.some((ship) => shipMatchesCatalogFilters(ship, { makers: new Set([maker]) }))
    )));
    renderManufacturerButtons(makers, disabledMakers);
  };

  const clearAllFilters = () => {
    els.search.value = '';
    els.role.value = '';
    updateSubroleFilter();
    els.subrole.value = '';
    els.size.value = '';
    els.cargo.value = '';
    els.crew.value = '';
    els.status.value = '';
    els.sort.value = '';
    selectedMakers.clear();
    resetVisibleLimit();
    clearQuizTheme();
    renderManufacturerButtons(Array.from(new Set(ships.map((ship) => ship.manufacturer))).sort());
    render();
  };

  const render = () => {
    root.querySelectorAll('[data-start-quiz]').forEach(button => button.disabled = !ships.length);
    if (initialized) saveState();
    root.querySelector('[data-saved-actions]').hidden = mode !== 'all' || catalogView !== 'saved';
    root.querySelector('[data-clear-saved]').textContent = sharedShortlist ? 'Close shared shortlist' : 'Clear saved ships';
    const quizCount = root.querySelector('[data-quiz-match-count]');
    quizCount.hidden = mode !== 'quiz' || !quizComplete;
    root.querySelectorAll('[data-mode]').forEach((node) => {
      node.hidden = node.dataset.mode !== mode;
    });
    root.classList.toggle('is-rankings-view', mode === 'all' && catalogView === 'rankings');
    els.viewTabs.forEach((button) => {
      const active = button.dataset.viewTab === catalogView;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    els.results.hidden = mode === 'choice';
    els.results.classList.remove('is-ranking');
    if (mode === 'choice') {
      activeDetailId = '';
      els.results.innerHTML = '';
      els.empty.classList.remove('is-visible');
      els.moreWrap.hidden = true;
      els.compareBar.hidden = true;
      els.count.textContent = ships.length ? 'Choose a path' : 'Loading ships...';
      renderDetailModal();
      return;
    }

    if (mode === 'all' && catalogView === 'rankings') {
      renderRankings();
      return;
    }

    if (mode === 'quiz') {
      els.moreWrap.hidden = true;
      if (!quizComplete) {
        els.results.innerHTML = '';
        els.empty.classList.remove('is-visible');
        els.compareBar.hidden = true;
        els.count.textContent = 'Quiz in progress';
        renderDetailModal();
        return;
      }
      const picks = core.recommend(ships.map(ship => ({ ...ship, primaryCategory: primaryCategory(ship) })), quizAnswers, quizCrewBands);
      recommendationById.clear();
      picks.forEach(entry => recommendationById.set(compareId(entry.ship), entry));
      const shown = picks.map(entry => entry.ship);

      currentMatchCount = shown.length;
      els.results.innerHTML = shown.map(card).join('');
      els.empty.textContent = 'No ships meet these requirements. Go back to change crew, activity or flight-ready requirements.';
      els.empty.classList.toggle('is-visible', shown.length === 0);
      updateCompareBar();
      els.count.textContent = shown.length ? `${shown.length} best matches` : 'No matches yet';
      quizCount.textContent = shown.length ? `Your shortlist / ${shown.length} best matches` : 'No matches yet';
      renderDetailModal();
      return;
    }

    recommendationById.clear();
    const shown = ships.filter(ship => shipMatchesCatalogFilters(ship) && (catalogView !== 'saved' || (sharedShortlist || favoriteIds).has(compareId(ship))));

    const sorted = sortShips(shown);

    const visible = sorted.slice(0, visibleLimit);
    els.empty.textContent = catalogView === 'saved' ? ((sharedShortlist || favoriteIds).size ? 'No saved ships match these filters.' : 'No ships saved yet.') : 'No matching ships or vehicles found. Try clearing one filter.';
    currentMatchCount = sorted.length;
    els.results.innerHTML = visible.map(card).join('');
    els.empty.classList.toggle('is-visible', sorted.length === 0);
    els.moreWrap.hidden = visible.length >= sorted.length;
    updateCompareBar();
    updateActiveFilters(sorted.length);
    updateFilterAvailability();
    els.count.textContent = sorted.length === ships.length ? `${ships.length} ships & vehicles` : `${sorted.length} of ${ships.length} ships & vehicles`;
    renderDetailModal();
  };

  const setMode = (nextMode) => {
    mode = nextMode;
    root.querySelectorAll('[data-mode]').forEach((node) => {
      node.hidden = node.dataset.mode !== nextMode;
    });
    els.results.hidden = nextMode === 'choice';
    if (nextMode !== 'all') catalogView = 'catalog';
    if (nextMode === 'quiz') renderQuiz();
    if (nextMode !== 'quiz') applyQuizTheme();
    render();
  };

  const subroleOptionsForQuiz = (answers = quizAnswers) => {
    const playstyle = answers.playstyle;
    const allowed = subrolesByPlaystyle[playstyle] || [];
    const labels = {
      'Daily driver': 'A flexible everyday ship for missions, travel, and light utility.',
      'Solo-flex multirole': 'Mostly solo-friendly, with room to bring a friend.',
      'Crew-flex multirole': 'A flexible ship that becomes better with a crew.',
      'Cargo-flex multirole': 'A general-purpose ship where cargo still matters.',
      'Utility multirole': 'Small utility space without turning into a full hauler.',
      'Combat-flex multirole': 'A flexible ship that can still fight when needed.',
      'Exploration-flex multirole': 'A flexible ship with scouting, range, or expedition flavor.',
      'Vehicle support multirole': 'A flexible ship that can support vehicles or surface play.',
      'Modular platform': 'A ship built around modules or future loadout flexibility.'
    };
    return Array.from(new Set(quizCandidateShips('subrole', answers)
      .flatMap((ship) => ship.subroles || [])
      .filter((subrole) => !allowed.length || allowed.includes(subrole))))
      .sort()
      .map((subrole) => [subrole, labels[subrole] || `Show me ${subrole.toLowerCase()} options.`]);
  };

  const dynamicOptionsForQuiz = (question, answers = quizAnswers) => {
    const pool = quizCandidateShips(question.id, answers);
    const meaningful = (value) => pool.some(ship => core.satisfies(ship, { ...answers, [question.id]: value }));
    if (question.id === 'subrole') return subroleOptionsForQuiz(answers);
    if (question.id === 'travel') return question.options.filter(([value]) => meaningful(value));
    if (question.id === 'equipment') return core.featureDefinitions.filter(([key]) => {
      const matches = pool.filter(ship => core.capabilities(ship)[key].value === true);
      return matches.length && matches.length < pool.length;
    }).map(([key, label]) => [key, label]);
    if (question.id === 'carrier') return [...new Set(pool.flatMap(ship => core.profile(ship).carriers || []))].sort().map(name => [name, 'Only documented fits for this carrier; unreviewed combinations are excluded.']);
    if (question.id === 'crew') {
      const order = ['Single seater', 'Solo or small group', 'Two-person team', 'Group crew', 'Org crew'];
      return order.filter(meaningful).map(value => [value, {
        'Single seater': 'One operator, with no additional crew stations to fill.',
        'Solo or small group': 'A reviewed solo-flex option with room to bring friends.',
        'Two-person team': 'We regularly play together and can staff a second station.',
        'Group crew': 'A regular crew for the ship\'s working stations.',
        'Org crew': 'A large team or an organization-scale operation.'
      }[value]]);
    }
    if (question.id === 'cargo') {
      if (!['Cargo', 'Multirole'].includes(answers.playstyle) && !['Cargo starter', 'Cargo vehicle'].includes(answers.subrole)) return [];
      return ['1+ SCU', '25+ SCU', '100+ SCU', '500+ SCU', '1000+ SCU'].filter(meaningful).map(value => [value, 'At least ' + value.replace('+', '') + ' of general freight space.']);
    }
    if (question.id === 'size') return ['Snub', 'Small', 'Medium', 'Large', 'Capital'].filter(meaningful).map(value => [value, 'Only ships in this size category.']);
    if (question.id === 'priority') {
      const values = ['Combat', 'Racing'].includes(answers.playstyle) ? ['Higher SCM speed', 'More shielding', 'Smaller footprint']
        : answers.subrole === 'Mining' || answers.subrole === 'Ground mining' ? ['More ore storage', 'Smaller footprint']
        : answers.playstyle === 'Cargo' || answers.subrole === 'Salvage' ? ['More capacity', 'Smaller footprint']
        : ['Multirole', 'Starter', 'Exploration'].includes(answers.playstyle) ? ['Versatility', 'Smaller footprint'] : ['Smaller footprint'];
      const ready = pool.map(ship => ({ ...ship, primaryCategory: primaryCategory(ship) }));
      const baseline = core.recommend(ready, { ...answers, priority: '' }).map(pick => core.family(pick.ship)).join('|');
      return values.filter(value => core.recommend(ready, { ...answers, priority: value }).map(pick => core.family(pick.ship)).join('|') !== baseline).map(value => [value, {
        'Higher SCM speed': 'Prioritize listed SCM speed among compatible options, not an overall combat ranking.',
        'More shielding': 'Prioritize listed stock shield capacity.',
        'More capacity': 'Prioritize general freight space.',
        'More ore storage': 'Prioritize dedicated ore capacity.',
        'Smaller footprint': 'Prioritize shorter hulls among compatible options.',
        'Versatility': 'Prioritize supported secondary activities.'
      }[value]]);
    }
    return question.options || [];
  };

  const clearAnswersAfter = (questionId) => {
    const order = quizQuestionOrder;
    const index = order.indexOf(questionId);
    if (index === -1) return;
    order.slice(index + 1).forEach((key) => delete quizAnswers[key]);
  };

  const renderQuiz = () => {
    const steps = root.querySelector('[data-quiz-steps]');
    const progress = root.querySelector('[data-quiz-progress]');
    const questions = activeQuizQuestions();
    if (quizComplete) {
      progress.textContent = 'Your shortlist';
      steps.innerHTML = `<div class="ibg-quiz-summary">${questions.filter(q => quizAnswers[q.id]).map(q => `<button class="ibg-filter-chip" type="button" data-edit-question="${q.id}">${escapeHtml(q.id === 'equipment' ? core.capabilities({})[quizAnswers[q.id]]?.label || quizAnswers[q.id] : quizAnswers[q.id])} ${icon('ArrowLeft')}</button>`).join('')}</div>`;
      root.querySelector('[data-quiz-back]').hidden = false;
      root.querySelector('[data-quiz-reset]').hidden = false;
      root.querySelector('[data-quiz-back]').disabled = false;
      return;
    }
    if (quizStep >= questions.length) quizStep = questions.length - 1;
    const question = questions[quizStep];
    let options = question.dynamic ? dynamicOptionsForQuiz(question) : question.options;
    if (question.id === 'playstyle') options = options.filter(([value]) => quizCandidateShips('playstyle').some(ship => ship.tags.includes(value)));
    if (question.id === 'availability') options = options.filter(([value]) => value !== 'Flight ready only' || ships.some(ship => ship.type === quizAnswers.vehicleType && core.isReady(ship)));
    const safeOptions = question.optional && question.id !== 'travel' ? [...options, ['Any', question.id === 'subrole' || question.id === 'crew' ? 'Not sure yet.' : 'No additional requirement.']] : options;

    const phase = ['vehicleType', 'availability', 'travel'].includes(question.id) ? 0 : ['playstyle', 'subrole', 'crew'].includes(question.id) ? 1 : 2;
    progress.innerHTML = `<span>Step ${String(quizStep + 1).padStart(2, '0')} / ${question.id === 'priority' ? 'Your preferences' : 'Your requirements'}</span><span class="ibg-progress-track" aria-hidden="true">${[0, 1, 2].map((index) => `<i class="${index <= phase ? 'is-complete' : ''}"></i>`).join('')}</span>`;
    steps.innerHTML = `
      <div class="ibg-quiz-step">
        <h3>${question.title}</h3>
        <div class="ibg-quiz-grid" style="--quiz-columns:${Math.min(3, safeOptions.length)}">
          ${safeOptions.map(([value, copy]) => {
            const theme = question.id === 'playstyle' ? roleTheme(value) : null;
            const vesselNames = value === 'Ship' ? ['avenger titan'] : ['ursa rover', 'ursa'];
            const vessel = question.id === 'vehicleType' ? (
              ships.find((ship) => ship.type === value && ship.hasRealImage && vesselNames.includes(normalize(ship.name)))
              || ships.find((ship) => ship.type === value && ship.hasRealImage)
            ) : null;
            const style = theme ? ` style="--role-accent:${theme[0]};--role-accent-soft:${theme[1]};--role-contrast:${theme[2] || '#fff'};"` : '';
            const roleAttr = theme ? ' data-role-accent' : '';
            return `
            <button class="ibg-quiz-option${vessel ? ' ibg-quiz-option--vessel' : ''}${quizAnswers[question.id] === value ? ' is-selected' : ''}" type="button" data-quiz-answer="${value}"${roleAttr}${style}>
              ${vessel ? `<img class="ibg-quiz-image" src="${escapeHtml(vessel.image)}" alt="" aria-hidden="true" loading="eager" decoding="async" referrerpolicy="no-referrer" ${shipImageFallbackAttributes(vessel, vessel.image, 720)}>` : ''}
              <strong>${question.id === 'equipment' && value !== 'Any' ? escapeHtml(copy) : escapeHtml(value)}</strong>
              <span>${question.id === 'equipment' && value !== 'Any' ? 'Only options with this listed capability.' : escapeHtml(copy)}</span>
              ${icon('ArrowRight')}
            </button>
          `;
          }).join('')}
        </div>
      </div>
    `;

    steps.querySelectorAll('[data-quiz-answer]').forEach((button) => {
      button.addEventListener('click', () => {
        track('quiz_answer', { question: question.id, answer: button.dataset.quizAnswer });
        quizComplete = false;
        quizAnswers[question.id] = button.dataset.quizAnswer === 'Any' ? '' : button.dataset.quizAnswer;
        if (question.id === 'vehicleType') {
          Object.keys(quizAnswers).forEach((key) => {
            if (key !== 'vehicleType') delete quizAnswers[key];
          });
        }
        clearAnswersAfter(question.id);
        if (question.id === 'playstyle') {
          applyQuizTheme();
        }
        const nextQuestions = activeQuizQuestions();
        if (quizStep < nextQuestions.length - 1) {
          quizStep += 1;
          renderQuiz();
        } else {
          quizComplete = true;
          track('quiz_complete', { activity: quizAnswers.playstyle, steps: nextQuestions.length });
          renderQuiz();
        }
        render();
      });
    });

    const backButton = root.querySelector('[data-quiz-back]');
    const resetButton = root.querySelector('[data-quiz-reset]');
    const isFirstQuizScreen = quizStep === 0;
    backButton.hidden = isFirstQuizScreen;
    resetButton.hidden = isFirstQuizScreen;
    backButton.disabled = isFirstQuizScreen;
  };

  const updateSubroleFilter = () => {
    const currentSubrole = els.subrole.value;
    const allowed = subrolesByPlaystyle[els.role.value] || [];
    const subroles = Array.from(new Set(ships
      .filter((ship) => !els.role.value || ship.tags.includes(els.role.value))
      .flatMap((ship) => ship.subroles || [])
      .filter((subrole) => !allowed.length || allowed.includes(subrole)))).sort();
    optionList(els.subrole, 'All subroles', subroles);
    if (subroles.includes(currentSubrole)) els.subrole.value = currentSubrole;
  };

  const hydrateFilters = () => {
    const roles = Array.from(new Set(ships.flatMap((ship) => ship.tags).filter((tag) => tag !== 'Ground'))).sort();
    const makers = Array.from(new Set(ships.map((ship) => ship.manufacturer))).sort();
    const sizes = Array.from(new Set(ships.map((ship) => ship.size))).sort((a, b) => ['Ground vehicle','Snub','Small','Medium','Large','Capital','Unknown'].indexOf(a) - ['Ground vehicle','Snub','Small','Medium','Large','Capital','Unknown'].indexOf(b));
    optionList(els.role, 'All playstyles', roles);
    updateSubroleFilter();
    renderManufacturerButtons(makers);
    optionList(els.size, 'All sizes', sizes);
    optionList(els.cargo, 'All cargo', ['No cargo','1-24 SCU','25-99 SCU','100-499 SCU','500-999 SCU','1000+ SCU']);
    optionList(els.crew, 'All crew sizes', ['Single seater','Solo or small group','Two-person team','Group crew','Org crew']);
    optionList(els.status, 'All statuses', ['Flight ready','In concept','In production / TBD']);
    optionList(els.sort, 'Sort: Name A-Z', ['Manufacturer','Cargo high to low','Crew solo first','Speed high to low','Size small to large','Size large to small']);
  };

  Object.values(els).forEach((el) => {
    if (el && typeof el.matches === 'function' && (el.matches('input') || el.matches('select'))) {
      el.addEventListener('input', () => {
        resetVisibleLimit();
        render();
      });
    }
  });
  els.role.addEventListener('input', () => {
    resetVisibleLimit();
    quizAnswers.playstyle = els.role.value;
    applyQuizTheme();
    delete quizAnswers.playstyle;
    updateSubroleFilter();
    render();
  });

  els.clearFilters.addEventListener('click', clearAllFilters);

  root.querySelectorAll('[data-filter-drawer-toggle]').forEach((button) => {
    button.addEventListener('click', () => setFilterDrawer(!root.classList.contains('is-filter-open')));
  });

  root.querySelectorAll('[data-filter-drawer-close]').forEach((button) => {
    button.addEventListener('click', () => setFilterDrawer(false));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setFilterDrawer(false);
  });

  root.addEventListener('error', (event) => {
    const image = event.target;
    if (!image || image.tagName !== 'IMG' || !image.hasAttribute('data-image-fallbacks')) return;
    let queue = [];
    try {
      queue = JSON.parse(image.dataset.imageFallbacks || '[]');
    } catch (error) {}
    const current = image.currentSrc || image.src;
    let next = '';
    while (queue.length && !next) {
      const candidate = String(queue.shift() || '');
      if (!candidate) continue;
      try {
        if (new URL(candidate, window.location.href).href === current) continue;
      } catch (error) {}
      next = candidate;
    }
    if (!next) {
      image.removeAttribute('data-image-fallbacks');
      return;
    }
    image.dataset.imageFallbacks = JSON.stringify(queue);
    image.src = next;
  }, true);

  root.querySelectorAll('[data-start-quiz]').forEach((button) => {
    button.addEventListener('click', () => {
      quizStartedAt = Date.now();
      track('quiz_start');
      setFilterDrawer(false);
      quizStep = 0;
      clearQuizTheme();
      setMode('quiz');
    });
  });

  root.querySelectorAll('[data-show-all]').forEach((button) => {
    button.addEventListener('click', () => {
      if (mode === 'quiz' && !quizComplete && quizStartedAt) track('quiz_abandon', { step: activeQuizQuestions()[quizStep]?.id || 'vehicleType', reason: 'browse_catalog' });
      quizStartedAt = 0;
      setFilterDrawer(false);
      clearQuizTheme();
      resetVisibleLimit();
      catalogView = 'catalog';
      setMode('all');
    });
  });

  els.viewTabs.forEach((button) => {
    button.addEventListener('click', () => {
      catalogView = ['rankings', 'saved'].includes(button.dataset.viewTab) ? button.dataset.viewTab : 'catalog';
      if (catalogView !== 'saved') sharedShortlist = null;
      track('view', { view: catalogView });
      setFilterDrawer(false);
      render();
    });
  });

  root.addEventListener('click', (event) => {
    const edit = event.target.closest('[data-edit-question]');
    if (edit) {
      quizComplete = false;
      quizStep = Math.max(0, activeQuizQuestions().findIndex(question => question.id === edit.dataset.editQuestion));
      clearAnswersAfter(edit.dataset.editQuestion);
      if (edit.dataset.editQuestion === 'playstyle') { delete quizAnswers.playstyle; applyQuizTheme(); }
      renderQuiz(); render(); return;
    }
    const save = event.target.closest('[data-save-id]');
    if (save) {
      const id = save.dataset.saveId;
      if (!ships.some(ship => compareId(ship) === id)) return;
      if (favoriteIds.has(id)) favoriteIds.delete(id);
      else if (favoriteIds.size < 50) favoriteIds.add(id);
      else { announce('Your shortlist has 50 ships. Unsave one before adding another.'); return; }
      const persisted = persistence.write(FAVORITES_KEY, [...favoriteIds]);
      root.querySelector('[data-saved-count]').textContent = favoriteIds.size;
      root.querySelectorAll('[data-save-id]').forEach(button => {
        const selected = favoriteIds.has(button.dataset.saveId);
        const ship = ships.find(item => compareId(item) === button.dataset.saveId);
        button.classList.toggle('is-saved', selected); button.setAttribute('aria-pressed', String(selected));
        button.setAttribute('aria-label', `${selected ? 'Unsave' : 'Save'} ${ship.name}`); button.title = selected ? 'Unsave ship' : 'Save ship';
      });
      if (!persisted) announce('Saved for this visit. Your browser is not allowing local storage.');
      if (catalogView === 'saved' && !activeDetailId) render();
      track('save_ship', { ship: id, saved: favoriteIds.has(id) }); return;
    }
    if (event.target.closest('[data-share-view]')) { shareState(activeDetailId ? 'ship' : 'view'); return; }
    if (event.target.closest('[data-share-compare]')) { shareState('comparison'); return; }
    if (event.target.closest('[data-share-saved]')) { shareState('saved'); return; }
    if (event.target.closest('[data-clear-saved]')) { if (sharedShortlist) sharedShortlist = null; else { favoriteIds.clear(); persistence.write(FAVORITES_KEY, []); } render(); return; }
    const buy = event.target.closest('a.ibg-link:not(.ibg-link--secondary)');
    if (buy) {
      const ship = ships.find(item => new URL(item.url, location.href).href === buy.href);
      track('buy_now', { ship: ship ? compareId(ship) : '', view: activeDetailId ? 'details' : mode === 'quiz' ? 'quiz' : catalogView });
    }
    const metricButton = event.target.closest('[data-ranking-metric]');
    if (metricButton) {
      rankingMetric = metricButton.dataset.rankingMetric;
      renderRankings();
      root.querySelector(`[data-ranking-metric="${rankingMetric}"]`)?.focus({ preventScroll: true });
      return;
    }
    const filterChip = event.target.closest('[data-remove-filter]');
    if (filterChip) {
      const field = filterChip.dataset.removeFilter;
      if (field === 'maker') selectedMakers.delete(filterChip.dataset.filterValue);
      else if (els[field]) els[field].value = '';
      if (field === 'role') updateSubroleFilter();
      resetVisibleLimit();
      render();
      els.search.focus({ preventScroll: true });
      return;
    }
    const detailButton = event.target.closest('[data-detail-id]');
    if (detailButton) {
      const id = detailButton.dataset.detailId;
      openDetailModal(id);
      return;
    }

    if (event.target.closest('[data-detail-close]')) {
      closeDetailModal();
      return;
    }

    const prevButton = event.target.closest('[data-gallery-prev]');
    const nextButton = event.target.closest('[data-gallery-next]');
    if (prevButton || nextButton) {
      const id = (prevButton || nextButton).dataset.galleryPrev || (prevButton || nextButton).dataset.galleryNext;
      const ship = ships.find((item) => compareId(item) === id);
      const images = galleryImagesFor(ship || {}, id);
      if (!images.length) return;
      const current = galleryIndexes.get(id) || 0;
      const next = prevButton ? current - 1 : current + 1;
      galleryIndexes.set(id, (next + images.length) % images.length);
      renderDetailModal();
      return;
    }

    const thumbButton = event.target.closest('[data-gallery-thumb]');
    if (thumbButton) {
      galleryIndexes.set(thumbButton.dataset.galleryThumb, Number(thumbButton.dataset.galleryIndex) || 0);
      renderDetailModal();
      return;
    }

    const button = event.target.closest('[data-compare-id]');
    if (!button) return;
    const id = button.dataset.compareId;
    const existingIndex = compareIds.indexOf(id);
    if (existingIndex >= 0) {
      compareIds.splice(existingIndex, 1);
    } else {
      if (compareIds.length >= 2) { announce('Two ships are already selected. Remove one from the comparison first.'); return; }
      compareIds.push(id);
    }
    track('compare', { ships: compareIds.join('|'), action: existingIndex >= 0 ? 'remove' : 'add' });
    render();
  });

  root.addEventListener('keydown', (event) => {
    const detailTarget = event.target.closest('[data-detail-id][tabindex="0"]');
    if (!detailTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openDetailModal(detailTarget.dataset.detailId);
  });

  els.clearCompare.addEventListener('click', () => {
    compareIds.splice(0, compareIds.length);
    render();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDetailId) closeDetailModal();
    if (event.key === 'Tab' && activeDetailId) {
      const focusable = Array.from(els.detailModal.querySelectorAll('button:not([disabled]),a[href],select,[tabindex="0"]')).filter((el) => el.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !els.detailModal.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !els.detailModal.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
  });

  els.makerButtons.addEventListener('click', (event) => {
    const button = event.target.closest('[data-maker]');
    if (!button) return;
    const maker = button.dataset.maker;
    if (!maker) {
      selectedMakers.clear();
    } else if (selectedMakers.has(maker)) {
      selectedMakers.delete(maker);
    } else {
      selectedMakers.add(maker);
    }
    resetVisibleLimit();
    renderManufacturerButtons(Array.from(new Set(ships.map((ship) => ship.manufacturer))).sort());
    render();
  });

  els.moreButton.addEventListener('click', () => {
    visibleLimit += VISIBLE_INCREMENT;
    render();
  });

  const loadMoreVisible = () => {
    if (mode !== 'all' || catalogView !== 'catalog' || visibleLimit >= currentMatchCount) return;
    visibleLimit += VISIBLE_INCREMENT;
    render();
  };

  if ('IntersectionObserver' in window) {
    const moreObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMoreVisible();
    }, { rootMargin: '900px 0px' });
    moreObserver.observe(els.moreWrap);
  } else {
    window.addEventListener('scroll', () => {
      const rect = els.moreWrap.getBoundingClientRect();
      if (rect.top < window.innerHeight + 900) loadMoreVisible();
    }, { passive: true });
  }

  root.querySelector('[data-quiz-back]').addEventListener('click', () => {
    if (quizStep > 0) {
      if (quizComplete) quizComplete = false;
      else quizStep -= 1;
      const question = activeQuizQuestions()[quizStep];
      clearAnswersAfter(question.id);
      if (question.id === 'playstyle') delete quizAnswers.playstyle;
      applyQuizTheme();
      renderQuiz();
      render();
    }
  });

  root.querySelector('[data-quiz-reset]').addEventListener('click', () => {
    track('quiz_restart');
    quizStep = 0;
    clearQuizTheme();
    renderQuiz();
    render();
  });

  root.addEventListener('change', event => {
    if (event.target.matches('[data-compare-differences]')) { onlyDifferences = event.target.checked; updateCompareBar(); }
    if (event.target.matches('[data-ranking-ready]')) {
      rankingReadyOnly = event.target.checked;
      if (rankingReadyOnly && els.status.value && els.status.value !== 'Flight ready') { els.status.value = ''; announce('The unreleased status filter was cleared for flight-ready rankings.'); }
      render();
    }
  });
  let searchEventTimer;
  els.search.addEventListener('input', () => {
    clearTimeout(searchEventTimer);
    searchEventTimer = setTimeout(() => track(currentMatchCount ? 'search' : 'no_results', { query_length: els.search.value.length, results: currentMatchCount, view: catalogView }), 700);
  });
  window.addEventListener('pagehide', () => {
    saveState();
    if (mode === 'quiz' && !quizComplete && quizStartedAt) track('quiz_abandon', { step: activeQuizQuestions()[quizStep]?.id || 'vehicleType' });
  });
  window.addEventListener('popstate', () => {
    activeDetailId = history.state?.ibgDetail || '';
    renderDetailModal();
    if (!activeDetailId && detailTrigger?.isConnected) detailTrigger.focus({ preventScroll: true });
  });
  render();


  const setCache = (vehicles) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ refreshed_at: Date.now(), generated_at: snapshotGeneratedAt, vehicles }));
    } catch (error) {}
  };

  const getCache = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!cached || !Array.isArray(cached.vehicles)) return null;
      return cached;
    } catch (error) {
      return null;
    }
  };

  const renderVehicles = (vehicles) => {
    ships = dedupeShips(vehicles.filter((vehicle) => isPurchasable(vehicle) && !isCosmeticRecord(vehicle)).map(mapShip).concat(supplementalShips)).sort((a, b) => a.name.localeCompare(b.name));
    const oldState = initialized ? captureState() : null;
    hydrateFilters();
    let restoredScroll = 0;
    if (!initialized) {
      const saved = persistence.read(FAVORITES_KEY);
      if (Array.isArray(saved)) saved.slice(0, 100).filter(id => ships.some(ship => compareId(ship) === id)).forEach(id => favoriteIds.add(id));
      const shared = readSharedState();
      const remembered = persistence.read(STATE_KEY);
      restoreState(shared || remembered);
      if (!shared && remembered) restoredScroll = Math.max(0, Number(remembered.scroll) || 0);
      initialized = true;
    } else restoreState(oldState);
    const date = new Date(snapshotGeneratedAt);
    root.querySelector('[data-snapshot-date]').textContent = Number.isNaN(date.valueOf()) ? '' : `Snapshot: ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.`;
    render();
    if (restoredScroll) requestAnimationFrame(() => window.scrollTo(0, restoredScroll));
    if (activeDetailId) { ensureGallery(ships.find(ship => compareId(ship) === activeDetailId), activeDetailId); els.detailModal.querySelector('.ibg-modal-close')?.focus({ preventScroll: true }); }
  };

  const loadSnapshot = async () => {
    const response = await fetch(SNAPSHOT_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Snapshot request failed');
    const payload = await response.json();
    const vehicles = Array.isArray(payload.vehicles) ? payload.vehicles : payload.data;
    if (!Array.isArray(vehicles) || !vehicles.length) throw new Error('Snapshot has no vehicle data');
    if (payload.review?.approved === false) throw new Error('This snapshot has not been approved for publishing');
    snapshotGeneratedAt = payload.generated_at || '';
    return vehicles;
  };

  const loadImageManifest = async () => {
    const response = await fetch(IMAGE_MANIFEST_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Image manifest request failed');
    const payload = await response.json();
    imageManifest = {
      source_to_asset: payload?.source_to_asset && typeof payload.source_to_asset === 'object' ? payload.source_to_asset : {},
      gallery_by_title: payload?.gallery_by_title && typeof payload.gallery_by_title === 'object' ? payload.gallery_by_title : {}
    };
    imageManifestLoaded = true;
  };

  const loadHoloSnapshot = async () => {
    const response = await fetch(HOLO_SNAPSHOT_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Holo snapshot request failed');
    const text = await response.text();
    const payload = JSON.parse(text.replace(/^\uFEFF/, ''));
    const vehicles = Array.isArray(payload.vehicles) ? payload.vehicles : payload.data;
    return Array.isArray(vehicles) ? vehicles : [];
  };

  const refreshInBackground = async () => {
    if (!AUTO_REFRESH) return;
    try {
      const vehicles = await loadSnapshot();
      setCache(vehicles);
      if (!ships.length) renderVehicles(vehicles);
    } catch (error) {}
  };

  const scheduleBackgroundRefresh = () => {
    if (!AUTO_REFRESH) return;
    const run = () => refreshInBackground();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 8000 });
    } else {
      window.setTimeout(run, 3500);
    }
  };

  const init = async () => {
    const cached = getCache();
    const [, holoVehiclesFromSnapshot, snapshotResult] = await Promise.all([
      loadImageManifest().catch(() => null),
      loadHoloSnapshot().catch(() => []),
      loadSnapshot().then(vehicles => ({ vehicles }), error => ({ error }))
    ]);
    indexHoloVehicles(holoVehiclesFromSnapshot);

    try {
      if (snapshotResult.error) throw snapshotResult.error;
      const snapshotVehicles = snapshotResult.vehicles;
      setCache(snapshotVehicles);
      renderVehicles(snapshotVehicles);
      if (!cached || Date.now() - cached.refreshed_at >= REFRESH_MS) scheduleBackgroundRefresh();
    } catch (error) {
      if (cached) {
        snapshotGeneratedAt = cached.generated_at || '';
        renderVehicles(cached.vehicles);
        announce('Showing the last saved snapshot. The latest file could not be loaded.');
        scheduleBackgroundRefresh();
      } else {
        els.count.textContent = 'Snapshot unavailable';
        els.empty.innerHTML = 'The ship catalog could not be loaded. <button type="button" class="ibg-action" data-retry-snapshot>Retry</button>';
        els.empty.classList.add('is-visible');
      }
    }
  };

  root.addEventListener('click', event => { if (event.target.closest('[data-retry-snapshot]')) { els.empty.classList.remove('is-visible'); els.count.textContent = 'Loading ships...'; init(); } });
  init();
  };
  const mount = (scope) => {
    const roots = scope.matches?.('[data-impound-buyer-guide]') ? [scope] : scope.querySelectorAll('[data-impound-buyer-guide]');
    roots.forEach(boot);
  };
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
})();
